import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { leerBolsaNoAlcanza, etiquetaDeContinuar } from '../lib/bolsaNoAlcanza.js';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Toast,
  ToastTitle,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
  useToastController,
} from '@fluentui/react-components';
// Input queda por la barra de búsqueda; no se usa en las filas de conceptos
import {
  ArrowLeft24Regular,
  ArrowSync24Regular,
  CheckmarkCircle20Filled,
  Play24Regular,
  Stop24Regular,
  Warning20Regular,
} from '@fluentui/react-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { Chip } from '../components/Chip.js';
import { useConfirm } from '../components/ConfirmDialog.js';
import {
  contarTrabajoEnRiesgo,
  hayTrabajoEnRiesgo,
  lineasDelAviso,
  totalSinteticos,
} from '../lib/trabajoQueSePierdeAlRearmar.js';
import { getProject, updateFileClassification, type ProjectFile } from '../api/projects.js';
import { setProjectCatalogs } from '../api/projectCatalogs.js';
import { ProjectCatalogsPicker, CATALOG_COLORS } from '../components/ProjectCatalogsPicker.js';
import { ConceptAnalysisView } from '../components/ConceptAnalysisView.js';
import { ConceptPricePanel } from '../components/ConceptPricePanel.js';
import { ApuGenerationProgress } from '../components/ApuGenerationProgress.js';
import { OpusTrackingChip } from '../components/OpusTrackingChip.js';
import { BotonesMensajes } from '../components/mensajes/BotonesMensajes.js';
import { mensajeDelPresupuesto } from '../lib/enviarMensaje.js';
import { brandingProductoActivo } from '../lib/brandingDelProducto.js';
import {
  getQuote,
  getQuoteConcepts,
  setQuoteSelection,
  setQuoteControl,
  regenerateQuote,
  setQuoteCatalogs,
  setQuoteSource,
  previewQuote,
  type Quote,
  type QuoteConcept,
  type QuotePreview,
} from '../api/quotes.js';
import {
  listCatalogs,
  type Catalog,
} from '../api/catalogs.js';
import {
  tituloDeEstructura,
  equivalenteFirme,
  preciosDeCatalogoEnRiesgo,
  precioVieneDelCatalogo,
  unitsAreEquivalent,
  mensajeDeFalloIa,
} from '@supernova/shared';
import { useProjectStream, type ProjectEvent, type QuoteProgressEvent, type QuoteOpusImportEvent } from '../api/projectStream.js';
import { useCanMutate, useIsEcosoftStaff } from '../stores/authStore.js';
import { esElFinDelArmado, estadoDeLaHojaTrasElAviso } from '../lib/estadoDeLaHojaAlAvisar.js';
import { formatoDeMoneda } from '../lib/money.js';
import { catalogUsable, catalogStatusLabel } from '../lib/catalogStatus.js';
import { refreshUsageSoon, useUsageStore } from '../stores/usageStore.js';
import { enCreditos, formatCreditos, promediosDe } from '../lib/creditos.js';
import { ingestProjectEvent, useQuoteJobsStore } from '../stores/quoteJobsStore.js';
import { QuoteBuildingAnimation } from '../components/QuoteBuildingAnimation.js';
import { TiendaDialog } from '../components/TiendaDialog.js';
import { usePricingStore, modelRatePer1k } from '../stores/pricingStore.js';
import {
  stopApuIaGeneration, approveAllMatrices, generateApuIa, getObraContext, setConceptRequested,
  setConceptsRequested,
  listApuIaRuns } from '../api/apuIa.js';
import { ApiClientError } from '../api/client.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { StickyGroupHeaders } from '../components/StickyGroupHeaders.js';
import { GeometriaHpPanel } from '../components/GeometriaHpPanel.js';
import { GEOMETRIA_HP_ENABLED } from '../lib/flags.js';
import { buildDemoCatalogList, findEcostosCatalogId } from '../demo/demoCatalogLabels.js';
import { OCULTAR_COSTO_IA } from '../lib/flags.js';

const ACTIVE_STATUSES = new Set(['queued', 'running', 'paused']);
const REVIEW_STATUSES = new Set(['done', 'completed', 'failed', 'canceled']);

/**
 * Texto CLIENT-SAFE de por qué falló una corrida de análisis con IA, para el camino de
 * recarga/red de seguridad. Es el MISMO que publica el worker y que muestra la hoja de planos:
 * vive en un solo lugar (`@supernova/shared` avisoIa.ts).
 */
const mensajeFalloApuIa = mensajeDeFalloIa;

// Sanitizador de marcas "[R:n]" (QA de incisos jul-2026, ya apagado en el
// worker): las cotizaciones generadas durante el QA aún traen la marca en la
// descripción — se limpia del texto mostrado. Borrar cuando no queden quotes
// de esa época.
function stripRenglonMark(desc: string): string {
  if (!desc.includes('[R:')) return desc;
  return desc.replace(/\s*\[R:\d+\]\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
}

const STAGE_LABEL: Record<NonNullable<QuoteProgressEvent['data']>['stage'], string> = {
  // "En cola" sonaba a que el trabajo ya estaba en marcha. Es una espera: se nombra
  // como espera, y el componente de avance no muestra nada moviéndose (2026-08-12).
  queued: 'Esperando su turno',
  extract: 'Armando la hoja de presupuesto…',
  pricing: 'Buscando equivalentes…',
  excel: 'Finalizando el presupuesto',
  done: '¡Presupuesto listo!',
  failed: 'Falló',
  paused: 'Pausada',
  stopped: 'Detenida',
};

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'informative' | undefined> = {
  done: 'success',
  completed: 'success',
  running: 'informative',
  queued: 'informative',
  paused: 'warning',
  failed: 'danger',
  canceled: 'danger',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  queued: 'En cola',
  running: 'Procesando',
  paused: 'Pausada',
  done: 'Completada',
  completed: 'Completada',
  failed: 'Error',
  canceled: 'Cancelada',
};

const CONFIANZA_ACCENT: Record<string, string> = {
  alta: 'var(--ok)',
  media: 'var(--warn)',
  sin_equivalente: 'var(--bad)',
  sin_candidato: 'var(--bad)',
  sintetica: 'var(--privado)',
  sin_procesar: 'var(--text-tertiary)',
};
const EXPANDED_HEIGHT = 200; // estimado; measureElement corrige el valor real
/** Ver la nota de `shouldAdjustScrollPositionOnItemSizeChange` en QuotePage. */
const NUNCA_CORRER_EL_SCROLL_AL_MEDIR = () => false;

// El desglose INLINE del concepto se reemplazó por el panel lateral
// (ConceptPricePanel). Se deja el código gated en false por si dirección quiere
// volver a esa vista. TODO: borrar el bloque `isExpanded` + este flag cuando
// confirmen. Ver memoria project_panel_analisis_precio_lateral.
const SHOW_INLINE_ANALYSIS = false;

const CONFIANZA_LABEL: Record<string, string> = {
  alta: 'Alta',
  media: 'Media',
  sin_equivalente: 'Sin equivalente',
  sin_candidato: 'Sin equivalente',
  sintetica: 'Precio analizado con IA',
  sin_procesar: 'Sin procesar',
};

// Estado EFECTIVO del concepto para pill/filtro. "Buscar equivalentes" SIEMPRE
// valida la zona gris con IA en la misma corrida, así que al terminar ya no queda
// nada "por confirmar": todo `sin_candidato` es "Sin equivalente".
//
// EXCEPCIÓN: un concepto que quedó `pending` NUNCA se intentó (p.ej. la hoja se
// canceló a mitad). En BD su confianza es `sin_candidato` por defecto, pero
// mostrarlo como "Sin equivalente" (rojo) miente: no es que no se le halló
// equivalente, es que no se buscó. Se muestra aparte, en neutro.
type ConfKind = 'alta' | 'media' | 'sin_equivalente' | 'sintetica' | 'sin_procesar';
function confKind(c: QuoteConcept): ConfKind {
  if (c.status === 'pending') return 'sin_procesar';
  if (c.confianza === 'sintetica') return 'sintetica';
  if (c.confianza === 'alta' || c.confianza === 'media') return c.confianza;
  return 'sin_equivalente';
}

// Alcances del encendido/apagado en lote de la IA (columna "Generar sintético").
// Orden pensado para el cotizador: primero lo que casi siempre querrá generar.
const IA_SCOPES: Array<{ k: ConfKind; label: string }> = [
  { k: 'sin_equivalente', label: 'Sin equivalente' },
  { k: 'media', label: 'Media' },
  { k: 'alta', label: 'Alta' },
  { k: 'sintetica', label: 'Analizado con IA' },
];

// Abreviatura de 2 letras para la insignia del catálogo en el rail colapsado
// (fórmula, NO tabla hardcodeada): iniciales de las primeras 2 palabras, o las
// primeras 2 letras si es una sola palabra. El nombre completo va en el tooltip.
function catalogAbbrev(name: string): string {
  const words = (name ?? '').trim().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (words.length >= 2) return (words[0]!.charAt(0) + words[1]!.charAt(0)).toUpperCase();
  return (words[0] ?? name ?? '?').slice(0, 2).toUpperCase();
}

// Etiqueta CORTA para el pill de la fila (el detalle completo va en el tooltip).
function confLabel(c: QuoteConcept): string {
  const k = confKind(c);
  if (k === 'sintetica') return 'Analizado con IA';
  return CONFIANZA_LABEL[k] ?? c.confianza;
}

/** Fuente legible en demo (chips). */
function demoFuenteLabel(
  c: QuoteConcept,
  catalogs: Array<{ id: string; name: string; kind?: string }>,
): 'eCostos' | 'Catálogo de muestra' | 'Analizado con IA' | null {
  if (c.confianza === 'sintetica' || c.apuIaJobRunId) return 'Analizado con IA';
  if (!c.catalogIdOrigen && !c.claveCatalogo) return null;
  const cat = catalogs.find((x) => x.id === c.catalogIdOrigen);
  if (cat?.kind === 'ecostos' || /ecostos/i.test(cat?.name ?? '') || /ecostos/i.test(c.fuente ?? '')) {
    return 'eCostos';
  }
  return 'Catálogo de muestra';
}

// Tarjetas de ayuda "?" de cada paso (texto del mockup aprobado, 2026-07-10).
const HELP_CARDS: Record<'h1' | 'h2' | 'h3' | 'h4', { title: string; body: string }> = {
  h1: {
    title: '1 · Armar la hoja de presupuesto',
    body: 'OPUS ai lee el documento del concurso y arma la hoja de presupuesto: partidas, conceptos, unidades y cantidades — todavía sin precios ni matrices. Revísala antes del paso 2; si el documento cambió, vuelve a armarla.',
  },
  h2: {
    title: '2 · Buscar equivalentes',
    body: 'Busca el equivalente de cada concepto en los catálogos de referencia que seleccionaste y toma su precio. Primero compara cada concepto contra tus catálogos y, en la misma búsqueda, revisa con inteligencia artificial los casos dudosos para confirmarlos. El precio se toma del catálogo con mayor prioridad que tenga coincidencia; la prioridad la ordenas arrastrando los catálogos en el panel de la izquierda. Nunca se toma un precio sin confirmar.',
  },
  h3: {
    title: '3 · Análisis de precios unitarios con IA',
    body: 'Para los conceptos sin equivalente en tus catálogos de referencia, la inteligencia artificial construye el análisis completo (cuadrilla, materiales, equipo y rendimientos) y su precio, usando el contexto de tu obra (requisito: ajustarlo antes). Tú lo revisas y apruebas.',
  },
  h4: {
    title: '⚙ Contexto de proyecto',
    body: 'Son los parámetros de tu obra —ubicación, altitud, tipo de obra y condiciones del sitio— que la inteligencia artificial usa para ajustar los rendimientos al crear los análisis de precios unitarios. No es un paso del flujo, pero ajustarlo es requisito para crear análisis de precios unitarios con IA (paso 3).',
  },
};

// Colores del badge-pill de confianza (estilo mockup: bg "weak" + texto del color).
const CONF_PILL: Record<string, { bg: string; fg: string }> = {
  alta: { bg: 'var(--ok-weak)', fg: 'var(--ok)' },
  media: { bg: 'var(--warn-weak)', fg: 'var(--warn)' },
  sin_equivalente: { bg: 'var(--bad-weak)', fg: 'var(--bad)' },
  sin_candidato: { bg: 'var(--bad-weak)', fg: 'var(--bad)' },
  sintetica: { bg: 'var(--privado-weak)', fg: 'var(--privado)' },
  sin_procesar: { bg: 'var(--bg-raised)', fg: 'var(--text-secondary)' },
};

// Qué significa cada nivel de la columna Confianza y qué hacer con él. En llano,
// sin mecánica interna (§11: nada de "matcher", "zona gris", "umbral", etc. a la
// vista del cliente). Alimenta el ⓘ del encabezado y el tooltip de cada renglón.
const CONFIANZA_AYUDA: Record<ConfKind, { que: string; hacer: string }> = {
  alta: {
    que: 'Se encontró en tu catálogo un concepto equivalente y el sistema está seguro de que corresponde. El precio sale de tu catálogo.',
    hacer: 'Puedes usarlo tal cual; revísalo solo si quieres confirmarlo.',
  },
  media: {
    que: 'Se encontró un concepto parecido, pero el sistema no está seguro de que sea el mismo. El precio sale de tu catálogo.',
    hacer: 'Revísalo antes de usarlo: confirma que el equivalente corresponde; si no, analízalo con IA para que arme el precio.',
  },
  sin_equivalente: {
    que: 'No se encontró en tu catálogo un concepto equivalente, así que el renglón todavía no tiene precio.',
    hacer: 'Analízalo con IA para que arme el precio.',
  },
  sintetica: {
    que: 'El precio lo armó la inteligencia artificial porque no había un equivalente en tu catálogo.',
    hacer: 'Revísalo y apruébalo (o recházalo); si no te convence, ajusta el contexto de proyecto y vuelve a generarlo.',
  },
  sin_procesar: {
    que: 'Este renglón todavía no se procesó (la hoja se detuvo antes de llegar a él), así que aún no tiene precio ni se le buscó equivalente.',
    hacer: 'Vuelve a buscar equivalentes para que se procese.',
  },
};
// El estado "sin_procesar" es de borde (hoja detenida a mitad): NO va en la
// leyenda del encabezado, solo en el tooltip del renglón que quedó pendiente.
const CONFIANZA_ORDEN: ConfKind[] = ['alta', 'media', 'sin_equivalente', 'sintetica'];

// Leyenda completa de la columna (los 4 niveles). Va en el ⓘ del encabezado.
function LeyendaConfianza() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
      <div style={{ fontWeight: 700 }}>Qué tan seguro está el sistema del precio de cada renglón:</div>
      {CONFIANZA_ORDEN.map((k) => (
        <div key={k} style={{ display: 'flex', gap: '7px' }}>
          <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', marginTop: '5px', background: CONFIANZA_ACCENT[k] }} />
          <div>
            <span style={{ fontWeight: 700 }}>{CONFIANZA_LABEL[k] ?? k}</span> — {CONFIANZA_AYUDA[k].que}{' '}
            <span style={{ color: 'var(--text-tertiary)' }}>{CONFIANZA_AYUDA[k].hacer}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function parseFuente(fuente: string | null): { llmVeredicto: 'si' | 'parcial' | null } {
  if (!fuente) return { llmVeredicto: null };
  const llmVeredicto = fuente.startsWith('llm-batch:si') ? 'si'
    : fuente.startsWith('llm-batch:parcial') ? 'parcial'
    : null;
  return { llmVeredicto };
}

/** True si ambas unidades existen y NO son equivalentes (incluye aliases m=ml, ton=t, pza=pz=pieza). */
function unitsDiffer(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return !unitsAreEquivalent(a, b);
}

// Estimación de costo IA SOLO para staff interno (el cliente nunca la ve).
// La tarifa NO está hardcodeada: sale de la tabla `llm_pricing` vía pricingStore
// (única fuente de verdad, la mantiene al día el cron `check-llm-pricing`).
function calcLlmCost(rate: { in: number; out: number }, inputTokens: number, outputTokens: number): number {
  return (inputTokens / 1000) * rate.in + (outputTokens / 1000) * rate.out;
}
function fmtCost(usd: number): string {
  if (usd < 0.001) return `$${usd.toFixed(6)}`;
  if (usd < 1) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}
// Los importes se muestran en LA MONEDA DE LA OBRA (`quote.moneda`), que es la
// misma con la que se decidió de qué catálogos salen los precios. Antes esto era
// 'MXN' fijo y un presupuesto chileno se leía en pesos mexicanos. Sin moneda
// conocida el número va sin símbolo (ver lib/money.ts).

// Comandos de cotización ocultos temporalmente (pedido 2026-06-09): se conserva
// todo el código, solo no se renderizan. "Enviar a Módulo 1" NO debe existir.
const SHOW_QUOTE_COMMANDS = false;
// "Aprobar todas" (bulk staff) oculto por el momento (2026-07-28): metía ruido en
// la barra. Código conservado; prender cuando se retire la vista de sintéticos.
const SHOW_APPROVE_ALL = false;

// Estilo del chip de filtro activo (acento), aplicado inline sobre confChip.
const CONF_CHIP_ACTIVE = {
  background: 'var(--accent-weak)',
  border: '1px solid var(--accent)',
  color: 'var(--accent)',
};

const useStyles = makeStyles({
  page: {
    height: '100vh',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  // Área scrolleable (head + stats + split) — la página scrollea como el mockup;
  // topbar y breadcrumb quedan fijos (filas 1 y 2 del grid).
  scrollArea: {
    // clip (no hidden): con overflow-y:auto, overflow-x:hidden se recalcula
    // como auto y un hijo animado con transform ensancha la página.
    overflowY: 'auto',
    overflowX: 'clip',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    position: 'relative',
  },
  topRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    // padding-bottom: aire entre la leyenda/chip y el grid de conceptos
    // (antes 0 → quedaba pegado al panel al leer/rearmar el listado).
    padding: '18px 18px 16px',
    background: 'transparent',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  headLine: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--space-l)',
    flexWrap: 'wrap',
    minWidth: 0,
    maxWidth: '100%',
  },
  /** Leyenda de prioridad + chip OPUS + cuota de conceptos. */
  metaLine: {
    display: 'flex',
    alignItems: 'center',
    // Leyenda de lectura a la IZQUIERDA; el pill de estado ("Listo para OPUS 27" /
    // "En OPUS 27") va a la derecha (via marginLeft:auto en su contenedor).
    justifyContent: 'flex-start',
    gap: 'var(--space-l)',
    flexWrap: 'wrap',
    minWidth: 0,
    maxWidth: '100%',
    marginTop: '4px',
    paddingTop: '10px',
    paddingBottom: '4px',
    borderTop: '1px solid var(--border-subtle)',
  },

  headActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    flexWrap: 'wrap',
  },
  // Barra de acciones principal de la cotización: botones grandes y centrados
  // (reunión 2026-07-02 — los subtle chicos alineados a la derecha se perdían).
  // Grid auto-fit: los pasos NUNCA suman min-content en una sola fila
  // (era la causa del ensanche + Contexto de proyecto cortado).
  actionsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
    gap: 'var(--space-s)',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    padding: 'var(--space-s) 0 var(--space-xs)',
    boxSizing: 'border-box',
  },
  actionsRight: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 'var(--space-s)',
    minWidth: 0,
    maxWidth: '100%',
  },
  subMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-m)',
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  // ── Tira "cómo funciona" (mockup) ──
  // ── Botón de paso (número + título arriba, subtítulo abajo) ──
  stepGroup: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '4px',
    minWidth: 0,
    maxWidth: '100%',
    width: '100%',
  },
  stepBtn: {
    height: 'auto',
    minHeight: '58px',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    textAlign: 'left',
    paddingTop: '9px',
    paddingBottom: '9px',
    gap: '2px',
    maxWidth: '100%',
    minWidth: 0,
  },
  stepBtnTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: 'var(--fs-300)',
    fontWeight: 600,
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  stepNum: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: 'var(--bg-overlay, rgba(0,0,0,.08))',
    color: 'var(--text-secondary)',
    fontSize: '11px',
    fontWeight: 700,
    flex: 'none',
  },
  stepBtnSub: {
    display: 'block',
    fontWeight: 400,
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    maxWidth: '260px',
    whiteSpace: 'normal',
    lineHeight: 1.3,
  },
  helpBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '19px',
    height: '19px',
    borderRadius: '50%',
    border: '1.5px solid var(--border-default)',
    color: 'var(--text-tertiary)',
    fontSize: '11px',
    fontWeight: 700,
    cursor: 'pointer',
    background: 'var(--bg-surface)',
    flex: 'none',
    alignSelf: 'center',
    ':hover': { border: '1.5px solid var(--accent)', color: 'var(--accent)' },
  },
  helpCard: {
    maxWidth: '340px',
    fontSize: 'var(--fs-200)',
    lineHeight: 1.45,
    color: 'var(--text-primary)',
  },
  helpCardTitle: { fontWeight: 700, color: 'var(--accent)' },
  actionsSep: { width: '1px', alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '0 2px' },
  ctxDone: { border: '1.5px solid var(--ok)', background: 'var(--ok-weak)' },
  ctxPending: { border: '1.5px solid var(--warn)' },
  menuItemSub: { fontSize: 'var(--fs-100)', color: 'var(--text-tertiary)', display: 'block', marginTop: '2px', whiteSpace: 'normal', maxWidth: '360px', lineHeight: 1.35 },
  menuItemRec: {
    display: 'inline-block',
    background: 'var(--ok-weak)',
    color: 'var(--ok)',
    border: '1px solid var(--ok)',
    fontSize: '9px',
    fontWeight: 700,
    borderRadius: '4px',
    padding: '0 5px',
    marginLeft: '6px',
    verticalAlign: '1px',
  },
  titleBlock: { flex: 1, minWidth: 0 },
  topTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--fs-400)',
    fontWeight: 700,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  topSub: {
    margin: 0,
    flex: 1,
    minWidth: 0,
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
  },
  // Tira de 4 stats (mockup V4)
  cotSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
    gap: '1px',
    background: 'var(--border-subtle)',
    border: '1px solid var(--border-subtle)',
    maxWidth: '100%',
    minWidth: 0,
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    margin: '14px 18px 18px',
  },
  cotCell: { background: 'var(--bg-surface)', padding: '15px 17px' },
  cotCellLink: { cursor: 'pointer', ':hover': { background: 'var(--bg-hover)' } },
  cotLab: {
    fontSize: '10.5px',
    letterSpacing: '0.7px',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
    fontWeight: 650,
  },
  cotBig: {
    fontSize: 'var(--fs-600)',
    fontWeight: 700,
    margin: '5px 0 2px',
    letterSpacing: '-0.5px',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-primary)',
  },
  cotNote: { fontSize: '11.5px', color: 'var(--text-secondary)' },
  shell: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 286px) minmax(0, 1fr)',
    gap: '18px',
    padding: '0 18px 18px',
    alignItems: 'start',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    // visible (antes 'hidden'): un ancestro con overflow no-visible se vuelve el
    // contenedor del `position:sticky` del thead y lo despega del área de scroll.
    // El recorte horizontal ya lo da scrollArea (overflowX:'clip').
    overflow: 'visible',
  },
  leftPanel: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    alignSelf: 'start',
    position: 'sticky',
    top: '0',
    maxHeight: 'calc(100vh - 120px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-m)',
    padding: '15px',
    overflowY: 'auto',
    overflowX: 'hidden',
    minWidth: 0,
    maxWidth: '100%',
  },
  panelCollapseBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '26px',
    height: '26px',
    flexShrink: 0,
    borderRadius: '6px',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-overlay)',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontSize: '13px',
    lineHeight: 1,
    ':hover': { background: 'var(--bg-raised)', color: 'var(--text-primary)' },
  },
  panelLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  rightPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-m)',
    minWidth: 0,
    maxWidth: '100%',
    // visible (antes 'hidden'): ver nota en `shell`/`table`. Necesario para que
    // el thead se fije a scrollArea y el header quede pegado al recorrer el grid.
    overflow: 'visible',
  },
  quoteHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-m)',
    flexWrap: 'wrap',
  },
  quoteName: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--fs-300)',
    fontWeight: 600,
  },
  meta: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
  },
  controlBar: {
    display: 'flex',
    gap: 'var(--space-s)',
    flexWrap: 'wrap',
  },
  progressLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
  },
  progressBarOuter: {
    height: '4px',
    background: 'var(--border-subtle)',
    borderRadius: '999px',
    overflow: 'hidden',
  },
  progressBarInner: {
    height: '100%',
    background: tokens.colorBrandBackground,
    transition: 'width 0.3s',
  },
  searchRow: {
    display: 'flex',
    gap: 'var(--space-s)',
    alignItems: 'center',
  },
  searchInput: {
    flex: 1,
  },
  // Aviso de actualización en segundo plano. Ocupa su lugar SIEMPRE (se esconde con
  // visibility, no se desmonta) para que aparecer y desaparecer no mueva la fila de
  // filtros ni un pixel — el salto era justo lo que se estaba arreglando.
  refreshHint: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-xs)',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
  },
  summaryRow: {
    display: 'flex',
    gap: 'var(--space-l)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
  },
  summaryNum: {
    fontWeight: 600,
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-mono)',
  },
  confStrip: {
    display: 'flex',
    gap: 'var(--space-s)',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  confChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '30px',
    fontSize: 'var(--fs-200)',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    borderRadius: '999px',
    padding: '0 12px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textTransform: 'capitalize',
    transitionProperty: 'color, background, border-color',
    transitionDuration: '120ms',
    ':hover': { color: 'var(--text-primary)' },
  },
  confDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  table: {
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-surface)',
    maxWidth: '100%',
    minWidth: 0,
    // overflow CLIP (antes 'auto', luego 'visible'): 'hidden' rompía el
    // `position:sticky` del thead (lo pegaba a la tabla en vez de al área
    // scrolleable) y 'visible' dejaba asomar la línea de color del concepto bajo
    // la esquina redondeada. 'clip' recorta las esquinas SIN crear contenedor de
    // scroll → no captura el sticky. Columnas minmax(0,1fr): sin scroll horizontal.
    overflow: 'clip',
  },
  thead: {
    display: 'grid',
    gridTemplateColumns: '16px 72px minmax(0, 1fr) 56px 64px 92px 116px minmax(0, 120px) 92px',
    columnGap: '10px',
    padding: '11px 14px',
    background: tokens.colorNeutralBackground2,
    borderBottom: '1px solid var(--border-subtle)',
    fontSize: '10.5px',
    fontWeight: 650,
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    // SIN border-radius propio: al estar sticky y scrollear, su esquina
    // redondeada dejaba una muesca transparente por donde asomaba la franja roja
    // de la fila de atrás (el clip del .table no aplica a media caja). El
    // redondeo superior lo da el `overflow:clip` + border-radius del .table en
    // reposo; al fijarse, el header va con esquinas rectas (sin muesca).
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
  },
  tbody: {},
  selBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    padding: '8px 14px',
    background: tokens.colorNeutralBackground2,
    borderBottom: '1px solid var(--border-subtle)',
    borderTopLeftRadius: 'var(--radius-lg)',
    borderTopRightRadius: 'var(--radius-lg)',
    fontSize: '12px',
  },
  selHint: {
    fontSize: '11px',
    color: 'var(--text-tertiary)',
  },
  trow: {
    display: 'grid',
    gridTemplateColumns: '16px 72px minmax(0, 1fr) 56px 64px 92px 116px minmax(0, 120px) 92px',
    columnGap: '10px',
    rowGap: 'var(--space-xs)',
    alignItems: 'start',
    padding: '10px 14px',
    minHeight: '44px',
    borderBottom: '1px solid var(--border-subtle)',
    fontSize: '12px',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
  },
  claveCell: {
    fontFamily: 'var(--font-mono)',
    fontSize: '12px',
    color: 'var(--text-secondary)',
    // La clave se muestra COMPLETA (QA 2026-07-04): claves largas tipo
    // "4.3.2. 010.1-A" se cortaban con ellipsis; ahora envuelven.
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  desc: {
    paddingTop: '1px',
    minWidth: 0,
    lineHeight: '1.45',
    whiteSpace: 'normal',
    fontSize: '12px',
  },
  num: {
    fontFamily: 'var(--font-mono)',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  // Unidad con el MISMO font y color que la descripción (reunión 2026-07-02 +
  // reporte 2026-07-03: el mono/gris tenue seguía viéndose chico e ilegible).
  unitCell: {
    fontSize: '12px',
    color: 'var(--text-primary)',
  },
  confPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: '22px',
    padding: '3px 9px',
    borderRadius: '999px',
    fontSize: '11.5px',
    fontWeight: 650,
    // Envuelve a 2 líneas dentro de su columna (etiquetas largas como
    // "Equivalente por confirmar" se encimaban con el toggle de al lado).
    whiteSpace: 'normal',
    maxWidth: '100%',
    lineHeight: 1.2,
    boxSizing: 'border-box',
  },
  confPillDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: 'currentColor',
    flexShrink: 0,
  },
  empty: {
    padding: 'var(--space-xxl)',
    textAlign: 'center',
    color: 'var(--text-tertiary)',
  },
  inputCell: {
    width: '100%',
    minWidth: 0,
  },
  loading: {
    display: 'grid',
    placeItems: 'center',
    height: '100%',
  },
  // Agrupador L1 — sección principal (fondo oscuro, tipografía prominente)
  groupRowL1: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    padding: '10px var(--space-m)',
    borderBottom: '1px solid var(--border-subtle)',
    background: tokens.colorBrandBackground2,
    borderLeft: `5px solid ${tokens.colorBrandBackground}`,
  },
  groupNombreL1: {
    fontWeight: 700,
    fontSize: 'var(--fs-200)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: tokens.colorBrandForeground1,
    flex: 1,
    minWidth: 0,
    lineHeight: '1.4',
  },
  // Agrupador L2 — subsección (fondo sutil, peso medio)
  groupRowL2: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    padding: '8px var(--space-m)',
    borderBottom: '1px solid var(--border-subtle)',
    background: tokens.colorNeutralBackground3,
    borderLeft: `3px solid ${tokens.colorBrandBackground}`,
  },
  groupNombreL2: {
    fontWeight: 600,
    fontSize: 'var(--fs-200)',
    letterSpacing: '0.02em',
    color: 'var(--text-primary)',
    flex: 1,
    minWidth: 0,
    lineHeight: '1.4',
  },
  groupClave: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    fontWeight: 700,
    color: tokens.colorBrandForeground1,
    minWidth: '52px',
  },
  conceptIndent: {
    paddingLeft: 'var(--space-xl)',
  },
  trowOuter: {
    cursor: 'pointer',
    // Hover: pinta el fondo como fila enfocada (la .trow interna es transparente
    // salvo cuando está enfocada, así que este fondo se ve al pasar el mouse).
    ':hover': { background: tokens.colorNeutralBackground1Hover },
  },
  expandPanel: {
    background: tokens.colorNeutralBackground2,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '8px 12px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    fontSize: '11px',
  },
  expandCols: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    flex: 1,
    minHeight: 0,
  },
  expandSide: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    overflow: 'hidden',
  },
  expandColLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: '9px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: 'var(--text-tertiary)',
    marginBottom: '2px',
  },
  expandColDesc: {
    fontSize: '11px',
    lineHeight: '1.5',
    color: 'var(--text-primary)',
    textTransform: 'none' as const,
  },
  expandColMeta: {
    fontSize: '10px',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-mono)',
    marginTop: '2px',
  },
  expandBottom: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap' as const,
    flexShrink: 0,
  },
  scoreWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  scoreTrack: {
    width: '100px',
    height: '5px',
    borderRadius: '999px',
    position: 'relative',
    flexShrink: 0,
    overflow: 'visible',
  },
  scorePct: {
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    fontSize: '11px',
    color: 'var(--text-primary)',
    minWidth: '28px',
  },
  llmVerdict: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: '11px',
    fontWeight: 600,
  },
  notaText: {
    fontSize: '10px',
    color: 'var(--text-tertiary)',
    fontStyle: 'italic' as const,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
});

/**
 * FILTRO POR ESTADO DEL RENGLÓN.
 *
 * Queda UNO solo: **por revisar**, los renglones que entraron marcados porque no se
 * pudieron leer del documento y todavía nadie corrigió. Con eso, el aviso de arriba no
 * necesita navegación propia: aplica el filtro y abre el primero.
 *
 * SE QUITARON "Con precio" y "Sin precio" (2026-08-20, pedido de Sebastián: confundían).
 * Por qué confundían: preguntaban casi lo mismo que los chips de confianza que están al
 * lado —un renglón "Sin equivalente" es justamente uno sin precio—, así que la tira
 * ofrecía dos caminos distintos para la misma pregunta y ninguno de los dos quedaba
 * claro. La cuenta de cuántos renglones tienen precio no se pierde: sigue en la tira de
 * totales, que es donde se lee sin tener que filtrar nada.
 */
type FilterMode = 'all' | 'por_revisar';

/** Entró marcado porque no se pudo leer del documento (migr 083) y nadie lo corrigió aún. */
function porRevisar(c: QuoteConcept): boolean {
  return c.origen === 'conteo_pagina' && !c.editadoManual;
}


export function QuotePage() {
  const styles = useStyles();
  const confirm = useConfirm();
  const navigate = useNavigate();
  // Ayuda "?" de cada paso (Popover con la tarjeta del mockup).
  const helpDot = useCallback((k: keyof typeof HELP_CARDS) => (
    <Popover withArrow positioning="below">
      <PopoverTrigger disableButtonEnhancement>
        <span className={styles.helpBtn} role="button" tabIndex={0} aria-label="Ayuda">?</span>
      </PopoverTrigger>
      <PopoverSurface>
        <div className={styles.helpCard}>
          <div className={styles.helpCardTitle}>{HELP_CARDS[k].title}</div>
          {HELP_CARDS[k].body}
        </div>
      </PopoverSurface>
    </Popover>
  ), [styles]);
  const { id: projectId, qid: quoteId } = useParams<{ id: string; qid: string }>();
  const canMutate = useCanMutate();
  const isEcosoftStaff = useIsEcosoftStaff();
  // PROTOTIPO staff-only "de la HP a la geometría": plano 2D + modelo 3D BIM + render.
  const [geoOpen, setGeoOpen] = useState(false);
  // Tarifa LLM (staff only) desde la tabla `llm_pricing` vía store — no hardcode.
  const pricingData = usePricingStore((s) => s.data);
  const ensurePricing = usePricingStore((s) => s.ensureLoaded);
  const llmRate = modelRatePer1k(pricingData, 'deepseek-v4-flash');
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  const [quote, setQuote] = useState<Quote | null>(null);
  // Formateadores de plata de ESTA obra. Se rearman solo si cambia la moneda.
  const fmtObra = useMemo(() => formatoDeMoneda(quote?.moneda), [quote?.moneda]);
  const fmtMoney = fmtObra.money;
  const [concepts, setConcepts] = useState<QuoteConcept[]>([]);
  const [conceptsTotal, setConceptsTotal] = useState(0);
  const [conceptsLoading, setConceptsLoading] = useState(false);
  /**
   * Recarga EN SEGUNDO PLANO del listado (mientras se trabaja sobre el presupuesto).
   * Es distinta de `conceptsLoading`: esa tapa la pantalla con un spinner y solo
   * corresponde la PRIMERA vez, cuando todavía no hay nada que mirar. Una vez que la
   * tabla está en pantalla, actualizarla no puede sacarla: el usuario pierde el renglón
   * que estaba leyendo y su posición en la lista (reporte de Sebastián, 2026-08-12).
   */
  const [conceptsRefreshing, setConceptsRefreshing] = useState(false);
  const [liveLlmTokens, setLiveLlmTokens] = useState<{ input: number; output: number } | null>(null);
  const [phaseTimings, setPhaseTimings] = useState<string | null>(null);
  /** Cuándo se trajo el listado por última vez (para espaciar las recargas en vivo). */
  const lastConceptRefresh = useRef<number>(0);
  /** Último avance visto de la búsqueda de equivalentes: si no cambió, no hay qué traer. */
  const ultimoAvancePricing = useRef<string>('');

  // Nombre del proyecto + archivos (para elegir el archivo del listado de conceptos).
  const [projectName, setProjectName] = useState<string | null>(null);
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  // Láminas de planos que todavía no se leyeron: se avisa antes de generar.
  const [planosPorLeer, setPlanosPorLeer] = useState(0);
  const [sourceSaving, setSourceSaving] = useState(false);
  // Etiqueta el job del topbar con el nombre del proyecto para que el pill
  // "Presupuesto listo" diga de cuál es (evita saltar a otro proyecto a ciegas).
  useEffect(() => {
    if (!quoteId || !projectName) return;
    const s = useQuoteJobsStore.getState();
    s.setLabel('quote', quoteId, projectName);
    s.setLabel('apu_ia', quoteId, projectName);
  }, [quoteId, projectName]);
  useEffect(() => {
    if (!projectId) return;
    getProject(projectId)
      .then((r) => {
        setProjectName(r.project.name);
        setProjectFiles(r.files);
        setPlanosPorLeer(r.project.leerPlanos === false ? 0 : (r.planosPendientes ?? 0));
      })
      .catch(() => {});
  }, [projectId]);

  // ¿El usuario ya ajustó el contexto de proyecto? Sin registro (404) o solo
  // deducido por la IA → aviso en el botón (pedido Sebastián 2026-07-06).
  // Se recarga al volver a la pestaña (p. ej. regresando de /obra-context).
  const [obraContextAjustado, setObraContextAjustado] = useState<boolean | null>(null);
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    const check = () => {
      getObraContext(projectId)
        .then((r) => { if (alive) setObraContextAjustado(r.source !== 'deduced'); })
        .catch(() => { if (alive) setObraContextAjustado(false); });
    };
    check();
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; window.removeEventListener('focus', onFocus); };
  }, [projectId]);

  // Cargar la tarifa LLM (solo staff) para el costo "Análisis IA".
  useEffect(() => {
    if (isEcosoftStaff) void ensurePricing();
  }, [isEcosoftStaff, ensurePricing]);

  const [allCatalogs, setAllCatalogs] = useState<Catalog[]>([]);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<string[]>([]);
  const [catalogsSaving, setCatalogsSaving] = useState(false);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [liveProgress, setLiveProgress] = useState<QuoteProgressEvent['data'] | null>(null);
  /**
   * Cuándo llegó el último aviso del proceso de esta cotización (epoch ms).
   * Es la prueba de que hay alguien trabajando: el worker repite el último estado
   * cada ~5 s, así que sin avisos recientes la pantalla NO puede decir "sigue
   * trabajando" (ver QuoteBuildingAnimation).
   */
  const [ultimoAvisoAt, setUltimoAvisoAt] = useState<number | null>(null);

  const [search, setSearch] = useState('');
  // Filtro por estado del renglón (con precio / sin precio / por revisar). Convive con el
  // filtro por confianza: son dos preguntas distintas y se cruzan.
  const [filter, setFilter] = useState<FilterMode>('all');
  // Filtro por confianza (chips estilo mockup): 'all' | clave de confianza.
  const [confFilter, setConfFilter] = useState<'all' | ConfKind>('all');
  // Panel izquierdo (selector de listado + catálogos de referencia) colapsable
  // para ensanchar el árbol de conceptos (2026-07-25).
  const [leftCollapsed, setLeftCollapsed] = useState(false);

  const expandedIdRef = useRef<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Concepto cuyo "Ver análisis de precio unitario" está abierto (dentro del panel).
  const [analysisOpenId, setAnalysisOpenId] = useState<string | null>(null);
  // Concepto enfocado en el PANEL LATERAL de precio (drawer). Sigue a la fila
  // expandida: al picar otra fila del grid, el panel se actualiza. Cerrar con la X.
  const [panelId, setPanelId] = useState<string | null>(null);
  // Panel de análisis estilo Gmail: a la derecha o abajo, redimensionable.
  // Persistido en localStorage (namespace supernova:*).
  type PaneMode = 'derecha' | 'abajo';
  const [paneMode, setPaneMode] = useState<PaneMode>(
    () => (localStorage.getItem('supernova:analysis-pane-mode') === 'abajo' ? 'abajo' : 'derecha'),
  );
  // Ancho inicial del panel a la derecha: angosto a propósito (400 px) para que la
  // columna DESCRIPCIÓN del listado no quede aplastada en un bloque altísimo de texto
  // envuelto. El usuario puede ensancharlo arrastrando el borde (mín. 340, máx. 760) y
  // eso sí se recuerda. La clave lleva sufijo `-w2` porque los anchos guardados con el
  // default viejo (480+) tapaban el cambio: quien ya tenía uno guardado nunca lo vería.
  const [paneW, setPaneW] = useState<number>(() => Number(localStorage.getItem('supernova:analysis-pane-w2')) || 400);
  const [paneH, setPaneH] = useState<number>(() => Number(localStorage.getItem('supernova:analysis-pane-h')) || 340);
  const changePaneMode = (m: PaneMode) => {
    setPaneMode(m);
    try { localStorage.setItem('supernova:analysis-pane-mode', m); } catch { /* noop */ }
    // Si no hay concepto enfocado, abre el primero para que se note que el panel
    // se activó (si no, parecería que el botón no hace nada). No durante la
    // generación (el panel queda bloqueado).
    if (!panelId && !isGenerating) {
      const first = concepts.find((c) => c.kind !== 'group');
      if (first) setPanelId(first.id);
    }
  };
  const changePaneSize = (n: number) => {
    if (paneMode === 'abajo') {
      setPaneH(n);
      try { localStorage.setItem('supernova:analysis-pane-h', String(Math.round(n))); } catch { /* noop */ }
    } else {
      setPaneW(n);
      try { localStorage.setItem('supernova:analysis-pane-w2', String(Math.round(n))); } catch { /* noop */ }
    }
  };
  // El panel se muestra como hermano flex del scroll (empuja, no encima).
  const paneOpen = panelId != null;
  // El árbol se aprieta si el panel va a la derecha con los catálogos abiertos
  // (i4). Mientras el panel esté a la derecha, colapsa los catálogos para darle
  // ancho al árbol; los restaura al cerrar o cambiar a "abajo". Respeta lo que el
  // usuario elija manualmente mientras tanto.
  const leftCollapsedByPane = useRef(false);
  useEffect(() => {
    const rightOpen = paneOpen && paneMode === 'derecha';
    if (rightOpen && !leftCollapsed) {
      leftCollapsedByPane.current = true;
      setLeftCollapsed(true);
    } else if (!rightOpen && leftCollapsedByPane.current) {
      leftCollapsedByPane.current = false;
      setLeftCollapsed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paneOpen, paneMode]);
  // Al ABRIR el panel (de cerrado a abierto), encuadra el grid: lleva su cabecera
  // (fila de filtros) al tope del área de contenido, para que el árbol + el panel
  // ocupen la pantalla (los pasos/KPIs quedan arriba, fuera de vista). No se
  // dispara al cambiar de un concepto a otro (solo en la apertura).
  const gridTopRef = useRef<HTMLDivElement | null>(null);
  const prevPanelIdRef = useRef<string | null>(null);
  useEffect(() => {
    const wasClosed = prevPanelIdRef.current == null;
    prevPanelIdRef.current = panelId;
    if (panelId && wasClosed) {
      requestAnimationFrame(() => {
        const area = scrollAreaRef.current;
        const top = gridTopRef.current;
        if (!area || !top) return;
        // Mueve SOLO el scrollArea (no la ventana; scrollIntoView corría el
        // topbar). Lleva la fila de filtros al tope del área de contenido.
        const delta = top.getBoundingClientRect().top - area.getBoundingClientRect().top;
        // ENCUADRAR ES SUBIR LOS KPIs, NUNCA DEVOLVER AL PRINCIPIO DE LA HOJA.
        //
        // El encuadre existe para sacar de la pantalla los pasos y los KPIs y dejarle
        // todo el alto al árbol + el panel. Eso solo tiene sentido cuando esos bloques
        // TODAVÍA SE VEN, o sea cuando la cabecera del grid está por debajo del tope
        // del área: ahí `delta` es positivo y hay que bajar un poco.
        //
        // Si ya estás dentro de la hoja, la cabecera del grid quedó muy arriba,
        // `delta` sale negativo y "llevarla al tope" significa VOLVER AL PRINCIPIO.
        // Medido el 2026-08-27 en la hoja de 2.838 renglones de ABEL MATA: picar el
        // renglón 1103, a 118.743 px de scroll, mandaba la vista a 662 px —se movía
        // sola 118.081 px— y el renglón que el cotizador acababa de picar desaparecía
        // de la pantalla (quedaban a la vista el 0 al 18). Se pica un concepto para
        // VERLO, así que perderlo de vista es lo contrario de lo que se pidió.
        if (delta <= 1) return;
        area.scrollTo({ top: area.scrollTop + delta, behavior: 'smooth' });
      });
    }
  }, [panelId]);
  const actionsRowRef = useRef<HTMLDivElement | null>(null);
  function goToEquivalentes() {
    // Scroll INSTANTÁNEO al paso 2 (scrollIntoView encuentra el contenedor real;
    // la página usa overflow:hidden, así que window.scrollTo no sirve). Instantáneo
    // evita la carrera con el scroll suave.
    actionsRowRef.current?.scrollIntoView({ block: 'start' });
  }
  // "Cotizar con IA" inline: marca de intención de conceptos NO generados (los
  // ya generados salen de la cola y se muestran como "✓ con IA").
  const [iaMarks, setIaMarks] = useState<Record<string, boolean>>({});
  // Grupo actualmente encendido en el control del header "Generar sintético"
  // (excluyente: encender uno apaga el anterior). null = ninguno.
  const [iaScope, setIaScope] = useState<ConfKind | 'all' | null>(null);
  /**
   * Picar una fila significa "mostrame ESTE concepto", y eso es IDEMPOTENTE: volver a
   * picar la fila que el panel ya está mostrando NO hace nada.
   *
   * Antes era un toggle y re-picar la misma fila cerraba el panel, que se sentía como
   * una falla: el usuario apuntó al renglón que quería ver, no a cerrar. Cerrar es otra
   * intención y tiene sus propios caminos —la ✕ del panel y el clic en una zona muerta
   * del listado—, que siguen intactos.
   *
   * La guarda mira `panelId` (lo que el panel MUESTRA) y no la fila expandida: si el
   * panel se cerró con la ✕ o clicando afuera, la fila sigue siendo la última enfocada,
   * y picarla tiene que volver a abrirlo.
   */
  function focusConcept(id: string) {
    // Bloqueado durante la generación: el panel se mantiene cerrado.
    if (isGenerating) return;
    if (panelId === id) return;
    expandedIdRef.current = id;
    setExpandedId(id);
    setAnalysisOpenId(null);
    // El panel lateral sigue a la fila enfocada.
    setPanelId(id);
  }

  /** Renglón corregido en este momento: sigue contando para las flechas aunque el filtro
   *  aplicado ya no lo incluya (ver el cálculo de `nav` en el panel). Se olvida en cuanto
   *  se cambia de filtro o de búsqueda. */
  const recienCorregidoRef = useRef<string | null>(null);
  useEffect(() => { recienCorregidoRef.current = null; }, [filter, confFilter, search]);

  /**
   * "Revisar estos renglones" (aviso de hoja incompleta): aplica el filtro "Por revisar" y
   * abre el primero. Desde ahí las flechas del panel recorren esa misma lista, así que el
   * aviso no necesita su propia navegación.
   */
  function abrirPorRevisar() {
    setSearch('');
    setConfFilter('all');
    setFilter('por_revisar');
    const primero = concepts.find((c) => c.kind !== 'group' && porRevisar(c));
    if (primero) focusConcept(primero.id);
  }

  // ===== Carga inicial =====
  //
  // Lo ÚNICO indispensable para abrir el presupuesto es el presupuesto: si la lista de
  // listas de precios no se puede traer, la hoja se muestra igual y lo único que queda
  // corto es el selector de listas. Antes las dos venían en el mismo `Promise.all`, así
  // que cualquier tropiezo de la accesoria dejaba la pantalla en el mensaje de error y
  // el presupuesto no se veía (le pasó al visitante del sitio de demostración,
  // 2026-08-14). Una cosa que se ve de más nunca puede tapar la que se venía a ver.
  useEffect(() => {
    if (!projectId || !quoteId) return;
    void (async () => {
      try {
        const [q, tenantCats] = await Promise.all([
          getQuote(quoteId),
          listCatalogs().catch(() => [] as Catalog[]),
        ]);
        setQuote(q);
        setAllCatalogs(tenantCats);
        // Demo: si el seed aún no trajo catalog_ids, mostrar ECOSTOS seleccionado.
        let cats = q.catalogIds ?? [];
        if (q.isExample && cats.length === 0) {
          const eco = findEcostosCatalogId(tenantCats);
          if (eco) cats = [eco];
        }
        setSelectedCatalogIds(cats);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, quoteId]);

  /** Cotización cuyo listado ya está en pantalla (para saber si esta carga es la primera). */
  const conceptsShownForRef = useRef<string | null>(null);
  /** Nº de la última consulta lanzada: descarta respuestas viejas que llegan tarde. */
  const conceptsReqRef = useRef(0);

  const loadConcepts = useCallback(async (id: string) => {
    // La primera carga de ESTA cotización es la única que puede tapar la pantalla:
    // no hay nada abajo que ocultar. Las siguientes son silenciosas — la tabla se queda
    // visible con su scroll y los datos se reemplazan cuando llegan.
    const primera = conceptsShownForRef.current !== id;
    if (primera) setConceptsLoading(true);
    else setConceptsRefreshing(true);
    const req = ++conceptsReqRef.current;
    try {
      const r = await getQuoteConcepts(id, { limit: 5000 });
      // Dos recargas encimadas: solo manda la última pedida (si no, una respuesta
      // lenta podría pisar datos más nuevos).
      if (req !== conceptsReqRef.current) return;
      setConcepts(r.items);
      setConceptsTotal(r.total);
      conceptsShownForRef.current = id;
    } catch (e) {
      if (req === conceptsReqRef.current) setErr((e as Error).message);
    } finally {
      if (primera) setConceptsLoading(false);
      if (req === conceptsReqRef.current) setConceptsRefreshing(false);
    }
  }, []);

  /**
   * Recarga del listado JUNTANDO los avisos del worker: como máximo una consulta cada
   * `RECARGA_MIN_MS`, y la última gana.
   *
   * Por qué: el worker publica avance por concepto (y un latido cada 5 s). Recargar en
   * cada aviso significaba traer hasta 5.000 renglones cada 3 segundos — trabajo tirado
   * a la basura, porque en esa ventana cambian unos pocos renglones. Quien llama a esto
   * ya se aseguró de que algo VISIBLE se movió; el latido nunca llega hasta acá.
   */
  const RECARGA_MIN_MS = 4000;
  const recargaPendienteRef = useRef<number | null>(null);
  const scheduleConceptsRefresh = useCallback((id: string) => {
    if (recargaPendienteRef.current != null) return; // ya hay una en camino
    const espera = Math.max(0, RECARGA_MIN_MS - (Date.now() - lastConceptRefresh.current));
    recargaPendienteRef.current = window.setTimeout(() => {
      recargaPendienteRef.current = null;
      lastConceptRefresh.current = Date.now();
      void loadConcepts(id);
    }, espera);
  }, [loadConcepts]);
  useEffect(() => () => {
    if (recargaPendienteRef.current != null) window.clearTimeout(recargaPendienteRef.current);
  }, []);

  // Generación APU-IA en curso (store global alimentado por el WS). Con esto
  // mostramos el avance INLINE en el presupuesto (arriba del listado) sin
  // navegar a la vista de sintéticos.
  const apuJob = useQuoteJobsStore((s) => (quoteId ? s.jobs[`apu_ia:${quoteId}`] : undefined));
  // Generación de sintéticos en curso → bloquea la UI. Mientras corre no se debe
  // poder rearmar la HP, buscar equivalentes, mover toggles ni abrir el panel de
  // análisis: tocar el estado a medias corrompe el listado que el worker escribe.
  const isGenerating = apuJob?.status === 'running';
  const apuJobRunningRef = useRef(false);
  /**
   * Al dejar de correr —terminó, se detuvo o falló— se recarga el listado para mostrar los
   * precios nuevos Y se apagan los interruptores de "generar con inteligencia artificial"
   * que quedaron encendidos en la pantalla.
   *
   * Lo segundo es plata: cada interruptor encendido entra en la próxima generación, así que
   * uno que quedó prendido de la corrida anterior puede volver a generar —y volver a
   * cobrar— algo que ya está hecho, sin que el usuario lo pidiera (reporte de Sebastián,
   * 2026-08-12).
   *
   * Lo que se apaga es SOLO lo de la pantalla: `iaMarks` (las marcas optimistas, que le
   * ganan al dato del servidor) y el grupo encendido en la cabecera. **No se borra ninguna
   * elección guardada**: la decisión por concepto vive en la cotización y el servidor ya la
   * deja al día —al lanzar el lote marca los pedidos, y al generar cada análisis lo apaga—,
   * así que al soltar las marcas locales cada renglón muestra la verdad: apagado el que ya
   * se generó, y todavía encendido el que quedó pendiente (una corrida parcial o detenida),
   * que es exactamente lo que corresponde.
   */
  useEffect(() => {
    const running = apuJob?.status === 'running';
    if (running) {
      apuJobRunningRef.current = true;
    } else if (apuJobRunningRef.current) {
      apuJobRunningRef.current = false;
      setIaMarks({});
      setIaScope(null);
      if (quoteId) void loadConcepts(quoteId);
    }
  }, [apuJob?.status, quoteId, loadConcepts]);
  /**
   * Mientras se generan los análisis, el listado se actualiza cuando el worker AVISA que
   * terminó alguno — nunca por reloj.
   *
   * Antes había acá un temporizador ciego: cada 3 segundos volvía a traer el listado
   * completo (hasta 5.000 renglones) sin preguntar si algo había cambiado. Dos problemas:
   * la hoja se recargaba sola aunque no hubiera absolutamente nada corriendo (bastaba con
   * que un trabajo hubiera quedado marcado como "en curso" en la pantalla, y así quedaba),
   * y cada recarga sacaba la tabla de la pantalla. **Una hoja de presupuesto quieta no se
   * recarga sola**: el sistema ya avisa por sí mismo cuando algo cambió.
   *
   * `processed` es el número de análisis terminados que publica el worker. El latido —el
   * aviso cada ~5 s que solo dice "sigo trabajando"— no lo mueve, así que no dispara nada:
   * este efecto solo vuelve a correr cuando ese número cambia de verdad.
   */
  const apuProcesados = apuJob?.processed ?? 0;
  useEffect(() => {
    if (apuJob?.status !== 'running' || !quoteId) return;
    if (apuProcesados <= 0) return; // todavía no terminó ninguno: no hay nada nuevo que traer
    scheduleConceptsRefresh(quoteId);
  }, [apuProcesados, apuJob?.status, quoteId, scheduleConceptsRefresh]);

  /**
   * Red de seguridad para el caso en que el aviso de "terminó" NUNCA llegue: el navegador
   * estaba en otra página cuando la generación acabó, se cortó la conexión, o el trabajo
   * murió. Sin esto la pantalla se queda creyendo que hay algo en curso para siempre — con
   * los controles bloqueados y (antes) recargando la hoja cada 3 segundos.
   *
   * Es una red, no una fuente de datos, y solo se activa ante el SILENCIO: mientras el
   * trabajo dé señales de vida (avance o latido, cada ~5 s) manda lo que dicen esos avisos y
   * acá no se pregunta nada. Recién cuando pasan `SILENCIO_MS` sin una sola señal se le
   * pregunta al servidor por el ESTADO de la corrida — una fila, no el listado de
   * conceptos — y si ya no hay nada corriendo se cierra el trabajo. El listado se trae
   * entonces UNA vez, en silencio (lo hace el efecto de "al terminar, recargar").
   */
  const SILENCIO_MS = 15000;
  useEffect(() => {
    if (apuJob?.status !== 'running' || !quoteId || !projectId) return;
    let vivo = true;
    const revisar = async () => {
      // Se relee del almacén (y no de la variable del render) para mirar la hora del
      // último aviso sin volver a armar este efecto en cada uno.
      const job = useQuoteJobsStore.getState().jobs[`apu_ia:${quoteId}`];
      if (!job || job.status !== 'running') return;
      if (Date.now() - job.updatedAt < SILENCIO_MS) return; // sigue dando señales: no molestar
      try {
        const { runs } = await listApuIaRuns(quoteId);
        const ultimo = runs[0];
        if (!vivo || !ultimo) return;
        if (ultimo.status === 'queued' || ultimo.status === 'running') return;
        useQuoteJobsStore.getState().upsert({
          kind: 'apu_ia', quoteId, projectId,
          stage: ultimo.status === 'completed' ? 'done' : ultimo.status,
          status: ultimo.status === 'completed' ? 'done' : 'failed',
          total: ultimo.conceptos_total || undefined,
          processed: ultimo.conceptos_ok + ultimo.conceptos_partial + ultimo.conceptos_failed,
          // Si falló, arrastrar el motivo client-safe para que el aviso lo diga (esta red de
          // seguridad puede ser el único camino que reponga el estado si se perdió el vivo).
          ...(ultimo.status === 'failed' ? { message: mensajeFalloApuIa(ultimo.fail_reason) } : {}),
        });
      } catch { /* silencioso: es una red de seguridad, no una función de la pantalla */ }
    };
    void revisar();
    const t = window.setInterval(() => void revisar(), 10000);
    return () => { vivo = false; window.clearInterval(t); };
  }, [apuJob?.status, quoteId, projectId]);
  // Al arrancar la generación, cerrar el panel de análisis (y colapsar el desglose
  // inline). No se puede reabrir hasta terminar — ver guardas en focusConcept y en
  // el click de fila.
  useEffect(() => {
    if (isGenerating) {
      setPanelId(null);
      setExpandedId(null);
      expandedIdRef.current = null;
      setAnalysisOpenId(null);
    }
  }, [isGenerating]);

  useEffect(() => {
    if (quoteId) void loadConcepts(quoteId);
  }, [quoteId, loadConcepts]);

  // ===== Refrescar quote desde servidor =====
  /** Devuelve si de verdad trajo la hoja: hay un caso (el fin del armado) que necesita
   *  saberlo para no dejar la pantalla diciendo "armando…" para siempre. */
  const loadQuote = useCallback(async (): Promise<boolean> => {
    if (!quoteId) return false;
    try {
      const q = await getQuote(quoteId);
      setQuote(q);
      return true;
    } catch { return false; /* silencioso */ }
  }, [quoteId]);

  // Refresca al volver al browser — cubre "importé desde m1 (otra app), volví a la ventana"
  useEffect(() => {
    const onFocus = () => void loadQuote();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadQuote]);

  // Si la cotización ya venía corriendo al abrir la página (o tras un reload),
  // regístrala en el indicador global para no perderla al navegar.
  useEffect(() => {
    if (!quote || !projectId) return;
    if (quote.status === 'queued' || quote.status === 'running') {
      useQuoteJobsStore.getState().upsert({
        kind: 'quote', quoteId: quote.id, projectId,
        label: quote.name, stage: quote.status, status: 'running',
        total: quote.conceptsTotal || undefined,
        processed: quote.conceptsProcessed || undefined,
        pct: quote.conceptsTotal > 0
          ? Math.min(100, Math.round((quote.conceptsProcessed / quote.conceptsTotal) * 100))
          : 0,
      });
    }
  }, [quote?.id, quote?.status, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== WS eventos live =====
  const handleEvent = useCallback(
    (ev: ProjectEvent) => {
      // Alimenta el indicador global del topbar (sigue visible al navegar).
      if (projectId) ingestProjectEvent(projectId, ev);
      if (ev.type === 'quote:opus-import' && ev.data.quoteId === quoteId) {
        const d = ev.data as QuoteOpusImportEvent['data'];
        setQuote((prev) =>
          prev ? { ...prev, opusImportedAt: d.opusImportedAt, opusObraName: d.obraName } : prev,
        );
        return;
      }
      if (ev.type !== 'quote:progress' || ev.data.quoteId !== quoteId) return;
      const d = ev.data;
      setLiveProgress(d);
      setUltimoAvisoAt(Date.now());
      // La regla de qué estado mostrar vive en un solo lugar, con sus pruebas: al
      // terminar de ARMARSE la hoja el estado lo dice el servidor y no el aviso, porque
      // el aviso manda `done` sobre una hoja que quedó en `draft`. Ver el porqué y lo
      // medido en `lib/estadoDeLaHojaAlAvisar.ts`.
      const terminoDeArmarse = esElFinDelArmado(d);
      setQuote((prev) =>
        prev
          ? {
              ...prev,
              status: estadoDeLaHojaTrasElAviso(prev.status, d) as Quote['status'],
              conceptsTotal: d.conceptsTotal ?? prev.conceptsTotal,
              conceptsProcessed: d.conceptsProcessed ?? prev.conceptsProcessed,
              conceptsPriced: d.conceptsPriced ?? prev.conceptsPriced,
              error: d.error ?? prev.error,
            }
          : prev,
      );
      if (d.llmTokens) setLiveLlmTokens(d.llmTokens);
      if (['done', 'failed', 'stopped'].includes(d.stage)) {
        if (d.stage === 'done' && d.message) setPhaseTimings(d.message);
        // Confirmación SIEMPRE visible al terminar: una corrida rápida cuyo
        // resultado se ve igual que el anterior parecía "no hacer nada" (2026-07-09).
        if (d.stage === 'done') {
          const tot = d.conceptsTotal ?? 0;
          const priced = d.conceptsPriced ?? 0;
          dispatchToast(
            <Toast><ToastTitle>
              Presupuesto listo: {priced} de {tot} conceptos con equivalente y precio{tot - priced > 0 ? `, ${tot - priced} sin equivalente` : ''}.
            </ToastTitle></Toast>,
            { intent: 'success', timeout: 6000 },
          );
        }
        void loadConcepts(quoteId!);
        void loadQuote();
        refreshUsageSoon();
      } else if (terminoDeArmarse) {
        // Fin de "Generar listado de conceptos" (extractOnly): recargar quote
        // (conceptsTotal habilita los botones de cotizar) y el listado.
        void loadConcepts(quoteId!);
        // El estado de la hoja lo trae el servidor (ver la nota de `terminoDeArmarse`,
        // arriba). Si esa consulta no llega, la pantalla se quedaría diciendo "armando…"
        // con los controles trabados, así que ahí —y solo ahí— se cae al estado del
        // aviso: peor que un parpadeo es una pantalla colgada.
        void (async () => {
          const llego = await loadQuote();
          if (!llego && d.status) {
            setQuote((prev) => (prev ? { ...prev, status: d.status as Quote['status'] } : prev));
          }
        })();
        refreshUsageSoon();
      } else if (d.stage === 'pricing') {
        // Igual que en los análisis: se recarga solo si algo que SE VE se movió. El latido
        // (el aviso cada ~5 s que solo dice "sigo trabajando") no trae ningún renglón
        // nuevo, así que no vale una consulta del listado completo.
        if (d.latido) return;
        const avance = `${d.conceptsProcessed ?? -1}/${d.conceptsPriced ?? -1}`;
        if (avance === ultimoAvancePricing.current) return;
        ultimoAvancePricing.current = avance;
        scheduleConceptsRefresh(quoteId!);
      }
    },
    [projectId, quoteId, loadConcepts, loadQuote, scheduleConceptsRefresh],
  );

  useProjectStream({ projectId, onEvent: handleEvent });

  const [controlPending, setControlPending] = useState<'pause' | 'stop' | 'run' | null>(null);

  // ===== Controles quote =====
  async function onControl(action: 'run' | 'pause' | 'stop') {
    if (!quote || controlPending) return;
    setControlPending(action);
    try {
      const r = await setQuoteControl(quote.id, action);
      setQuote(r.quote);
      // "Pausando…" / "Deteniendo…" se mantiene hasta que el WORKER confirme el
      // estado nuevo, no cuando responde el endpoint. El endpoint solo escribe la
      // bandera de control; el trabajo se detiene en el próximo límite seguro (entre
      // conceptos), y eso puede tardar. Decir "Detenido" antes de que pare sería
      // mentirle al usuario: se queda mirando el progreso avanzar con el cartel
      // puesto. Se libera en el efecto que observa `quote.status`.
    } catch (e) {
      setErr((e as Error).message);
      setControlPending(null);
    }
  }
  /**
   * Confirmación antes de detener CUALQUIER proceso largo (regla de Sebastián, 2026-08-10).
   *
   * No es solo cortesía: detener es TERMINAL y cobra lo entregado, así que merece un "¿estás
   * seguro?". Y tiene un efecto útil de regalo — los segundos que el usuario tarda en
   * confirmar son los que el worker aprovecha para terminar y guardar lo que está
   * procesando, así se detiene en un límite más limpio.
   */
  const [confirmarAlto, setConfirmarAlto] = useState<null | 'equivalentes' | 'sinteticos'>(null);

  // Detener la generación de sintéticos. El "Deteniendo…" se mantiene mientras el job siga
  // corriendo: se libera solo cuando el progreso deja de reportar `running`.
  const [deteniendoApu, setDeteniendoApu] = useState(false);
  async function onStopApuIa() {
    if (!quoteId || deteniendoApu) return;
    setDeteniendoApu(true);
    try {
      await stopApuIaGeneration(quoteId);
    } catch (e) {
      setErr((e as Error).message);
      setDeteniendoApu(false);
    }
  }
  useEffect(() => {
    if (deteniendoApu && apuJob?.status !== 'running') setDeteniendoApu(false);
  }, [apuJob?.status, deteniendoApu]);

  // Libera el "Pausando…/Deteniendo…/Reanudando…" cuando el estado real ya cambió.
  useEffect(() => {
    if (!controlPending || !quote) return;
    const llego = controlPending === 'pause' ? quote.status === 'paused'
      : controlPending === 'stop' ? quote.status === 'canceled'
        : quote.status === 'running';
    if (llego) setControlPending(null);
  }, [quote?.status, controlPending, quote]);


  // ===== Diálogo de overflow para recotizar (preview ANTES de re-encolar) =====
  // Se guarda el preview + las opciones pendientes de re-regenerar para que,
  // al elegir "cotizar primeros N" desde el diálogo, se preserven skipLlm y
  // se agregue maxConcepts.
  const [overflow, setOverflow] = useState<
    { preview: QuotePreview; pendingOpts: { skipLlm?: boolean } } | null
  >(null);

  // ===== Documento no legible (escaneo) → requiere IA de visión =====
  const [visionPreview, setVisionPreview] = useState<QuotePreview | null>(null);
  // Opciones del pedido que quedó esperando el aviso de escaneo, para poder
  // continuarlo tal como venía cuando el usuario acepta.
  const [visionOptions, setVisionOptions] = useState<Parameters<typeof runRegenerate>[0] | null>(null);

  // ===== Confirmación antes de cotizar =====
  // Bolsa única de tokens mensual: ya no hay límite por counter
  // para conceptos — el gate real es la bolsa de tokens (enforced server-side).
  // Este diálogo es informativo: cuántos conceptos se van a cotizar.
  const usageData = useUsageStore((s) => s.data);
  const quotePricing = {
    modos: {
      catalogos: { label: 'Buscar equivalentes' },
      ia_completa: { label: 'Buscar equivalentes' },
      apus_sinteticos: { label: 'Análisis de precios unitarios con IA' },
    } as Record<string, { label: string }>,
  };
  const cr = enCreditos(usageData);
  const palabraBolsa = cr ? 'créditos' : 'tokens';
  const conceptsLimit = usageData?.limits.concepts_quoted ?? null;
  const conceptsEffectiveRemaining = conceptsLimit == null
    ? null
    : Math.max(0, conceptsLimit - (usageData?.counters.concepts_quoted ?? 0));
  const [costConfirm, setCostConfirm] = useState<
    { mode: 'catalogos' | 'ia_completa'; concepts: number; opts: { skipLlm?: boolean; reuseConcepts?: boolean } } | null
  >(null);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  // Al RE-buscar equivalentes: qué conservar de la búsqueda anterior (los
  // sintéticos siempre se conservan aparte). Se elige en el diálogo de confirmación.
  const [preserveScope, setPreserveScope] = useState<'alta' | 'alta_media' | 'none'>('alta');

  // Lanza el regenerate real con options ya construidas (sin más checks).
  // `confirmarParcial` llega en true en el segundo intento, cuando el usuario ya vio
  // que su bolsa no alcanza para toda la hoja y aceptó que se arme hasta donde alcance.
  async function runRegenerate(options: Record<string, unknown>, confirmarParcial = false) {
    if (!quoteId) return;
    setCatalogsSaving(true);
    setErr(null);
    try {
      const r = await regenerateQuote(quoteId, selectedCatalogIds, options, confirmarParcial);
      setQuote(r.quote);
      // Registra el job en el indicador global desde el arranque (aún sin
      // eventos WS) para que el pill del topbar aparezca de inmediato.
      if (projectId) {
        useQuoteJobsStore.getState().upsert({
          kind: 'quote', quoteId, projectId,
          label: r.quote.name, stage: 'queued', status: 'running', pct: 0,
        });
      }
      void loadConcepts(quoteId);
    } catch (e) {
      // La bolsa no alcanza para toda la hoja. El servidor NO encoló nada: dijo cuántos
      // renglones entran y espera respuesta. Hasta hoy esto no se preguntaba, y un
      // cliente gastó dos veces y media su plan mensual en una sola hoja.
      const noAlcanza = leerBolsaNoAlcanza(e);
      if (noAlcanza) {
        setCatalogsSaving(false);
        if (await confirm({
          title: 'Tu bolsa no alcanza para todo',
          message: noAlcanza.mensaje,
          confirmLabel: etiquetaDeContinuar(noAlcanza, 'renglones'),
        })) {
          await runRegenerate(options, true);
        }
        return;
      }
      setErr((e as Error).message);
    } finally {
      setCatalogsSaving(false);
    }
  }

  // ===== Generar APUs con IA =====
  // Sin conceptIds → default histórico (sin_candidato + sintetica). Con
  // conceptIds (desde el panel "Cotizar con IA") → genera EXACTAMENTE esos.
  async function onGenerateApuIa(conceptIds?: string[]) {
    if (!quoteId || !quote) return;
    // Verificar que el proyecto tenga contexto de proyecto
    let obraCtx: Awaited<ReturnType<typeof getObraContext>> | null = null;
    try {
      obraCtx = await getObraContext(quote.projectId);
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 404) {
        const ok = await confirm({
          title: 'Falta el contexto de proyecto',
          message:
            'Este proyecto no tiene contexto de proyecto capturado. El análisis de precios unitarios lo necesita para aplicar los factores correctamente.\n\n' +
            '¿Quieres ir a configurarlo ahora?',
          confirmLabel: 'Configurar',
        });
        if (ok) navigate(`/project/${quote.projectId}/obra-context`, { state: { returnTo: `/project/${projectId}/quotes/${quoteId}` } });
        return;
      }
      throw e;
    }
    // Aviso si el contexto quedó incompleto (no se guardó o faltan campos): se
    // usarán defaults. Se antepone al diálogo de confirmación para que el cotizador
    // decida con la info a la vista.
    const oc = obraCtx?.context ?? {};
    const faltantesCtx: string[] = [];
    if (!oc.tipo_obra) faltantesCtx.push('Tipo de obra → Pública');
    if (!oc.pais) faltantesCtx.push('País → México');
    if (!oc.moneda) faltantesCtx.push('Moneda → MXN');
    const avisoContexto = faltantesCtx.length > 0
      ? '⚠️ El contexto de proyecto no se guardó completo. Se usarán valores por defecto:\n' +
        faltantesCtx.map((f) => `   • ${f}`).join('\n') +
        '\n\nPuedes ajustarlo en "Contexto de proyecto".\n\n'
      : '';
    // Aviso de planos: si quedan láminas sin leer, se leen ANTES de generar y eso
    // consume tokens. Callarlo sería cobrarle sin avisarle.
    const avisoPlanos = planosPorLeer > 0
      ? `Este proyecto tiene ${planosPorLeer} hoja(s) de planos sin leer. ` +
        'Se van a leer antes de generar los análisis, para tomar de ahí medidas y ' +
        'cantidades que el listado de conceptos no dice. Eso consume ' + palabraBolsa + ' de tu ' +
        'bolsa una sola vez: después queda guardado y no se vuelven a leer.\n\n' +
        'Si prefieres que no los lea, apaga "Leer los planos" en la pestaña ' +
        'Junta de aclaraciones del proyecto.\n\n'
      : '';
    // EL AVISO QUE FALTABA: de los conceptos que se van a generar, cuántos YA tienen precio
    // salido de los catálogos del cliente. Ese precio se reemplaza por el que arme la
    // inteligencia artificial, así que se dice antes de gastar. La cuenta se hace sobre los
    // conceptos que realmente van a correr: los marcados, o —si no hay selección— los
    // mismos que toma el servidor por default (marcados que aún no tienen análisis).
    const aCorrer = conceptIds
      ? concepts.filter((c) => conceptIds.includes(c.id))
      : concepts.filter((c) => c.kind !== 'group' && !c.hasApuIaMatrix && iaDefault(c));
    const enRiesgo = preciosDeCatalogoEnRiesgo(aCorrer);
    const avisoPrecios = enRiesgo.total > 0
      ? `⚠️ ${enRiesgo.total === 1 ? '1 de estos conceptos ya tiene' : `${enRiesgo.total} de estos conceptos ya tienen`} precio de tu catálogo`
        + (enRiesgo.firmes > 0
          ? ` (${enRiesgo.firmes === 1 ? '1 con coincidencia alta' : `${enRiesgo.firmes} con coincidencia alta`}).`
          : '.')
        + '\nEl precio que arme la inteligencia artificial va a reemplazarlo en el total.'
        + '\nDespués puedes volver al precio de tu catálogo en un clic, desde el análisis del concepto.\n\n'
      : '';
    // Total a procesar: si vienen conceptIds (panel), son esos; si no, el
    // default histórico (sin_candidato nuevos + sintetica a regenerar).
    const totalACorrer = conceptIds ? conceptIds.length : sinCandidatoCount + sinteticaCount;
    if (totalACorrer === 0) {
      dispatchToast(
        <Toast><ToastTitle>No hay conceptos para procesar</ToastTitle></Toast>,
        { intent: 'warning', timeout: 4000 },
      );
      return;
    }
    // Confirmación para el flujo por selección (evita dispararlo por error al picar).
    if (conceptIds) {
      const ok = await confirm({
        title: 'Generar análisis con inteligencia artificial',
        message:
          avisoContexto + avisoPlanos + avisoPrecios +
          `Se generarán ${conceptIds.length} análisis de precio unitario con inteligencia artificial.\n\n` +
          'Los conceptos que ya tengan uno se volverán a crear.\n\n¿Continuar?',
        confirmLabel: `Generar ${conceptIds.length}`,
      });
      if (!ok) return;
    }
    // El diálogo grande solo aplica al flujo directo (sin selección).
    if (!conceptIds) {
    // Bolsa única de tokens mensual: los APUs ya no tienen límite por counter
    // (limits.apus_generated viene null). Si hubiera un límite, se informa.
    const apusLimit = usageData?.limits.apus_generated ?? null;
    const apusRemaining = apusLimit == null
      ? null
      : Math.max(0, apusLimit - (usageData?.counters.apus_generated ?? 0));
    const costoLine = apusRemaining != null
      ? `Cuota de APUs restante este mes: ${apusRemaining.toLocaleString('es-MX')}.`
      : '';
    let msg: string;
    if (sinCandidatoCount > 0 && sinteticaCount > 0) {
      msg =
        `Se van a procesar:\n` +
        `  • ${sinCandidatoCount} concepto(s) nuevo(s) sin equivalente\n` +
        `  • ${sinteticaCount} análisis de precio unitario hechos con IA — SE VOLVERÁN A CREAR con el contexto actual\n\n` +
        `⚠ Los análisis de precios unitarios aprobados perderán esa aprobación y volverán a "pendiente".\n\n` +
        `${costoLine}\n\n¿Continuar?`;
    } else if (sinteticaCount > 0) {
      msg =
        `Se van a volver a crear ${sinteticaCount} análisis de precio unitario con el contexto y catálogos actuales.\n\n` +
        `⚠ Los análisis de precios unitarios aprobados perderán esa aprobación y volverán a "pendiente".\n\n` +
        `${costoLine}\n\n¿Continuar?`;
    } else {
      msg =
        `Se van a procesar ${sinCandidatoCount} concepto(s) sin equivalente con inteligencia artificial.\n\n` +
        `${costoLine}\n\n¿Continuar?`;
    }
    const ok = await confirm({
      title: 'Generar análisis de precios unitarios',
      message: avisoContexto + avisoPlanos + avisoPrecios + msg,
      confirmLabel: 'Continuar',
    });
    if (!ok) return;
    }
    try {
      const res = await generateApuIa(quoteId, conceptIds ? { conceptIds } : {});
      dispatchToast(
        <Toast><ToastTitle>Análisis de precios unitarios iniciado · {res.conceptos_total} conceptos en cola</ToastTitle></Toast>,
        { intent: 'info', timeout: 4000 },
      );
      // Se queda en el presupuesto; el avance se muestra arriba del listado (apuJob).
    } catch (e) {
      // Cuota de APUs excedida → ofrecer "generar los primeros N" (parcial),
      // igual que la cotización. El listener global ya mostró el toast de cuota.
      // La bolsa no alcanza para todo el lote. El servidor NO encoló nada: dijo cuántos
      // entran y espera respuesta. Es el aviso que no existía cuando un cliente gastó
      // dos veces y media su plan en una sola hoja sin que nadie se lo dijera.
      const noAlcanza = leerBolsaNoAlcanza(e);
      if (noAlcanza) {
        if (await confirm({
          title: 'Tu bolsa no alcanza para todo',
          message: noAlcanza.mensaje,
          confirmLabel: etiquetaDeContinuar(noAlcanza, 'análisis'),
        })) {
          try {
            const res3 = await generateApuIa(quoteId, {
              ...(conceptIds ? { conceptIds } : {}),
              confirmarParcial: true,
            });
            dispatchToast(
              <Toast><ToastTitle>Análisis de precios unitarios iniciado · {res3.conceptos_total} conceptos en cola</ToastTitle></Toast>,
              { intent: 'info', timeout: 4000 },
            );
          } catch { /* el listener global informa el error */ }
        }
        return;
      }
      if (e instanceof ApiClientError && e.code === 'QUOTA_EXCEEDED') {
        const d = (e.details ?? {}) as { current?: number; limit?: number | null; effectiveLimit?: number | null };
        const cap = d.effectiveLimit ?? d.limit ?? 0;
        const remaining = Math.max(0, (cap ?? 0) - (d.current ?? 0));
        if (remaining > 0 && (await confirm({
          title: 'Cuota de análisis insuficiente',
          message:
            `Tu cuota permite generar ${remaining} análisis de precio unitario más este mes, pero hay ${totalACorrer} pendientes.\n\n` +
            `¿Generar los primeros ${remaining} ahora? (para todos, cambia de plan).`,
          confirmLabel: `Generar ${remaining}`,
        }))) {
          try {
            const res2 = await generateApuIa(quoteId, conceptIds ? { conceptIds, maxConcepts: remaining } : { maxConcepts: remaining });
            dispatchToast(
              <Toast><ToastTitle>Análisis de precios unitarios iniciado · {res2.conceptos_total} conceptos en cola</ToastTitle></Toast>,
              { intent: 'info', timeout: 4000 },
            );
          } catch { /* el listener global informa el error */ }
        }
        return;
      }
      const msg = e instanceof ApiClientError ? e.message : 'Error al iniciar el análisis de precios unitarios';
      dispatchToast(
        <Toast><ToastTitle>{msg}</ToastTitle></Toast>,
        { intent: 'error', timeout: 4000 },
      );
    }
  }

  // ===== Regenerar cotización con catálogos actuales =====
  // skipLlm=true → solo Fase 1 (léxico/semántico local), nada sale al LLM en la nube.
  // Zona gris queda como sin_candidato con nota "Pendiente revisión IA".
  // onlyLlmZonaGris=true → solo re-corre Fase 2 sobre la zona gris existente,
  // no re-extrae conceptos del archivo (no toca cuota), así que se salta preview.
  async function onRegenerate(opts: { skipLlm?: boolean; onlyLlmZonaGris?: boolean; extractOnly?: boolean; reuseConcepts?: boolean } = {}) {
    if (!quoteId || !quote) return;
    // Guard: no generar sin archivo del listado de conceptos ni sin catálogo.
    // (onlyLlmZonaGris solo re-valida lo existente, no re-extrae → no requiere archivo.
    //  extractOnly solo lee el documento → no requiere catálogo.)
    if (opts.extractOnly) {
      if (missingForExtract) {
        setErr(missingForExtract);
        return;
      }
    } else if (!opts.onlyLlmZonaGris && missingForGenerate) {
      setErr(missingForGenerate);
      return;
    }
    // ── REARMAR BORRA TODO EL TRABAJO DEL USUARIO EN ESTA HOJA ──
    // El servidor hace `DELETE FROM quote_concepts` y reconstruye desde la extraccion,
    // asi que se van los analisis con IA, los renglones corregidos a mano, la seleccion
    // de conceptos y los interruptores de IA. Hasta hoy no decia una palabra.
    //
    // No se puede "conservar parte": al releer, los renglones se reconstruyen y las
    // claves pueden no coincidir —el archivo fuente de un presupuesto se puede
    // reemplazar—. Un analisis pegado al concepto equivocado es PEOR que no tenerlo:
    // nadie lo ve hasta que ya se cotizo. Por eso es binario y lo unico honesto es
    // avisar bien antes.
    //
    // Si no hay nada que perder no pregunta nada: un aviso que aparece siempre se
    // aprende a ignorar, y entonces el dia que importa nadie lo lee.
    if (!opts.reuseConcepts && !opts.onlyLlmZonaGris) {
      const enRiesgo = contarTrabajoEnRiesgo(concepts);
      if (hayTrabajoEnRiesgo(enRiesgo)) {
        const cuantosApu = totalSinteticos(enRiesgo);
        const ok = await confirm({
          title: 'Rearmar vuelve a leer el documento desde cero',
          message: (
            <span>
              <span style={{ display: 'block', marginBottom: 10 }}>
                {cuantosApu > 0
                  ? 'Los renglones se vuelven a construir desde el documento, así que no se pueden emparejar con los que hay ahora — el documento pudo haber cambiado. Esto se pierde:'
                  : 'Los renglones se vuelven a construir desde el documento. Esto se pierde:'}
              </span>
              <span style={{ display: 'block' }}>
                {lineasDelAviso(enRiesgo).map((linea) => (
                  <span key={linea} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                    <span aria-hidden>•</span>
                    <span>{linea}</span>
                  </span>
                ))}
              </span>
            </span>
          ),
          confirmLabel: 'Descartar y rearmar',
          cancelLabel: 'Cancelar',
          destructive: true,
        });
        if (!ok) return;
      }
    }

    const options: Record<string, unknown> = {};
    if (opts.skipLlm) options.skipLlm = true;
    if (opts.onlyLlmZonaGris) options.onlyLlmZonaGris = true;
    if (opts.extractOnly) options.extractOnly = true;
    if (opts.reuseConcepts) options.reuseConcepts = true;

    // Si solo es re-validación de zona gris, no se re-extrae → no aplica cuota.
    if (opts.onlyLlmZonaGris || !quote.sourceFileId || !quote.projectId) {
      await runRegenerate(options);
      return;
    }

    // Cotizar REUSANDO el listado ya leído (flujo dos pasos): no re-extrae →
    // no re-preview del archivo; el costo se calcula sobre los conceptos
    // SELECCIONADOS (lo no seleccionado no se cotiza ni cobra).
    if (opts.reuseConcepts) {
      const conceptosReales = concepts.filter((c) => c.kind !== 'group' && c.cotizar !== false).length;
      setCostConfirm({
        mode: opts.skipLlm ? 'catalogos' : 'ia_completa',
        concepts: conceptosReales,
        opts: { skipLlm: opts.skipLlm, reuseConcepts: true },
      });
      return;
    }

    // Re-extracción completa → verificar cuota antes de re-encolar.
    setCatalogsSaving(true);
    setErr(null);
    try {
      const preview = await previewQuote(quote.projectId, quote.sourceFileId);
      // Documento no legible (escaneo mal leído) → no se puede rearmar de forma
      // automática; se avisa y se ofrece el análisis avanzado con IA (visión).
      // Escaneo: no hay texto que leer. Se avisa y, si acepta, se rearma igual —
      // el worker lo lee mirando las páginas.
      // La cuota se respeta IGUAL que en el flujo por texto: sin cuota restante
      // no hay visión (mismo diálogo de overflow); si queda algo, la corrida se
      // limita a los primeros N vía maxConcepts.
      if (preview.needsVision) {
        if (!preview.fits && !preview.remaining) {
          setOverflow({ preview, pendingOpts: { skipLlm: opts.skipLlm } });
          return;
        }
        const visOpts: Record<string, unknown> = { ...options };
        if (!preview.fits && preview.remaining) visOpts.maxConcepts = preview.remaining;
        setVisionOptions(visOpts);
        setVisionPreview(preview);
        return;
      }
      if (preview.fits) {
        if (opts.extractOnly) {
          // Leer el listado no cobra tokens (el precio cerrado se cobra al
          // completar la cotización) → sin diálogo de costo; la cuota de
          // conceptos sí se verificó con el preview.
          await runRegenerate(options);
          return;
        }
        // Confirmación de COSTO (precio cerrado): el usuario ve los tokens
        // exactos antes de aceptar. runRegenerate se dispara desde el diálogo.
        setCostConfirm({
          mode: opts.skipLlm ? 'catalogos' : 'ia_completa',
          concepts: preview.totalConcepts,
          opts: { skipLlm: opts.skipLlm },
        });
        return;
      }
      // Excede cuota: mostrar diálogo con opciones (parcial o upgrade).
      setOverflow({ preview, pendingOpts: { skipLlm: opts.skipLlm } });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCatalogsSaving(false);
    }
  }

  // ===== Filtrado de conceptos =====
  const filtered = useMemo(() => {
    // Primero filtrar solo conceptos (no agrupadores)
    let leaves = concepts.filter((c) => c.kind !== 'group');
    if (filter === 'por_revisar') leaves = leaves.filter(porRevisar);
    if (confFilter !== 'all') leaves = leaves.filter((c) => confKind(c) === confFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      leaves = leaves.filter(
        (c) =>
          c.descripcion.toLowerCase().includes(q) ||
          (c.clave ?? '').toLowerCase().includes(q),
      );
    }
    if (filter === 'all' && confFilter === 'all' && !search.trim()) {
      // Sin filtro: mostrar todo (agrupadores + conceptos) en orden original
      return concepts;
    }
    // Con filtro/búsqueda: mostrar solo los grupos que tienen hijos visibles + esos hijos.
    // Soporta parentIdx (nuevo) y parentClave (compat. quotes antiguas).
    const visibleParentIdxSet = new Set(leaves.map((c) => c.parentIdx).filter((x): x is number => x != null));
    const visibleParentClaveSet = new Set(leaves.map((c) => c.parentClave).filter(Boolean));
    const groups = concepts.filter((c) =>
      c.kind === 'group' && (visibleParentIdxSet.has(c.idx) || visibleParentClaveSet.has(c.clave ?? ''))
    );
    return [...groups, ...leaves].sort((a, b) => a.idx - b.idx);
  }, [concepts, filter, confFilter, search]);

  // Profundidad de cada fila por la cadena parentIdx — para la sangría de
  // agrupadores anidados (catálogos tipo CONAGUA traen hasta 5 niveles).
  const depthByIdx = useMemo(() => {
    const byIdx = new Map<number, QuoteConcept>();
    for (const c of concepts) byIdx.set(c.idx, c);
    const memo = new Map<number, number>();
    const depthOf = (c: QuoteConcept, hops = 0): number => {
      if (c.parentIdx == null || hops > 12) return 0;
      const hit = memo.get(c.idx);
      if (hit != null) return hit;
      const parent = byIdx.get(c.parentIdx);
      const d = parent ? depthOf(parent, hops + 1) + 1 : 0;
      memo.set(c.idx, d);
      return d;
    };
    const out = new Map<number, number>();
    for (const c of concepts) out.set(c.idx, depthOf(c));
    return out;
  }, [concepts]);

  // (Se quitó `conceptByIdx`: el overlay de agrupadores pegados ya no sube por
  //  la cadena `parentIdx` de cada concepto — usa el orden del documento. Ver
  //  la nota de arriba en `StickyGroupHeaders.tsx`.)

  // ===== Selección de conceptos a cotizar (2026-07-06) =====
  // Solo mientras el listado está en borrador (generado, aún sin cotizar): el
  // cotizador marca por concepto y por partida qué se cotiza. Lo no marcado se
  // exporta a m1 "sin análisis".
  // La selección está disponible siempre que se pueda (re)cotizar y haya
  // listado: draft, failed o completed. Solo se oculta mientras corre una
  // cotización (running/queued/paused). Antes exigía 'draft' y desaparecía
  // tras cotizar/fallar (2026-07-07).
  // Selección por concepto (checkboxes) OCULTA por ahora: no se usa. Todos los
  // conceptos se tratan como seleccionados (cotizar). Para reactivarla, volver a
  // la condición de abajo. Ver pendiente de dirección.
  const selectionMode = false;
  // const selectionMode =
  //   !!canMutate && (quote?.conceptsTotal ?? 0) > 0 && !ACTIVE_STATUSES.has(quote?.status ?? '');
  const selectableCount = useMemo(() => concepts.filter((c) => c.kind !== 'group').length, [concepts]);

  // ── "Cotizar con IA" inline (marca para generar en lote) ────────────────────
  // El toggle SOLO aplica a conceptos NO generados (selección de cola). Un
  // concepto ya generado sale de la cola y se muestra como "✓ con IA".
  // Default: los sin-equivalente vienen PREMARCADOS, pero SOLO después de buscar
  // equivalentes (processedAt != null). Un listado recién extraído / rearmado
  // guarda todo como 'sin_candidato' con processedAt NULL → ahí los toggles van
  // APAGADOS; se encienden cuando el matcher corre y confirma que no hay
  // equivalente. Una elección explícita (apu_ia_requested no nulo) siempre gana.
  // Al generar, el worker deja apu_ia_requested=false → el concepto queda OFF.
  const iaDefault = (c: QuoteConcept) =>
    (c.apuIaRequested != null ? c.apuIaRequested : (c.confianza === 'sin_candidato' && c.processedAt != null));
  const iaOn = (c: QuoteConcept): boolean => iaMarks[c.id] ?? iaDefault(c);
  // Conceptos marcados con el toggle IA (incluye los YA generados que el
  // cotizador vuelve a encender → se regeneran). Los generados quedan OFF por
  // default (apu_ia_requested=false tras generar), así solo entran si se marcan.
  const iaMarkedPendingIds = useMemo(
    () => concepts.filter((c) => c.kind !== 'group' && iaOn(c)).map((c) => c.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [concepts, iaMarks],
  );
  async function flipIa(c: QuoteConcept) {
    const next = !iaOn(c);
    // Un toggle manual rompe el grupo excluyente del header (ya no coincide 1:1).
    setIaScope(null);
    // Marca de intención: optimista local + PERSISTE (sobrevive a la navegación
    // y el lote la toma desde la vista de sintéticos).
    setIaMarks((m) => ({ ...m, [c.id]: next }));
    try {
      await setConceptRequested(c.id, next);
    } catch {
      setIaMarks((m) => ({ ...m, [c.id]: !next }));
    }
  }
  // TODOS QUIERE DECIR TODOS. Los encendidos en bloque no dejan a nadie afuera, ni siquiera
  // a los conceptos que ya tienen precio de los catálogos del cliente con coincidencia alta:
  // pedir el análisis de uno de esos es legítimo y a veces es justo lo que se busca (hay
  // catálogos con la mano de obra muy por debajo de la realidad, y ahí el precio armado con
  // inteligencia artificial es el más cercano). Lo que sí se hace es AVISAR: el contador de
  // abajo dice cuántos de los que se van a generar ya tienen precio de catálogo, y el aviso
  // previo a gastar lo repite. Informar sí; decidir por el cotizador, no.
  const conEquivalenteFirme = useMemo(
    () => concepts.filter((c) => c.kind !== 'group' && equivalenteFirme(c)),
    [concepts],
  );
  // ── LO QUE SE APAGA, SE QUEDA APAGADO ──────────────────────────────────────────
  //
  // Estos controles de bloque SÍ guardan la elección en el servidor, igual que el toggle
  // de un renglón. Antes solo cambiaban el estado del navegador: el cotizador apagaba
  // todos, se iba al proyecto, volvía al presupuesto y **estaban todos encendidos otra
  // vez**, porque sin elección guardada el default vuelve a encender los renglones sin
  // equivalente (reportado el 2026-08-20).
  //
  // Si el guardado falla, la pantalla vuelve a como estaba: mostrar los toggles apagados
  // sobre una elección que no se guardó es la misma trampa de antes.
  async function iaMarcarTodos(on: boolean) {
    const ids = concepts.filter((c) => c.kind !== 'group' && !c.hasApuIaMatrix).map((c) => c.id);
    if (ids.length === 0) return;
    const previo = iaMarks;
    setIaMarks((m) => {
      const n = { ...m };
      for (const id of ids) n[id] = on;
      return n;
    });
    try {
      await setConceptsRequested(quoteId!, on, ids);
    } catch (e) {
      setIaMarks(previo);
      setErr((e as Error).message);
    }
  }
  // Control del header "Generar sintético": enciende UN grupo a la vez
  // (excluyente estilo radio). Encender un grupo marca SOLO sus conceptos para
  // generar con IA y apaga el resto; volver a tocar el mismo grupo lo apaga todo.
  // Se guarda en el servidor, igual que `iaMarcarTodos`, y el lote toma
  // `iaMarkedPendingIds`.
  async function toggleIaScope(kind: ConfKind | 'all') {
    const turningOff = iaScope === kind;
    const previoScope = iaScope;
    const previoMarks = iaMarks;
    setIaScope(turningOff ? null : kind);
    const encendidos: string[] = [];
    setIaMarks(() => {
      const n: Record<string, boolean> = {};
      for (const c of concepts) {
        if (c.kind === 'group') continue;
        // "Todos" son todos (ver `iaMarcarTodos`): ningún concepto queda fuera por tener
        // ya precio de catálogo. Los demás grupos filtran por su confianza, como siempre.
        const on = !turningOff && (kind === 'all' || confKind(c) === kind);
        n[c.id] = on;
        if (on) encendidos.push(c.id);
      }
      return n;
    });
    // Se guarda igual que arriba. `exclusivo` deja el radio en UNA sola consulta: los de
    // este grupo encendidos y todo el resto apagado, sin un instante a medio guardar.
    try {
      if (turningOff || encendidos.length === 0) await setConceptsRequested(quoteId!, false);
      else await setConceptsRequested(quoteId!, true, encendidos, true);
    } catch (e) {
      setIaScope(previoScope);
      setIaMarks(previoMarks);
      setErr((e as Error).message);
    }
  }
  function renderIaToggle(c: QuoteConcept) {
    // Toggle en TODAS las filas. Sin generar → marcar para generar. Ya generado
    // → encenderlo lo vuelve a generar (el lote lo reenvía; el worker regenera
    // por IDs explícitos, sin importar el estado).
    const generated = c.hasApuIaMatrix;
    const on = iaOn(c);
    // Si el concepto ya vale con un precio de su catálogo, el tooltip lo dice ANTES de
    // prenderlo: el precio que arme la inteligencia artificial va a reemplazarlo en el
    // total. No bloquea (marcar no cuesta nada); la puerta firme es el aviso que sale al
    // generar, que es donde se gasta.
    const conPrecioDeCatalogo = precioVieneDelCatalogo(c);
    const tip = generated
      ? (on ? 'Se volverá a generar con inteligencia artificial.' : 'Ya tiene análisis con inteligencia artificial. Enciéndelo para volver a generarlo.')
      : conPrecioDeCatalogo
        ? (on
            ? `Este concepto ya tiene precio de tu catálogo. Al generar, el precio que arme la inteligencia artificial lo reemplaza en el total (podrás volver al de tu catálogo en un clic).`
            : `Este concepto ya tiene precio de tu catálogo. Enciéndelo solo si quieres que la inteligencia artificial arme otro precio en su lugar.`)
        : (on ? 'Marcado para generar con inteligencia artificial.' : 'Marcar para generar con inteligencia artificial.');
    // Pill de estado de la matriz sintética (debajo del toggle): solo si ya se
    // generó. "Por revisar" = generada pero sin aceptar (evita confundir con
    // "Rechazada", que es un descarte explícito del cotizador).
    const reviewPill = generated ? (() => {
      const st = c.apuIaReviewStatus;
      const cfg = st === 'approved'
        ? { label: 'Aceptada', bg: 'var(--ok-weak)', fg: 'var(--ok)' }
        : st === 'rejected'
          ? { label: 'Rechazada', bg: 'var(--bad-weak)', fg: 'var(--bad)' }
          : { label: 'Por revisar', bg: 'var(--warn-weak)', fg: 'var(--warn)' };
      return (
        <span style={{ fontSize: 9, fontWeight: 700, lineHeight: 1.5, padding: '0 6px', borderRadius: 999, background: cfg.bg, color: cfg.fg, whiteSpace: 'nowrap' }}>
          {cfg.label}
        </span>
      );
    })() : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <Tooltip content={tip} relationship="label">
          <button
            role="switch"
            aria-checked={on}
            disabled={isGenerating}
            aria-label={generated ? 'Volver a generar con inteligencia artificial' : 'Marcar para generar con inteligencia artificial'}
            data-demo-sin={c.confianza === 'sin_candidato' && c.processedAt != null ? '1' : undefined}
            onClick={(e) => { e.stopPropagation(); if (!isGenerating) void flipIa(c); }}
            style={{
              position: 'relative', width: 38, height: 22, borderRadius: 999, padding: 0, flexShrink: 0,
              border: '1px solid',
              borderColor: on ? 'var(--privado)' : tokens.colorNeutralStrokeAccessible,
              // Track OFF en gris accesible (visible en tema claro y oscuro).
              background: on ? 'var(--privado)' : tokens.colorNeutralStrokeAccessible,
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              opacity: isGenerating ? 0.5 : 1,
            }}
          >
            <span style={{ position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.3)', transition: 'left .15s' }} />
          </button>
        </Tooltip>
        {reviewPill}
      </div>
    );
  }
  // `cotizar !== false` = seleccionado (ausente/undefined → seleccionado por
  // defecto; así datos viejos o sin el campo no se leen como "no cotizar").
  const selectedCount = useMemo(
    () => concepts.filter((c) => c.kind !== 'group' && c.cotizar !== false).length,
    [concepts],
  );

  // idx de conceptos-hoja que cuelgan de una partida (cadena parentIdx).
  const conceptIdsUnderGroup = useCallback(
    (groupIdx: number): string[] => {
      const ids: string[] = [];
      for (const c of concepts) {
        if (c.kind === 'group') continue;
        let p = c.parentIdx;
        let hops = 0;
        while (p != null && hops++ < 14) {
          if (p === groupIdx) { ids.push(c.id); break; }
          p = concepts.find((x) => x.idx === p)?.parentIdx ?? null;
        }
      }
      return ids;
    },
    [concepts],
  );

  // Marca/desmarca en la BD + estado local optimista.
  const applySelection = useCallback(
    async (ids: string[], cotizar: boolean) => {
      if (!quoteId || ids.length === 0) return;
      const idSet = new Set(ids);
      setConcepts((prev) => prev.map((c) => (idSet.has(c.id) ? { ...c, cotizar } : c)));
      try {
        await setQuoteSelection(quoteId, ids, cotizar);
      } catch (e) {
        // Revertir si falla.
        setConcepts((prev) => prev.map((c) => (idSet.has(c.id) ? { ...c, cotizar: !cotizar } : c)));
        setErr((e as Error).message);
      }
    },
    [quoteId],
  );

  const toggleConcepto = useCallback(
    (c: QuoteConcept) => void applySelection([c.id], c.cotizar === false),
    [applySelection],
  );
  const togglePartida = useCallback(
    (groupIdx: number, cotizar: boolean) => void applySelection(conceptIdsUnderGroup(groupIdx), cotizar),
    [applySelection, conceptIdsUnderGroup],
  );
  const toggleTodos = useCallback(
    (cotizar: boolean) => void applySelection(concepts.filter((c) => c.kind !== 'group').map((c) => c.id), cotizar),
    [applySelection, concepts],
  );

  // ===== Virtualizer =====
  // La página (scrollArea) es el contenedor de scroll; la lista de conceptos
  // empieza debajo de head+stats+chips+search → scrollMargin = ese offset.
  const tbodyRef = useRef<HTMLDivElement | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const theadRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  // Alto del header de columnas (sticky) — el overlay de agrupadores pinnea debajo.
  const [theadH, setTheadH] = useState(0);
  // Medición del thead vía CALLBACK REF (no useEffect): garantiza medir/observar
  // el NODO REAL en cuanto monta. Con el effect anterior, si React recreaba el
  // nodo del thead sin cambiar las deps, el ResizeObserver quedaba sobre un nodo
  // desprendido y `theadH` se quedaba en 0 → el overlay de agrupadores no pinneaba
  // (bug reproducido en CONAGUA: thead real=46px pero theadH=0).
  const theadRoRef = useRef<ResizeObserver | null>(null);
  const setTheadEl = useCallback((el: HTMLDivElement | null) => {
    theadRef.current = el;
    theadRoRef.current?.disconnect();
    theadRoRef.current = null;
    if (!el) return;
    const measure = () => setTheadH(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    theadRoRef.current = ro;
  }, []);
  // Caja horizontal del ÁRBOL (`.table`) respecto al viewport — el panel se acota
  // a este ancho/posición para NO ocupar toda la pantalla (sobre todo en "abajo").
  const [treeBox, setTreeBox] = useState({ left: 0, width: 0 });
  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollAreaRef.current,
    // CADA RENGLÓN SE IDENTIFICA POR SU id, NO POR SU POSICIÓN.
    //
    // Los renglones no miden todos igual (una descripción larga ocupa varias líneas),
    // así que el virtualizador guarda la altura real de cada uno a medida que entra en
    // pantalla. Sin esta línea las guardaba indexadas por POSICIÓN: al cambiar la lista
    // —el buscador, un filtro, o cualquier refresco que reordene— la altura de un
    // renglón se le aplicaba al que quedó en ese lugar. Es correcto y se queda.
    //
    // No confundir con el parpadeo del 2026-08-27: ese NO era esto. Se midió en el
    // navegador y era el scroll que se corría solo — ver la nota de
    // `shouldAdjustScrollPositionOnItemSizeChange`, abajo.
    getItemKey: (i) => filtered[i]?.id ?? i,
    estimateSize: (i) => {
      const c = filtered[i];
      if (!c || c.kind === 'group') return 36;
      // Estimar líneas por longitud (~95 chars/línea a este ancho): las
      // descripciones heredadas de incisos llegan a ~900 chars y con un
      // estimado plano el virtualizador encimaba filas (QA 2026-07-04).
      //
      // El supuesto se queda CORTO y se sabe cuánto: medido el 2026-08-27 en una hoja de
      // 2.838 renglones, la altura de verdad da entre 12 y 107 px más que ésta (grupos
      // 36 → 38,8; renglones de 3-4 líneas 79/96 → 91,4/108,8; el más largo 419 → 526,4).
      // A este ancho entran ~76 caracteres por línea, no 95. NO se afina acá a mano
      // porque el número depende del ancho de la columna (pantalla, panel de análisis
      // abierto) y sería cambiar un número mágico por otro. Hoy no se ve: el hueco de
      // arriba y el navegador dejan el renglón quieto igual, y lo único que queda torcido
      // es el largo de la barra de scroll mientras se mide.
      const lines = Math.max(1, Math.ceil(c.descripcion.length / 95));
      const base = 28 + lines * 17;
      return SHOW_INLINE_ANALYSIS && expandedIdRef.current === c.id ? base + EXPANDED_HEIGHT : base;
    },
    measureElement: (el) => el.getBoundingClientRect().height,
    scrollMargin,
    overscan: 12,
  });
  /**
   * EL ÁRBOL NO SE MUEVE SOLO: MEDIR UN RENGLÓN NO PUEDE CORRER EL SCROLL.
   *
   * (Esto y los dos huecos hermanos de más abajo son UNA SOLA cosa; se explica entera
   * acá y allá queda la referencia.)
   *
   * Los renglones no miden todos igual —una descripción larga ocupa varias líneas—, así
   * que el virtualizador supone una altura y guarda la de verdad en cuanto el renglón
   * entra en pantalla. Cuando la de verdad no coincide con la supuesta, la biblioteca
   * mueve el scroll esa diferencia. Eso está pensado para su modo de dibujo normal, en
   * el que cada renglón va colocado a mano en la coordenada que ella calcula: ahí
   * corregir la altura EMPUJA todo lo que está abajo, y mover el scroll lo mismo es lo
   * que evita el brinco.
   *
   * Aquí los renglones van en FLUJO NORMAL, uno atrás de otro, con dos huecos —arriba y
   * abajo— que ocupan el lugar de los que no están dibujados. Se hizo así el 2026-07-06
   * porque colocarlos a mano encimaba filas en pantallas angostas. Y en flujo normal el
   * navegador ya los dibujó con su altura de verdad desde el primer cuadro: medirlos no
   * empuja NADA. La corrección se queda sin contraparte y lo único que hace es correr
   * el scroll — la hoja se desliza sola.
   *
   * MEDIDO en el navegador el 2026-08-27 (hoja de 2.838 renglones, ABEL MATA): al llevar
   * la vista a una zona todavía sin medir, en UN cuadro el scroll pasó de 80.000 a
   * 80.170 px sin que nadie tocara nada, y el renglón que estaba en el centro de la
   * pantalla pasó a ser el de dos más abajo. Una sola bajada acumuló 720 px de
   * corrimiento propio. Es el parpadeo que reportó Sebastián ("sube uno o dos renglones
   * y se baja"), y explica el "a veces": solo aparece mientras haya renglones que se
   * están midiendo por primera vez, y al terminar de armar la hoja lo están TODOS.
   *
   * Con esto apagado, quien mantiene la hoja quieta es el navegador: cuando algo cambia
   * de alto por encima de lo que estás mirando, Chrome corre el scroll solo para dejarte
   * el mismo renglón en el mismo lugar. Medido en el mismo caso: el renglón del centro
   * se quedó clavado en 339,3 px mientras el scroll se acomodaba de 80.000 a 80.013,6.
   *
   * OJO: esto NO es una opción del `useVirtualizer` — es una propiedad de la instancia
   * (la biblioteca la lee como `this.shouldAdjustScrollPositionOnItemSizeChange`, no
   * como `this.options...`). Pasarla arriba, junto a `overscan`, compila y no hace nada.
   * Y se asigna acá, en el dibujado, no en un efecto: la primera medición ocurre cuando
   * React monta las filas, o sea ANTES de que corra cualquier efecto.
   */
  rowVirtualizer.shouldAdjustScrollPositionOnItemSizeChange = NUNCA_CORRER_EL_SCROLL_AL_MEDIR;

  /**
   * PICAR UN RENGLÓN NO PUEDE BORRAR LO QUE YA SE MIDIÓ DE TODA LA HOJA.
   *
   * `rowVirtualizer.measure()` no "vuelve a medir": TIRA A LA BASURA la altura real de
   * los 2.838 renglones y deja a todos otra vez en el valor supuesto. Los que están en
   * pantalla se vuelven a medir enseguida; los de ARRIBA no —no están dibujados—, así
   * que el hueco que los representa se encoge de golpe y la hoja entera se desliza
   * hacia arriba bajo los ojos del cotizador. Después, a medida que se vuelve a medir,
   * baja de nuevo: es el "sube uno o dos renglones y se baja" que reportó Sebastián el
   * 2026-08-27, y el "a veces" es que a ratos el navegador alcanza a sostenerla y a
   * ratos no.
   *
   * MEDIDO el 2026-08-27 (hoja de 2.838 renglones, ABEL MATA, con el panel de análisis
   * YA abierto —sin encuadre de por medio— y la vista a 111.273 px): al picar otro
   * renglón, sin que el scroll se moviera ni un píxel, el renglón de referencia saltó
   * 717,8 px hacia arriba en UN cuadro y el alto total cayó de 239.771 a 239.054 px. En
   * la corrida anterior, misma maniobra, el alto cayó 2.472 px. Cerrando el panel arriba
   * de todo el alto se desplomaba de 271.985 a 237.461 px —el valor de una hoja sin
   * medir NADA— y la hoja subía 144,8 px y volvía a bajar 110.
   *
   * Y todo eso NO SERVÍA PARA NADA: lo único que hacía que un renglón cambiara de alto
   * al enfocarlo era el desglose INLINE, que está apagado (`SHOW_INLINE_ANALYSIS`) desde
   * que el análisis se mudó al panel lateral. Con él apagado, `estimateSize` ni mira el
   * renglón enfocado, así que no hay una sola medida que corregir. Cualquier cambio de
   * alto de verdad lo agarran igual `measureElement` y el observador de tamaño, renglón
   * por renglón y sin tocar a los demás.
   *
   * Si algún día vuelve el desglose inline, esto tiene que volver a correr — por eso se
   * deja atado al mismo interruptor y no borrado.
   */
  useEffect(() => {
    if (!SHOW_INLINE_ANALYSIS) return;
    rowVirtualizer.measure();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId]);

  // Mide el offset de la lista dentro del scrollArea (head/stats/chips/search la
  // empujan hacia abajo) y lo alimenta como scrollMargin del virtualizador.
  useEffect(() => {
    const measure = () => {
      const list = listRef.current;
      const area = scrollAreaRef.current;
      if (!list || !area) return;
      const off =
        list.getBoundingClientRect().top - area.getBoundingClientRect().top + area.scrollTop;
      setScrollMargin((prev) => (Math.abs(prev - off) > 1 ? off : prev));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, quote?.status, conceptsLoading]);

  // Mide la caja horizontal del árbol (`.table`) para acotar ahí el panel
  // (sobre todo "abajo": que NO ocupe todo el ancho de la pantalla).
  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setTreeBox((prev) => (Math.abs(prev.left - r.left) > 1 || Math.abs(prev.width - r.width) > 1
        ? { left: Math.round(r.left), width: Math.round(r.width) } : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptsLoading, quote?.status, filtered.length, leftCollapsed]);

  // ===== Totales =====
  const totals = useMemo(() => {
    const leaves = concepts.filter((c) => c.kind !== 'group');
    const sum = leaves.reduce((acc, c) => acc + (Number(c.importe) || 0), 0);
    // "Con precio" = tiene un importe real. Además de los matcheados (status
    // priced/manual), esto incluye los sintéticos, que traen su importe aunque su
    // status siga en 'pending' (por revisar) — antes salían 0/N aunque el Monto
    // del proyecto sí los sumaba (incoherencia reportada en EJEMPLO VI).
    const priced = leaves.filter(
      (c) => c.status === 'priced' || c.status === 'manual' || Number(c.importe) > 0,
    ).length;
    const failed = leaves.filter((c) => c.status === 'failed').length;
    return { sum, priced, failed, total: leaves.length };
  }, [concepts]);

  // Total del presupuesto para la tarjeta: número grande + moneda chica.
  const montoGrande = fmtObra.partes(totals.sum);

  const sinCandidatoCount = useMemo(
    () =>
      concepts.filter(
        (c) => c.kind !== 'group' && c.confianza === 'sin_candidato',
      ).length,
    [concepts],
  );

  const sinteticaCount = useMemo(
    () =>
      concepts.filter(
        (c) => c.kind !== 'group' && c.confianza === 'sintetica',
      ).length,
    [concepts],
  );

  // análisis con IA EXISTENTES (generados), independientemente de si la fuente
  // activa es IA o catálogo. Estable al prender/apagar el toggle (el análisis no
  // se borra al cambiar de fuente) → alimenta el botón staff de aprobar en lote.
  const apuGeneratedCount = useMemo(
    () => concepts.filter((c) => c.kind !== 'group' && c.hasApuIaMatrix).length,
    [concepts],
  );

  // análisis con IA cuyo contexto cambió después de su generación —
  // se importarán a OPUS con datos viejos a menos que el Analista los regenere.
  const outdatedCount = useMemo(
    () =>
      concepts.filter(
        (c) => c.kind !== 'group' && c.confianza === 'sintetica' && c.apuIaContextOutdated,
      ).length,
    [concepts],
  );

  // ID del job-run más reciente — lo pide la vista de impresión del análisis
  // (`openApuPrint`), que es lo único que sigue entrando por la ruta /apu-ia.
  const ultimoApuRunId = useMemo(() => {
    const sintCons = concepts.filter((c) => c.confianza === 'sintetica' && c.apuIaJobRunId);
    return sintCons[0]?.apuIaJobRunId ?? null;
  }, [concepts]);

  // Aprobar en lote todas las análisis con IA — solo staff Ecosoft
  // (conveniencia para smoke tests m0→m1; el server solo aprueba las pendientes).
  const [approvingAll, setApprovingAll] = useState(false);
  async function onApproveAllMatrices() {
    if (!quoteId) return;
    const ok = await confirm({
      title: 'Aprobar todas las matrices',
      message: `Se aceptarán las matrices generadas con inteligencia artificial de este presupuesto, incluidas las de cobertura parcial o limitada. Podrás reabrir cualquiera después.`,
      confirmLabel: 'Aprobar todas',
    });
    if (!ok) return;
    setApprovingAll(true);
    try {
      const res = await approveAllMatrices(quoteId);
      await loadConcepts(quoteId);
      dispatchToast(
        <Toast><ToastTitle>✓ {res.approved} matriz{res.approved === 1 ? '' : 'es'} aprobada{res.approved === 1 ? '' : 's'}</ToastTitle></Toast>,
        { intent: 'success', timeout: 4000 },
      );
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>{e instanceof Error ? e.message : 'No se pudieron aprobar las matrices'}</ToastTitle></Toast>,
        { intent: 'error', timeout: 5000 },
      );
    } finally {
      setApprovingAll(false);
    }
  }

  // Impresión de los análisis con IAs (pestaña nueva, diálogo nativo del
  // navegador). Único acceso ahora que se retira la vista de sintéticos.
  function openApuPrint(mode: 'simple' | 'detallado') {
    if (!projectId || !quoteId || !ultimoApuRunId) return;
    window.open(
      `/project/${projectId}/quotes/${quoteId}/apu-ia/runs/${ultimoApuRunId}/print?mode=${mode}`,
      '_blank',
      'noopener',
    );
  }

  const catalogColorMap = useMemo(
    () => Object.fromEntries(
      selectedCatalogIds.map((id, i) => [id, CATALOG_COLORS[i % CATALOG_COLORS.length]!])
    ),
    [selectedCatalogIds],
  );

  const catalogPriorityMap = useMemo(
    () => Object.fromEntries(selectedCatalogIds.map((id, i) => [id, i + 1])),
    [selectedCatalogIds],
  );

  // Catálogos seleccionados que todavía se están generando (parse/embeddings)
  // → no se puede cotizar con ellos hasta que terminen (matcher necesita embeddings).
  const generatingCatalogs = useMemo(
    () =>
      selectedCatalogIds
        .map((id) => allCatalogs.find((c) => c.id === id))
        .filter((c): c is Catalog => !!c && !catalogUsable(c)),
    [selectedCatalogIds, allCatalogs],
  );

  // Conteo por estado efectivo (confKind) para la tira de filtros (solo conceptos).
  const confianzaCounts = useMemo(() => {
    const acc = { alta: 0, media: 0, sin_equivalente: 0, sintetica: 0, sin_procesar: 0 };
    for (const c of concepts) {
      if (c.kind === 'group') continue;
      acc[confKind(c)]++;
    }
    return acc;
  }, [concepts]);

  // Cuántos renglones quedan por revisar: alimenta el chip y el aviso de arriba.
  const estadoCounts = useMemo(() => {
    const acc = { por_revisar: 0 };
    for (const c of concepts) {
      if (c.kind === 'group') continue;
      if (porRevisar(c)) acc.por_revisar++;
    }
    return acc;
  }, [concepts]);

  // ¿Ya se buscó equivalentes alguna vez? Distingue la PRIMERA búsqueda —
  // conceptos recién leídos del documento, todos en 'pending', sin resultado
  // aún— de una RE-búsqueda (ya hay conceptos con precio o marcados sin
  // equivalente). Sin esto, el diálogo decía "se volverán a buscar" en un
  // proyecto nuevo que nunca se buscó: solo miraba si EXISTÍAN conceptos, no si
  // ya tenían resultado. Un concepto se considera "ya buscado" cuando salió de
  // 'pending' (priced/failed/manual/skipped).
  const hasSearchedBefore = useMemo(
    () => concepts.some((c) => c.kind !== 'group' && c.status !== 'pending'),
    [concepts],
  );

  // Sintéticos que aún faltan aceptar (matriz sin aprobar): excluye aprobados y
  // rechazados (ésos ya se resolvieron). Alimenta el KPI "Sintéticos por aceptar".
  const sinteticosPorAceptar = useMemo(
    () =>
      concepts.filter(
        (c) =>
          c.kind !== 'group' &&
          confKind(c) === 'sintetica' &&
          c.apuIaReviewStatus !== 'approved' &&
          c.apuIaReviewStatus !== 'rejected',
      ).length,
    [concepts],
  );

  // Cuántos conceptos se BUSCARÁN realmente en el diálogo de "Buscar equivalentes":
  //  - re-búsqueda (reuseConcepts): total MENOS lo que se conserva. Los sintéticos
  //    SIEMPRE se conservan; los de catálogo según `preserveScope`. Así el diálogo
  //    no dice "76" cuando en realidad respeta sintéticos y lo conservado.
  //  - búsqueda inicial (no reuse): el total leído del documento.
  const costToSearch = useMemo(() => {
    if (!costConfirm) return 0;
    if (!costConfirm.opts.reuseConcepts) return costConfirm.concepts;
    // Los `sin_procesar` (pending, hoja detenida a mitad) también se buscan al
    // re-buscar equivalentes: van en los tres alcances, como los sin_equivalente.
    if (preserveScope === 'alta_media') return confianzaCounts.sin_equivalente + confianzaCounts.sin_procesar;
    if (preserveScope === 'alta') return confianzaCounts.media + confianzaCounts.sin_equivalente + confianzaCounts.sin_procesar;
    return confianzaCounts.alta + confianzaCounts.media + confianzaCounts.sin_equivalente + confianzaCounts.sin_procesar; // 'none'
  }, [costConfirm, preserveScope, confianzaCounts]);

  const isActive = quote && ACTIVE_STATUSES.has(quote.status);
  const isReview = quote && REVIEW_STATUSES.has(quote.status);
  // Aviso de una generación con IA que falló. Si el proceso mandó un motivo
  // apto para el cliente (ej. "el servicio de IA no está respondiendo, intentá
  // en un rato") se usa ese; si no, el genérico. Se calcula acá —no dentro del
  // MessageBar— a propósito: el aviso visible no puede nombrar la mecánica interna
  // (regla del repo), y la referencia a la señal en vivo no debe quedar en el texto.
  const avisoGeneracionFallida = apuJob?.message
    ? apuJob.message
    : 'La generación de análisis con inteligencia artificial no llegó a terminar.';
  /**
   * La corrida NO terminó (falló o la detuvieron): lo que hay en pantalla es un
   * resultado a medias y ningún número puede presentarse como definitivo.
   */
  const corridaIncompleta = quote?.status === 'failed' || quote?.status === 'canceled';

  // Para poder generar/recotizar hace falta el archivo del listado de conceptos
  // y al menos un catálogo. Si falta algo, se bloquean los botones y se avisa.
  const missingForGenerate = !quote?.sourceFileId
    ? 'Falta elegir el archivo del listado de conceptos'
    : selectedCatalogIds.length === 0
      ? 'Selecciona al menos un catálogo'
      : null;
  // El proyecto de ejemplo (demo) es de solo lectura: bloquea todo lo que gasta
  // tokens (armar hoja / buscar equivalentes / APUs con IA).
  const isDemo = quote?.isExample === true;
  const demoCatalogs = useMemo(
    () => (isDemo ? buildDemoCatalogList(allCatalogs) : allCatalogs),
    [isDemo, allCatalogs],
  );
  /** En demo: si falta source_file_id, usar el PDF marcado como catálogo / listado. */
  const sourceSelectValue = useMemo(() => {
    if (quote?.sourceFileId) return quote.sourceFileId;
    if (!isDemo) return '';
    return (
      projectFiles.find((f) => f.classification === 'catalogo')?.id
      ?? projectFiles.find((f) => /listado/i.test(f.originalName))?.id
      ?? ''
    );
  }, [quote?.sourceFileId, isDemo, projectFiles]);
  const canGenerate = !missingForGenerate && !isDemo;
  // Leer el listado solo requiere el archivo fuente (los catálogos son del pricing).
  const missingForExtract = !quote?.sourceFileId ? 'Falta elegir el archivo del listado de conceptos' : null;
  const canExtract = !missingForExtract && !isDemo;
  // Flujo en dos pasos: cotizar se habilita cuando ya hay conceptos leídos.
  // Manda el listado que EXISTE, no el contador de la cotización: si una corrida se
  // cortó después de guardar los conceptos pero antes de escribir el contador
  // (`concepts_total` se quedó en 0), la pantalla mostraba la hoja llena y el botón
  // seguía diciendo "Armar la hoja de presupuesto", como si no hubiera nada —
  // reporte de Sebastián, 2026-08-12.
  const hasConcepts = (quote?.conceptsTotal ?? 0) > 0 || concepts.length > 0;

  // Secuencia de guardados de catálogos: descarta respuestas fuera de orden
  // para no pisar estado más nuevo (p. ej. una cotización recién encolada).
  const catalogSaveSeq = useRef(0);
  const stageMsg = liveProgress
    ? (liveProgress.message ?? STAGE_LABEL[liveProgress.stage])
    : null;
  // Stage real del worker (no el % ponderado). Sin evento aún: cola / armar / equivalentes.
  const progressStage =
    liveProgress?.stage
    ?? (quote?.status === 'queued'
      ? 'queued'
      : (quote?.conceptsTotal ?? 0) === 0
        ? 'extract'
        : 'pricing');
  /**
   * ESPERANDO SU TURNO ≠ TRABAJANDO. La cotización está encolada y ningún worker la
   * tomó: no hay lectura en curso, no hay avance y no hay nada que "siga trabajando".
   * Un evento del worker (aunque sea el primero) rompe la espera, porque significa
   * que alguien la tomó.
   */
  const enCola = quote?.status === 'queued'
    && (liveProgress == null || liveProgress.stage === 'queued');
  // Desde cuándo espera: `updatedAt` es el momento en que se encoló (la ruta lo
  // toca al pasar a 'queued'), así que el reloj sobrevive a recargar la página.
  const esperandoDesde = useMemo(() => {
    if (!enCola || !quote?.updatedAt) return null;
    const t = Date.parse(String(quote.updatedAt));
    return Number.isFinite(t) ? t : null;
  }, [enCola, quote?.updatedAt]);
  // El mismo botón de Detener corta DOS trabajos distintos según la etapa, y tiene que
  // decir cuál: primero se arma la hoja de presupuesto (leer el listado del documento) y
  // después se buscan los equivalentes en los catálogos. Un "Detener" pelado obligaba al
  // usuario a adivinar qué estaba por cortar (pedido de Sebastián, 2026-08-12).
  const enArmado = progressStage !== 'pricing';
  const procesoPresupuesto = enArmado
    ? 'el armado de la hoja de presupuesto'
    : 'la búsqueda de equivalentes';
  // Versión corta para el botón mientras la detención está en camino: nombra el trabajo
  // sin que el botón cambie de ancho de golpe.
  const procesoPresupuestoCorto = enArmado ? 'el armado' : 'la búsqueda';
  // Mientras la detención está pedida pero el worker todavía no paró, la leyenda del
  // progreso lo DICE. Antes solo cambiaba el texto de un botón chico: el usuario confirmaba
  // el Alto, veía el progreso seguir avanzando y no tenía forma de saber que su pedido
  // estaba en camino (regla de Sebastián: el worker sigue trabajando un rato después de
  // confirmar, así que hay que mostrarlo).
  const progressStageLabel = controlPending === 'stop'
    ? `Deteniendo ${procesoPresupuesto}… se está terminando lo que quedó a medias`
    : (STAGE_LABEL[progressStage as keyof typeof STAGE_LABEL] ?? STAGE_LABEL.pricing);
  // ETA calculada por el store global (velocidad real conceptos/seg).
  const jobEtaMs = useQuoteJobsStore((s) => (quoteId ? s.jobs[`quote:${quoteId}`]?.etaMs ?? null : null));

  // Breadcrumb: Inicio / Proyectos / {proyecto} / Cotización (última = actual).
  const quoteCrumbs = [
    { label: 'Inicio', to: '/projects' },
    { label: 'Proyectos', to: '/projects' },
    { label: projectName ?? 'Proyecto', to: `/project/${projectId}` },
    { label: 'Presupuesto' },
  ];

  if (loading) {
    return (
      <div className={styles.page}>
        <AppTopBar />
        <Breadcrumbs items={quoteCrumbs} />
        <div className={styles.loading}>
          <Spinner label="Cargando presupuesto…" />
        </div>
      </div>
    );
  }

  if (err && !quote) {
    return (
      <div className={styles.page}>
        <AppTopBar />
        <Breadcrumbs items={quoteCrumbs} />
        <div style={{ padding: 'var(--space-xxl)' }}>
          {/* `layout="multiline"` porque los mensajes de error largos —el de la moneda
              de la obra, por ejemplo— se salían de la pantalla en una sola línea y la
              parte que dice QUÉ HACER quedaba cortada del lado derecho. */}
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>{err}</MessageBarBody>
          </MessageBar>
          <Button
            appearance="subtle"
            icon={<ArrowLeft24Regular />}
            style={{ marginTop: 'var(--space-l)' }}
            onClick={() => navigate(`/project/${projectId}`)}
          >
            Volver al proyecto
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page} style={{ overflow: 'hidden', maxWidth: '100%' }}>
      <AppTopBar />
      <Breadcrumbs items={quoteCrumbs} />

      {/* Área de trabajo: el contenido scrolleable y el panel de análisis son
          HERMANOS en un flex → el panel EMPUJA el contenido (nunca lo encima) y el
          scroll queda acotado. Fila cuando el panel va a la derecha; columna
          cuando va abajo. */}
      <div style={{ minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden', background: 'var(--bg-page)' }}>
      {/* ===== Catálogos de referencia: columna lateral de altura completa =====
          Fuera del área scrolleable → siempre visibles y llegan hasta abajo. El
          panel de análisis queda embebido en la columna del grid (a la derecha). */}
      <div style={{ flexShrink: 0, minHeight: 0, display: 'flex', padding: '18px 0 18px 18px', boxSizing: 'border-box' }}>
        <aside
          className={styles.leftPanel}
          style={{ position: 'static', top: 'auto', height: '100%', maxHeight: '100%', alignSelf: 'stretch', boxSizing: 'border-box', width: leftCollapsed ? 50 : 286, ...(leftCollapsed ? { padding: '10px 6px', alignItems: 'center' } : {}) }}
        >
          {leftCollapsed ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%', minHeight: 0 }}>
              <button
                type="button"
                className={styles.panelCollapseBtn}
                title="Mostrar el selector de listado y los catálogos de referencia"
                onClick={() => setLeftCollapsed(false)}
              >▸</button>
              {/* Rail colapsado: una insignia por catálogo (2 letras + color); los
                  que están EN USO van resaltados (relleno) y de primero, en orden de
                  prioridad. Clic = abrir el panel para gestionarlos. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', alignItems: 'center', overflowY: 'auto', minHeight: 0 }}>
                {(() => {
                  const idxOf = new Map(demoCatalogs.map((c, i) => [c.id, i] as const));
                  const selSet = new Set(selectedCatalogIds);
                  const selCats = selectedCatalogIds
                    .map((id) => demoCatalogs.find((c) => c.id === id))
                    .filter((c): c is typeof demoCatalogs[number] => !!c);
                  const unselCats = demoCatalogs.filter((c) => !selSet.has(c.id));
                  const badge = (c: typeof demoCatalogs[number], sel: boolean) => {
                    const color = CATALOG_COLORS[(idxOf.get(c.id) ?? 0) % CATALOG_COLORS.length]!;
                    return (
                      <Tooltip key={c.id} content={sel ? `${c.name} · en uso` : c.name} relationship="label">
                        <button
                          type="button"
                          aria-label={c.name}
                          onClick={() => setLeftCollapsed(false)}
                          style={{
                            width: 34, height: 30, borderRadius: 8, flexShrink: 0, padding: 0,
                            fontSize: 11, fontWeight: 800, letterSpacing: '0.02em', cursor: 'pointer',
                            border: `1.5px solid ${color}`,
                            background: sel ? color : 'transparent',
                            color: sel ? '#fff' : color,
                            opacity: sel ? 1 : 0.55,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >{catalogAbbrev(c.name)}</button>
                      </Tooltip>
                    );
                  };
                  return (
                    <>
                      {selCats.map((c) => badge(c, true))}
                      {/* Línea roja doble: arriba = en uso, abajo = sin usar. */}
                      {selCats.length > 0 && unselCats.length > 0 && (
                        <div
                          title="Arriba: en uso · Abajo: sin usar"
                          style={{ width: '82%', height: 3, borderTop: '2px solid #e5484d', borderBottom: '2px solid #e5484d', flexShrink: 0, margin: '3px 0' }}
                        />
                      )}
                      {unselCats.map((c) => badge(c, false))}
                    </>
                  );
                })()}
              </div>
            </div>
          ) : (
            <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span className={styles.panelLabel}>Listado de conceptos</span>
            <button
              type="button"
              className={styles.panelCollapseBtn}
              title="Ocultar para ensanchar el árbol de conceptos"
              onClick={() => setLeftCollapsed(true)}
            >◂</button>
          </div>
          <Select
            value={sourceSelectValue}
            disabled={sourceSaving || !canMutate || !!isActive || isDemo}
            style={{ width: '100%', marginBottom: 'var(--space-l)' }}
            onChange={async (_, d) => {
              if (!d.value || !quote) return;
              setSourceSaving(true);
              try {
                const r = await setQuoteSource(quote.id, d.value);
                setQuote(r.quote);
                // Selección compartida: reflejar la elección en el proyecto
                // (marca el archivo como catálogo de conceptos; el backend
                // degrada los demás). Best-effort, no bloquea.
                if (quote.projectId) {
                  void updateFileClassification(quote.projectId, d.value, 'catalogo').catch(() => {});
                }
              } catch (e) {
                setErr(e instanceof Error ? e.message : 'No se pudo cambiar el archivo');
              } finally {
                setSourceSaving(false);
              }
            }}
          >
            <option value="">Selecciona el archivo…</option>
            {projectFiles.map((f) => (
              <option key={f.id} value={f.id}>{f.originalName}</option>
            ))}
          </Select>
          {!sourceSelectValue && (
            <div style={{ fontSize: 'var(--fs-100)', color: 'var(--warn)', margin: '-8px 0 12px' }}>
              Elige el archivo con el listado de conceptos para armar la hoja de presupuesto.
            </div>
          )}

          <span className={styles.panelLabel}>Catálogos de referencia</span>
          {selectedCatalogIds.length === 0 && (
            <div style={{ fontSize: 'var(--fs-100)', color: 'var(--warn)', margin: '4px 0 8px' }}>
              Selecciona al menos un catálogo para buscar equivalentes.
            </div>
          )}

          <div data-demo="catalogos">
          <ProjectCatalogsPicker
            available={demoCatalogs}
            initialSelected={selectedCatalogIds}
            onChange={(ids) => {
              setSelectedCatalogIds(ids);
              // Selección compartida (proyecto ↔ cotización): persiste de
              // inmediato en la cotización Y en el proyecto, para que sobreviva
              // al salir sin regenerar y se refleje en el proyecto. Solo cuando
              // la cotización es editable (no corriendo).
              if (quoteId && canMutate && !isActive) {
                // Refrescar SOLO catalogIds al confirmar el guardado (re-dispara
                // la estimación de cobertura) y descartar respuestas fuera de
                // orden. NO reemplazar la quote completa: la respuesta del PATCH
                // puede llegar tarde y pisar estado más nuevo (bug 2026-07-08:
                // recotizar tras cambiar catálogos parecía "no hacer nada").
                const seq = ++catalogSaveSeq.current;
                void setQuoteCatalogs(quoteId, ids)
                  .then((r) => {
                    if (seq !== catalogSaveSeq.current) return; // hay un guardado más nuevo
                    setQuote((prev) => (prev ? { ...prev, catalogIds: r.quote.catalogIds } : prev));
                  })
                  .catch(() => {});
                if (quote?.projectId) {
                  void setProjectCatalogs(quote.projectId, ids).catch(() => {});
                }
              }
            }}
            disabled={catalogsSaving || !canMutate}
          />
          </div>

          {/* Costo IA acumulado: removido de la vista de cotizaciones (era visible
              a CUALQUIER usuario, incluido cliente, y usaba una tarifa LLM desactualizada).
              El consumo $ por tenant ya vive en el dashboard staff /staff/usage.
              Pendiente: que /staff/usage desglose el costo por tenant TAMBIÉN por usuario. */}
            </>
          )}
        </aside>
      </div>

      {/* Columna de contenido: stepper + grid (scrollea) + panel de análisis
          embebido (empuja, nunca encima). Fila si va a la derecha; columna si abajo. */}
      <div style={{ flex: '1 1 0', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: paneOpen && paneMode === 'abajo' ? 'column' : 'row', overflow: 'hidden' }}>
      {/* Barra superior con nombre y estado */}
      <div
        ref={scrollAreaRef}
        className={styles.scrollArea}
        style={{ overflowX: 'clip', overflowY: 'auto', boxSizing: 'border-box', flex: '1 1 0', minWidth: 0, minHeight: 0 }}
        onClick={(e) => {
          // Cerrar el panel al hacer clic en una "zona muerta" (ni fila ni control).
          if (!panelId) return;
          const t = e.target as HTMLElement;
          if (t.closest('[data-index]')) return; // clic en una fila → la maneja la fila
          if (t.closest('button, a, input, select, textarea, label, [role="menuitem"], [role="button"], [role="tab"], [contenteditable="true"], .fui-Combobox, .fui-Dropdown')) return;
          setPanelId(null);
        }}
      >
      <div className={styles.topRow}>
        <div className={styles.headLine}>
          <h1 className={styles.topTitle}>Presupuesto · {quote?.name ?? '—'}</h1>
          {/* Mensajes (docs/29): enviar el presupuesto y avisar al equipo; solo si el hub lo tiene. */}
          {quote && !isDemo && (
            <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
              <BotonesMensajes
                app={brandingProductoActivo().producto}
                refDoc={quote.id}
                titulo={`Presupuesto ${quote.name}`}
                url={window.location.href}
                mensaje={mensajeDelPresupuesto(quote.name, window.location.href)}
              />
            </span>
          )}
        </div>
        <div className={styles.actionsRow} ref={actionsRowRef}>
          {/* Demo: mostrar pasos 1–2 deshabilitados (ya hechos) para no perder el flujo pedagógico. */}
          {(canMutate || isDemo) && (
            <>
              {/* Paso 1 — armar la hoja desde el documento */}
              <span className={styles.stepGroup}>
                <Button
                  className={styles.stepBtn}
                  // Ningún paso en azul: los tres iguales, el estado lo cuenta el texto.
                  appearance="secondary"
                  disabled={isDemo || catalogsSaving || !!isActive || isGenerating || !canExtract}
                  onClick={() => { if (!isDemo) void onRegenerate({ extractOnly: true }); }}
                  data-demo="paso1"
                  title={isDemo
                    ? 'En el demo este paso ya está hecho: la hoja se armó desde el listado de conceptos del expediente.'
                    : (missingForExtract ?? undefined)}
                >
                  <span className={styles.stepBtnTop}>
                    <span className={styles.stepNum}>1</span>
                    {isDemo
                      ? 'Hoja de presupuesto (ya armada)'
                      : (hasConcepts ? 'Rearmar la hoja de presupuesto' : 'Armar la hoja de presupuesto')}
                  </span>
                  <span className={styles.stepBtnSub}>
                    {isDemo
                      ? 'demo · solo lectura — partidas y cantidades ya cargadas'
                      : 'partidas, conceptos y cantidades — todavía sin precios ni matrices'}
                  </span>
                </Button>
                {helpDot('h1')}
              </span>

              {/* Paso 2 — buscar equivalentes (siempre proceso completo: léxico/semántico + IA en zona gris) */}
              <span className={styles.stepGroup}>
                {isDemo ? (
                  <Button
                    className={styles.stepBtn}
                    appearance="secondary"
                    disabled
                    title="En el demo este paso ya está hecho: los equivalentes y precios de catálogo ya se buscaron."
                  >
                    <span className={styles.stepBtnTop}>
                      <span className={styles.stepNum}>2</span>
                      Buscar equivalentes (ya hecho)
                    </span>
                    <span className={styles.stepBtnSub}>
                      demo · solo lectura — catálogos de referencia ya aplicados
                    </span>
                  </Button>
                ) : (
                <Button
                  className={styles.stepBtn}
                  // NINGUNO de los pasos va en azul (reporte de dirección, 2026-08-11): el
                  // azul del paso 2 seguía puesto DESPUÉS de haber buscado, y se leía como
                  // "hacé esto ahora" o "esto está corriendo". Los tres pasos quedan iguales
                  // y el estado lo cuenta el texto, no el color.
                  appearance="secondary"
                  data-demo="paso2"
                  disabled={catalogsSaving || !!isActive || isGenerating || generatingCatalogs.length > 0 || !canGenerate || !hasConcepts || (selectionMode && selectedCount === 0)}
                  title={!hasConcepts
                    ? 'Primero arma la hoja de presupuesto.'
                    : (selectionMode && selectedCount === 0)
                      ? 'Selecciona al menos un concepto.'
                      : (missingForGenerate ?? 'Compara cada concepto contra tus catálogos y confirma los dudosos con inteligencia artificial.')}
                  onClick={() => void onRegenerate({ ...(hasConcepts ? { reuseConcepts: true } : {}) })}
                >
                  <span className={styles.stepBtnTop}>
                    <span className={styles.stepNum}>2</span>
                    {/* "Volver a buscar" cuando ya se buscó: el mismo rótulo de antes hacía
                        dudar de si había que apretarlo otra vez. */}
                    {hasSearchedBefore ? 'Volver a buscar equivalentes' : 'Buscar equivalentes'}
                  </span>
                  <span className={styles.stepBtnSub}>
                    en tus catálogos de referencia
                  </span>
                </Button>
                )}
                {helpDot('h2')}
              </span>
            </>
          )}

          {/* Paso 3 — análisis de precios unitarios con IA */}
          <span className={styles.stepGroup}>
            <Button
              className={styles.stepBtn}
              appearance="secondary"
              data-demo="paso3"
              disabled={!quote || !!isActive || isGenerating || iaMarkedPendingIds.length === 0}
              onClick={() => void onGenerateApuIa(iaMarkedPendingIds)}
              title={!quote
                ? 'Carga la cotización primero.'
                : isActive
                  ? 'Espera a que termine el proceso en curso.'
                  : iaMarkedPendingIds.length === 0
                    ? 'Enciende el interruptor de inteligencia artificial en los conceptos que quieras construir con IA.'
                    : `Construir con inteligencia artificial ${iaMarkedPendingIds.length} concepto(s).`}
            >
              <span className={styles.stepBtnTop}>
                <span className={styles.stepNum}>3</span>
                ⚡ Análisis de precios unitarios con IA{iaMarkedPendingIds.length > 0 ? ` · ${iaMarkedPendingIds.length}` : ''}
              </span>
              <span className={styles.stepBtnSub}>
                precios construidos por inteligencia artificial para lo que no está en catálogo
              </span>
            </Button>
            {helpDot('h3')}
          </span>

          {/* Configuración (no es paso) — contexto de proyecto, empujado a la derecha */}
          <span className={styles.actionsRight}>
            <span className={styles.actionsSep} />
            <span className={styles.stepGroup}>
              <Button
                className={mergeClasses(
                  styles.stepBtn,
                  obraContextAjustado === true && styles.ctxDone,
                  obraContextAjustado === false && styles.ctxPending,
                )}
                appearance="subtle"
                onClick={() => navigate(`/project/${projectId}/obra-context`, { state: { returnTo: `/project/${projectId}/quotes/${quoteId}` } })}
              >
                <span className={styles.stepBtnTop}>
                  {obraContextAjustado === false ? '⚠️' : obraContextAjustado === true ? '✓' : '⚙'} Contexto de proyecto
                </span>
                <span className={styles.stepBtnSub}>
                  {obraContextAjustado === false
                    ? 'sin ajustar — requerido para crear análisis de precios unitarios con IA'
                    : 'ajustado — lo usan los análisis de precios unitarios con inteligencia artificial'}
                </span>
              </Button>
              {helpDot('h4')}
            </span>
          </span>
        </div>
        {/* NO SE AFIRMA QUE LA HOJA ESTÉ COMPLETA (2026-09-02, orden de Sebastián).
            Acá iba un "✓ Lectura verificada: la hoja está completa" atado a
            `integrity.verdict === 'ok'`. Ese veredicto solo mira que NO FALTEN
            renglones (`sourceIntegrity.ts`: avisa cuando lo extraído queda por
            debajo de la señal del documento), así que una hoja con renglones DE
            MÁS lo cumplía y salía en verde — Docencia III trae 344 donde el
            documento declara 335 y el cliente leía que estaba todo bien. Medido
            en la base local: el verde salió en 20 de 39 hojas con señal y el
            ámbar, cero veces. El criterio manda: de más o de menos, es
            descalificación, así que la afirmación positiva no se sostiene y se
            va. El aviso ámbar de posible omisión (abajo) se queda. */}
        <div className={styles.metaLine}>
          <div className={styles.subMeta} style={{ marginLeft: 'auto' }}>
            {quote && <OpusTrackingChip quote={quote} />}
          </div>
        </div>
      </div>

      {/* Verificación de integridad de la extracción (capa 1, 2026-07-06):
          cruce independiente documento-fuente vs listado. Nunca bloquea; una
          posible omisión jamás pasa callada. */}
      {quote?.integrity?.verdict === 'warning' && (
        <div style={{ padding: '0 var(--space-xxl) var(--space-m)' }}>
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>Revisa la hoja antes de buscar equivalentes</MessageBarTitle>
              El documento aparenta {quote.integrity.approximate ? '~' : ''}
              {quote.integrity.estimatedQuotableRows?.toLocaleString('es-MX')} renglones con concepto y la
              hoja tiene {quote.integrity.extractedConcepts.toLocaleString('es-MX')} conceptos —
              es posible que la lectura haya omitido renglones.
              {quote.integrity.sequenceGaps && quote.integrity.sequenceGaps.length > 0 && (
                <> Verifica en particular los renglones {quote.integrity.sequenceGaps.slice(0, 12).join(', ')}
                {quote.integrity.sequenceGaps.length > 12 ? '…' : ''} del documento.</>
              )}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}
      {/* Solo queda el aviso ámbar: es accionable y no afirma nada que no se pueda
          sostener. Cuando el veredicto es 'ok' NO se muestra nada — ver la nota de
          arriba (metaLine). */}

      {/* POR QUÉ LA HOJA LLEVA CLAVES PROPIAS (2026-08-12). Cuando el catálogo numera sus
          renglones pero ese número no identifica al concepto (el mismo número en dos conceptos
          distintos), el sistema le pone a cada renglón una clave propia. El texto viene armado
          desde la lectura, con el motivo y un ejemplo del documento, y se muestra tal cual. */}
      {quote?.clavesAviso && (
        <div style={{ padding: '0 var(--space-xxl) var(--space-m)' }}>
          <MessageBar intent="info" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>Las claves de esta hoja las asignó OPUS ai</MessageBarTitle>
              {quote.clavesAviso}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* LA HOJA NO QUEDÓ COMPLETA, CON NOMBRE Y APELLIDO (2026-08-13). Hay renglones que
          el documento muestra y no se pudieron leer ni volviendo sobre su página. Entran a
          la hoja marcados y sin precio en vez de desaparecer —un renglón que falta no se
          nota y puede dejar una propuesta fuera— y acá se dice cuáles y en qué páginas
          están, para que nadie mande esta hoja creyendo que está entera. El texto del
          aviso viene armado desde el worker y se muestra tal cual. */}
      {/* LA HOJA SALIÓ SIN SUS PARTIDAS (AI-59). Aviso propio y separado del de los
          renglones que no se pudieron leer: acá los renglones están bien y lo que falta
          es la agrupación, así que pedirle al cotizador "revisar renglones" lo mandaría a
          buscar un problema que no existe. El texto viene redactado desde el worker y se
          muestra tal cual. */}
      {/* SI SE RESOLVIÓ, NO SE DIBUJA NADA. Antes el recuadro salía igual, en verde,
          diciendo que las partidas "se repusieron" — cero información para quien lo lee, y
          la duda de si la hoja está mal. Ahora el aviso queda solo para el caso en que el
          cotizador SÍ tiene algo que hacer: cuando no se pudo arreglar la agrupación. */}
      {quote?.estructuraDudosa && !quote.estructuraDudosa.resuelta && (
        <div style={{ padding: '0 var(--space-xxl) var(--space-m)' }}>
          <MessageBar intent={quote.estructuraDudosa.resuelta ? 'success' : 'warning'} layout="multiline">
            <MessageBarBody>
              {/* El título viene redactado del worker, igual que el cuerpo. Estaba fijo
                  acá y decía "quedó sin sus partidas" pasara lo que pasara: una hoja con
                  499 partidas y un solo agrupador dudoso mostraba ese cartel, y el
                  cotizador salía a rearmar una hoja que estaba bien.

                  Las hojas guardadas antes del 2026-08-26 no traen `titulo`, y por eso el
                  respaldo NO es el texto viejo sino el mismo criterio aplicado a los
                  motivos, que sí están guardados: si no, esas hojas seguirían mostrando el
                  cartel equivocado hasta que alguien las regenere — y regenerar destruye
                  trabajo. `tituloDeEstructura` vive en @supernova/shared, que usan los dos lados. */}
              <MessageBarTitle>
                {quote.estructuraDudosa.titulo
                  ?? tituloDeEstructura(quote.estructuraDudosa, quote.estructuraDudosa.resuelta)}
              </MessageBarTitle>
              {quote.estructuraDudosa.aviso}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}
      {quote?.hojaIncompleta && quote.hojaIncompleta.total > 0 && (
        <div style={{ padding: '0 var(--space-xxl) var(--space-m)' }}>
          {/* El aviso cuenta los que FALTAN, en vivo: a medida que el cotizador los
              corrige, el número baja y al llegar a cero el aviso lo dice en vez de seguir
              pidiendo algo que ya se hizo. Y trae el botón que aplica el filtro "Por
              revisar" y abre el primero — así el aviso no necesita navegación propia. */}
          <MessageBar intent={estadoCounts.por_revisar > 0 ? 'warning' : 'success'} layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>
                {estadoCounts.por_revisar === 0
                  ? (quote.hojaIncompleta.total === 1
                      ? 'Ya revisaste el renglón que no se pudo leer'
                      : `Ya revisaste los ${quote.hojaIncompleta.total} renglones que no se pudieron leer`)
                  : estadoCounts.por_revisar === 1
                    ? 'Falta revisar 1 renglón antes de entregar esta hoja'
                    : `Faltan revisar ${estadoCounts.por_revisar} renglones antes de entregar esta hoja`}
              </MessageBarTitle>
              {/* El texto concuerda con el número: con un solo renglón el plural sonaba a
                  que faltaban otros. */}
              {estadoCounts.por_revisar > 0 ? quote.hojaIncompleta.aviso : (
                quote.hojaIncompleta.total === 1
                  ? 'Lo corregiste a mano, así que quedó marcado como tal: en la hoja se ve quién lo tocó y cuándo.'
                  : 'Los corregiste a mano, así que quedaron marcados como tales: en la hoja se ve quién los tocó y cuándo.'
              )}
              {estadoCounts.por_revisar > 0 && (
                <>
                  <ul style={{ margin: 'var(--space-s) 0 0', paddingLeft: '1.2em' }}>
                    {quote.hojaIncompleta.renglones.slice(0, 8).map((r, i) => (
                      <li key={`${r.pagina}-${r.clave ?? ''}-${i}`} style={{ marginBottom: '2px' }}>
                        <strong>Página {r.pagina}</strong>
                        {r.clave ? ` · ${r.clave}` : ''} · {r.descripcion}
                        {r.cantidad != null && r.unidad ? ` (${r.cantidad.toLocaleString('es-MX')} ${r.unidad})` : ''}
                      </li>
                    ))}
                  </ul>
                  {quote.hojaIncompleta.renglones.length > 8 && (
                    <div style={{ marginTop: '2px', color: 'var(--text-tertiary)' }}>
                      Y {quote.hojaIncompleta.renglones.length - 8} más, marcados en la hoja.
                    </div>
                  )}
                  <div style={{ marginTop: 'var(--space-s)' }}>
                    <Button size="small" appearance="primary" onClick={abrirPorRevisar}>
                      Revisar estos renglones
                    </Button>
                  </div>
                </>
              )}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* ── En qué terminó la última corrida ────────────────────────────────────
          Una cotización que NO terminó no puede verse igual que una terminada. Hasta
          el 2026-08-12 una corrida fallida mostraba la hoja con datos, el monto y los
          botones como si todo estuviera bien: el único rastro del fallo era un
          renglón rojo con el mensaje del error… que llegaba vacío cuando el fallo era
          técnico (ver reconcileFailedQuote en el worker). Sebastián leyó "Sin
          conceptos detectados" y se fue a revisar su documento, que estaba impecable.
          Acá se dice qué pasó y qué puede hacer, arriba de todo y en una sola frase. */}
      {!isActive && quote?.status === 'failed' && (
        <div style={{ padding: '0 var(--space-xxl) var(--space-m)' }}>
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>No pudimos terminar este presupuesto</MessageBarTitle>
              {quote.error
                ?? 'El proceso se interrumpió antes de terminar. Vuelve a intentarlo; si se repite, avísanos.'}
              {totals.total > 0 && ' Lo que ves abajo quedó a medias: todavía no es un presupuesto para entregar.'}
              {' '}
              {hasConcepts
                ? 'Para volver a intentarlo, usa el paso 2 (buscar equivalentes) o rearma la hoja con el paso 1.'
                : 'Para volver a intentarlo, usa el paso 1 (armar la hoja de presupuesto).'}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {!isActive && quote?.status === 'canceled' && (
        <div style={{ padding: '0 var(--space-xxl) var(--space-m)' }}>
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>Este presupuesto se detuvo antes de terminar</MessageBarTitle>
              Lo que ves abajo quedó a medias: sirve para revisar, no para entregar.
              Cuando quieras, retoma con el paso 1 o el paso 2.
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* Un borrador con precios existe: los análisis con inteligencia artificial se
          generan sobre la hoja sin haber buscado equivalentes, y la cotización sigue
          en borrador. Ahí "todavía sin precios" sería otra mentira, así que el aviso
          solo aparece cuando de verdad no hay ni un precio en la hoja. */}
      {!isActive && quote?.status === 'draft' && hasConcepts && totals.priced === 0 && (
        <div style={{ padding: '0 var(--space-xxl) var(--space-m)' }}>
          <MessageBar intent="info" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>La hoja está armada, todavía sin precios</MessageBarTitle>
              Ya tienes las partidas, los conceptos y las cantidades del documento.
              Los precios llegan en el paso 2, cuando se buscan los equivalentes en tus catálogos.
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* Estimación de cobertura de catálogos (previa a cotizar) RETIRADA 2026-07-25:
          confundía (aparecía "0% / el resto saldrá Sin equivalente" aunque los
          sintéticos resuelven eso). La cobertura real se ve en la tira de stats
          post-cotización. */}

      {/* Tira de 4 stats (mockup V4) */}
      {quote && totals.total > 0 && (
        <div className={styles.cotSummary}>
          <div className={styles.cotCell}>
            <div className={styles.cotLab}>Conceptos con precio</div>
            <div className={styles.cotBig}>{totals.priced.toLocaleString('es-MX')}</div>
            <div className={styles.cotNote}>de {totals.total.toLocaleString('es-MX')} totales</div>
          </div>
          {/* El monto NO se presenta como cifra final si la corrida no terminó: una
              suma parcial con el rótulo de siempre se lee como el precio de la obra. */}
          <div className={styles.cotCell}>
            <div className={styles.cotLab}>
              {corridaIncompleta ? 'Monto parcial' : 'Monto del proyecto'}
            </div>
            {/* Número grande + moneda chica, y tipografía que cede cuando el número es
                largo: el total de una obra de miles de millones no entra en el ancho de
                la tarjeta y se estaba CORTANDO (un total cortado miente). Antes pasaba
                igual con el signo de pesos; el código de moneda solo lo hizo visible. */}
            <div
              className={styles.cotBig}
              style={{
                display: 'flex', alignItems: 'baseline', gap: 5, minWidth: 0,
                ...(montoGrande.numero.length > 11 ? { fontSize: 'var(--fs-400)' } : {}),
              }}
            >
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{montoGrande.numero}</span>
              {montoGrande.codigo && (
                <span style={{ fontSize: '12px', fontWeight: 600, letterSpacing: 0, color: 'var(--text-secondary)' }}>
                  {montoGrande.codigo}
                </span>
              )}
            </div>
            <div className={styles.cotNote}>
              {corridaIncompleta
                ? 'incompleto — la corrida no terminó'
                : quote?.status === 'draft' && totals.sum === 0
                  ? 'todavía sin precios'
                  : 'suma de importes'}
            </div>
          </div>
          <div className={styles.cotCell}>
            <div className={styles.cotLab}>Confianza media</div>
            <div
              className={styles.cotBig}
              style={{ color: confianzaCounts.media > 0 ? 'var(--warn)' : 'var(--text-primary)' }}
            >
              {confianzaCounts.media.toLocaleString('es-MX')}
            </div>
            <div className={styles.cotNote}>equivalente dudoso — conviene revisar</div>
          </div>
          <div className={styles.cotCell}>
            <div className={styles.cotLab}>Análisis por aceptar</div>
            <div
              className={styles.cotBig}
              style={{ color: sinteticosPorAceptar > 0 ? 'var(--privado)' : 'var(--text-primary)' }}
            >
              {sinteticosPorAceptar.toLocaleString('es-MX')}
            </div>
            <div className={styles.cotNote}>falta aceptar su matriz</div>
          </div>
          <div className={styles.cotCell}>
            <div className={styles.cotLab}>Sin equivalente</div>
            <div className={styles.cotBig} style={{ color: 'var(--bad)' }}>
              {confianzaCounts.sin_equivalente.toLocaleString('es-MX')}
            </div>
            <div className={styles.cotNote}>sin equivalente ni candidatos</div>
          </div>
        </div>
      )}

      <div className={styles.shell} style={{ gridTemplateColumns: '1fr' }}>
        {/* ===== Panel derecho: conceptos ===== */}
        <section className={styles.rightPanel}>
          {/* `layout="multiline"` para que un mensaje largo se acomode en varias líneas:
              en una sola, el aviso de la moneda de la obra se cortaba justo donde dice
              QUÉ HACER. */}
          {err && (
            <MessageBar intent="error" layout="multiline">
              <MessageBarBody>{err}</MessageBarBody>
            </MessageBar>
          )}

          {/* (El control del panel de análisis + Imprimir viven en la fila de
              filtros de confianza, más abajo — junto a Todos/Alta/Media/…) */}

          {/* Banner: análisis con IA desactualizados */}
          {outdatedCount > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              marginBottom: 'var(--space-s)',
              background: 'var(--warn-weak)',
              border: '1px solid var(--warn)',
              borderLeft: '4px solid var(--warn)',
              borderRadius: 6,
              fontSize: 13,
            }}>
              <span style={{ fontSize: 20 }}>⚠</span>
              <div style={{ flex: 1 }}>
                <strong style={{ color: '#a16207' }}>{outdatedCount} análisis de precio unitario con IA{outdatedCount > 1 ? 's' : ''} desactualizado{outdatedCount > 1 ? 's' : ''}</strong>
                <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>
                  El contexto de proyecto cambió después de crearlos. Si OPUS importa este presupuesto ahora, va a recibir matrices con factores/parámetros obsoletos. <strong>Vuelve a crearlos antes de exportar.</strong>
                </div>
              </div>
              <Button
                size="small"
                appearance="primary"
                disabled={!!isActive || isGenerating}
                onClick={() => void onGenerateApuIa()}
                style={{ background: 'var(--warn)', borderColor: 'var(--warn)', color: '#000', whiteSpace: 'nowrap' }}
              >
                ⚡ Regenerar ahora
              </Button>
            </div>
          )}

          {/* Avance de generación con IA — arriba del listado (se queda en el
              presupuesto en vez de ir a la vista de sintéticos). */}
          {apuJob && apuJob.status === 'running' && (
            <>
              {/* ALTO para los sintéticos (reporte de dirección). Lo ya generado se
                  CONSERVA —cada análisis se guarda apenas se genera— y se cobra, porque es
                  trabajo entregado y usable. El worker para después de terminar el análisis
                  en curso, así que el cartel dice "Deteniendo…" hasta que el progreso lo
                  confirme: no se le miente al usuario diciéndole que ya paró. */}
              {canMutate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  {/* Visible a propósito: `subtle` lo dejaba casi invisible al lado del
                      progreso y Sebastián no lo encontraba. Es la única salida de un proceso
                      que puede durar minutos. */}
                  <Button
                    appearance="secondary"
                    size="medium"
                    icon={deteniendoApu ? <Spinner size="extra-tiny" /> : <Stop24Regular />}
                    disabled={deteniendoApu}
                    onClick={() => setConfirmarAlto('sinteticos')}
                    title="Detener la generación de análisis de precios con inteligencia artificial. Los que ya están hechos se conservan."
                  >
                    {deteniendoApu
                      ? 'Deteniendo la generación…'
                      : 'Detener la generación de análisis de precios'}
                  </Button>
                  {/* El aviso vive al lado del botón y NO se borra hasta que el worker
                      confirma: después de confirmar el Alto, el proceso sigue un rato
                      terminando lo que tiene a medias, y sin esto parecía que el botón no
                      había hecho nada. */}
                  {deteniendoApu && (
                    <span style={{ fontSize: 'var(--fs-200)', color: 'var(--semantic-warning, var(--text-secondary))' }}>
                      {/* La pantalla pone el NOMBRE del proceso (el usuario tiene que saber qué
                          se está deteniendo) y el worker aporta el detalle: el número exacto de
                          análisis que va a terminar. Hasta que llegue ese dato, un texto genérico. */}
                      Deteniendo la generación de análisis de precios:{' '}
                      {apuJob?.message?.startsWith('Deteniendo')
                        ? apuJob.message.replace(/^Deteniendo[:.…\s]*/i, '')
                        : 'se están terminando los que ya empezaron para no perderlos.'}
                    </span>
                  )}
                </div>
              )}
              <ApuGenerationProgress job={apuJob} />
            </>
          )}

          {/* LA GENERACIÓN QUE NO LLEGÓ A TERMINAR TIENE QUE DECIRLO.
              Hasta acá, el panel de avance solo existía mientras el trabajo estaba
              corriendo: cuando se cortaba, el panel **desaparecía sin una palabra**.
              El usuario veía "generando… 12 de 40", después nada, y unos análisis
              nuevos en el listado sin saber si eso era todo, si había fallado, ni si
              tenía que hacer algo.

              Lo importante del texto es la segunda frase: lo ya generado se guarda
              apenas se termina cada análisis, y volver a generar toma SOLO los que
              quedaron pendientes. Sin eso, "falló" se lee como "perdí todo y tengo que
              empezar de cero" —que es lo contrario de lo que pasó— y el usuario
              regenera de más, pagando dos veces lo mismo.

              Nada del motivo técnico acá: `error_message` guarda el error crudo del
              sistema (hay corridas en producción que dicen "Response del LLM no es
              JSON válido…") y el cliente no puede ver la mecánica interna. */}
          {apuJob && apuJob.status === 'failed' && (
            /* `multiline`: sin esto el aviso se dibuja en UNA sola línea y se corta a la
               derecha — se perdía justamente el final, que es la parte útil ("solo se
               trabajan los que quedaron pendientes"). Visto en el navegador. */
            <MessageBar intent="warning" layout="multiline" style={{ marginBottom: 'var(--space-s)' }}>
              <MessageBarBody>
                {avisoGeneracionFallida}{' '}
                Los análisis que sí se completaron quedaron guardados: si
                vuelves a generar, solo se trabajan los que quedaron pendientes.
              </MessageBarBody>
            </MessageBar>
          )}

          {/* Controles y progreso */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-s)' }}>
            {generatingCatalogs.length > 0 && (
              <MessageBar intent="info">
                <MessageBarBody>
                  Generando {generatingCatalogs.length === 1 ? 'el catálogo' : 'los catálogos'}{' '}
                  {generatingCatalogs.map((c) => c.name).join(', ')}. El presupuesto se habilita
                  cuando termine{generatingCatalogs.length === 1 ? '' : 'n'} (
                  {generatingCatalogs.map((c) => catalogStatusLabel(c)).join(' · ')}).
                </MessageBarBody>
              </MessageBar>
            )}
            {/* ALTO / SUSPENDER (reporte de dirección, 2ª vez que lo piden): estos tres
                botones y su backend YA existían y funcionaban de punta a punta — estaban
                apagados junto con la barra vieja de comandos. Se muestran solos, fuera de
                `SHOW_QUOTE_COMMANDS`, porque el resto de esa barra (recotizar, generar con
                IA, editar contexto) hoy vive en otro lado de la pantalla y prender todo
                duplicaría botones.
                Detener CONSERVA el avance: los conceptos ya resueltos quedan con su precio
                y la cotización queda "Cancelada" (ver runQuote: el control se revisa entre
                conceptos, nunca a mitad de una escritura). */}
            {canMutate && (quote?.status === 'running' || quote?.status === 'paused' || quote?.status === 'queued') && (
              <div className={styles.controlBar}>
              {/* REINTENTAR, solo para un job atorado en cola. NO hay "Reanudar":
                  Pausar se quitó a propósito (decisión de Sebastián, 2026-08-10). Con
                  Detener conservando el avance y "Recotizar" reusando lo que ya está,
                  detener-y-relanzar cubre el único beneficio de pausar sin agregar un
                  estado más. Y reanudar traía un riesgo que detener no tiene: dos clics
                  encolaban dos jobs sobre la misma cotización, que corren en paralelo y
                  se pisan. Un botón menos es un riesgo menos.
                  Para `failed` tampoco se ofrece: ahí va "Recotizar completo", que pasa
                  por preview y permite cotización parcial si excede la cuota. */}
              {quote?.status === 'queued' && (
                <Button
                  appearance="primary"
                  icon={controlPending === 'run' ? <Spinner size="extra-tiny" /> : <Play24Regular />}
                  size="small"
                  disabled={!!controlPending}
                  onClick={() => void onControl('run')}
                >
                  Reintentar
                </Button>
              )}
              {/* DETENER, en cualquier fase.
                  BÚSQUEDA DE EQUIVALENTES: el worker mira la señal entre olas y corta, ~2 s.
                  ARMADO (texto y visión): desde 2026-09-17 cada llamada paga de la lectura
                  —ubicar, extraer, completitud, visión de catálogo y de estructura, y la
                  jerarquía por IA— recibe la señal de Detener y aborta la llamada en curso;
                  el job cierra en 'canceled' sin persistir (hoja vacía o como estaba). El
                  caso que colgaba ~10 min era la visión de estructura de un escaneo. */}
              {(quote?.status === 'running' || quote?.status === 'paused') && (
                <Button
                  appearance="secondary"
                  icon={controlPending === 'stop' ? <Spinner size="extra-tiny" /> : <Stop24Regular />}
                  size="medium"
                  disabled={!!controlPending}
                  onClick={() => setConfirmarAlto('equivalentes')}
                  title={`Detener ${procesoPresupuesto}. Lo que ya se resolvió se conserva.`}
                >
                  {controlPending === 'stop'
                    ? `Deteniendo ${procesoPresupuestoCorto}…`
                    : `Detener ${procesoPresupuesto}`}
                </Button>
              )}
              </div>
            )}

            {SHOW_QUOTE_COMMANDS && canMutate && <div className={styles.controlBar}>
              <Button
                appearance={quote?.status === 'draft' ? 'primary' : 'outline'}
                size="small"
                icon={
                  catalogsSaving
                    ? <Spinner size="extra-tiny" />
                    : quote?.status === 'draft' ? <Play24Regular /> : <ArrowSync24Regular />
                }
                disabled={catalogsSaving || !!isActive || isGenerating || generatingCatalogs.length > 0}
                onClick={() => void onRegenerate()}
                title={
                  isActive
                    ? 'La cotización está en proceso. Pausa o detén antes de re-lanzar.'
                    : generatingCatalogs.length > 0
                      ? 'Espera a que termine de generarse el catálogo antes de cotizar.'
                      : 'Genera la cotización completa; usa inteligencia artificial para resolver los conceptos dudosos.'
                }
              >
                {catalogsSaving
                  ? 'Procesando…'
                  : quote?.status === 'draft' ? 'Generar cotización con inteligencia artificial' : 'Recotizar completo con inteligencia artificial'}
              </Button>
              {selectableCount > 0 && (
                <>
                  <Button appearance="subtle" size="small" disabled={!!isActive || isGenerating} onClick={() => void iaMarcarTodos(true)} style={{ color: 'var(--privado)' }} title="Encender el toggle IA en todos los conceptos">
                    Marcar todos con IA
                  </Button>
                  <Button appearance="subtle" size="small" disabled={!!isActive || isGenerating} onClick={() => void iaMarcarTodos(false)} title="Apagar el toggle IA en todos los conceptos">
                    Quitar
                  </Button>
                </>
              )}
              <Button
                appearance="outline"
                size="small"
                icon={<ArrowSync24Regular />}
                disabled={!!isActive || isGenerating || iaMarkedPendingIds.length === 0}
                onClick={() => void onGenerateApuIa(iaMarkedPendingIds)}
                title={
                  iaMarkedPendingIds.length === 0
                    ? 'Enciende el toggle "IA" en los conceptos que quieras cotizar con inteligencia artificial.'
                    : `Generar el análisis con inteligencia artificial de ${iaMarkedPendingIds.length} concepto(s) marcado(s).`
                }
                style={{ background: 'var(--privado-weak)', borderColor: 'var(--privado)', color: 'var(--privado)' }}
              >
                ⚡ Generar con inteligencia artificial{iaMarkedPendingIds.length > 0 ? ` (${iaMarkedPendingIds.length})` : ''}
              </Button>
              {/* Acá estaba "Ver APUs generados (N)": era la última puerta a la
                  pantalla vieja de análisis de precios. Se cerró el 2026-08-23
                  porque el análisis ya se ve y se opera dentro del presupuesto.
                  `ultimoApuRunId` sigue en uso: la vista de impresión lo necesita. */}
              <Button
                appearance="subtle"
                size="small"
                onClick={() => navigate(`/project/${projectId}/obra-context`, { state: { returnTo: `/project/${projectId}/quotes/${quoteId}` } })}
                title="Configurar contexto de proyecto y factores activos para APU-IA"
                style={{ color: 'var(--privado)' }}
              >
                Editar contexto de proyecto
              </Button>
            </div>}

            {isActive && (
              <QuoteBuildingAnimation
                stage={progressStage}
                stageLabel={progressStageLabel}
                message={stageMsg}
                pct={
                  progressStage === 'extract' || progressStage === 'queued'
                    ? liveProgress?.pct
                    : undefined
                }
                processed={liveProgress?.conceptsProcessed ?? quote?.conceptsProcessed}
                total={liveProgress?.conceptsTotal ?? quote?.conceptsTotal}
                priced={liveProgress?.conceptsPriced ?? quote?.conceptsPriced}
                etaMs={jobEtaMs}
                extra={undefined}
                enCola={enCola}
                esperandoDesde={esperandoDesde}
                ultimoAvisoAt={ultimoAvisoAt}
              />
            )}

            {/* El motivo del fallo ya se explica arriba, con título y qué hacer, para
                failed/canceled. Acá queda solo para un estado que no tenga su aviso
                propio (p. ej. una pausa que dejó un motivo), sin repetir el mensaje. */}
            {quote?.error && !corridaIncompleta && (
              <div
                style={{
                  color: 'var(--semantic-danger)',
                  fontSize: 'var(--fs-200)',
                  background: 'rgba(220,38,38,0.08)',
                  padding: 'var(--space-s)',
                  borderRadius: 'var(--radius-s)',
                }}
              >
                {quote.error}
              </div>
            )}
          </div>

          {/* Tiempos de fase — herramienta interna de calibración. SOLO staff
              Ecosoft; expone la mecánica del pipeline (Fase 1/Fase 2) que el
              cliente no debe ver. */}
          {isEcosoftStaff && phaseTimings && (
            <div style={{
              fontFamily: tokens.fontFamilyMonospace,
              fontSize: '11px',
              color: tokens.colorNeutralForeground4,
              background: tokens.colorNeutralBackground3,
              padding: '6px 10px',
              borderRadius: tokens.borderRadiusSmall,
              borderLeft: `3px solid ${tokens.colorBrandBackground}`,
              letterSpacing: '0.02em',
            }}>
              {phaseTimings}
            </div>
          )}

          {/* PROTOTIPO staff-only "de la HP a la geometría": genera plano 2D + modelo
              3D BIM + render desde los conceptos. Doble puerta: staff Ecosoft Y el
              flag de build (en prod va apagado). El backend es el portero real. */}
          {isEcosoftStaff && GEOMETRIA_HP_ENABLED && !isActive && totals.total > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Button size="small" appearance="secondary" onClick={() => setGeoOpen(true)}>
                Generar plano y modelo 3D
              </Button>
              <span style={{ fontSize: '11px', color: 'var(--privado)', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                Interno · Staff
              </span>
            </div>
          )}
          {isEcosoftStaff && GEOMETRIA_HP_ENABLED && quote && (
            <GeometriaHpPanel
              quoteId={quote.id}
              quoteName={quote.name}
              open={geoOpen}
              onClose={() => setGeoOpen(false)}
            />
          )}

          {/* Chips de filtro por confianza (Chip compartido) */}
          {!isActive && totals.total > 0 && (
            <div className={styles.confStrip} ref={gridTopRef}>
              <Chip
                label="Todos"
                count={totals.total}
                active={confFilter === 'all' && filter === 'all'}
                onClick={() => { setConfFilter('all'); setFilter('all'); }}
              />
              {(['alta', 'media', 'sin_equivalente', 'sintetica'] as const).map((k) =>
                confianzaCounts[k] > 0 ? (
                  <Chip
                    key={k}
                    label={CONFIANZA_LABEL[k] ?? k}
                    count={confianzaCounts[k]}
                    dotColor={CONFIANZA_ACCENT[k]}
                    active={confFilter === k}
                    onClick={() => setConfFilter(confFilter === k ? 'all' : k)}
                  />
                ) : null,
              )}

              {/* "Por revisar" son los renglones que entraron marcados y todavía nadie
                  corrigió: filtrarlos y recorrerlos con las flechas del panel es el camino
                  para cerrarlos. Va en su propio grupo, separado por una línea, porque
                  pregunta otra cosa que la confianza.

                  Se muestra aunque su cuenta caiga a cero SI está aplicado: corregir el
                  último renglón hacía desaparecer el chip y dejaba la lista vacía sin decir
                  por qué (visto en la prueba, 2026-08-13). Y si no hay ninguno por revisar,
                  no aparece la línea divisoria sola. */}
              {(estadoCounts.por_revisar > 0 || filter === 'por_revisar') && (
                <>
                  <span aria-hidden style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '0 2px' }} />
                  <Chip
                    label="Por revisar"
                    count={estadoCounts.por_revisar}
                    dotColor="var(--bad)"
                    active={filter === 'por_revisar'}
                    onClick={() => setFilter(filter === 'por_revisar' ? 'all' : 'por_revisar')}
                  />
                </>
              )}

              {/* Acciones a la derecha de los filtros: dónde ver el análisis +
                  imprimir sintéticos (+ aprobar lote staff). */}
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Menu positioning="below-end">
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      size="small"
                      appearance="subtle"
                      title="Elige dónde ver el análisis de precio del concepto que selecciones"
                    >
                      ▥ Ver análisis: {paneMode === 'derecha' ? 'a la derecha' : 'abajo'} ▾
                    </Button>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem onClick={() => changePaneMode('derecha')}>A la derecha</MenuItem>
                      <MenuItem onClick={() => changePaneMode('abajo')}>Abajo</MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
                {SHOW_APPROVE_ALL && isEcosoftStaff && apuGeneratedCount > 0 && (
                  <Button
                    size="small"
                    appearance="outline"
                    disabled={approvingAll || !!isActive}
                    onClick={() => void onApproveAllMatrices()}
                    title="Aprueba en lote todas las análisis con IA pendientes. Solo staff Ecosoft."
                  >
                    {approvingAll ? 'Aprobando…' : `✓ Aprobar análisis (${apuGeneratedCount})`}
                  </Button>
                )}
                {ultimoApuRunId && (
                  <Menu positioning="below-end">
                    <MenuTrigger disableButtonEnhancement>
                      <Button size="small" appearance="outline">🖨 Imprimir análisis</Button>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem onClick={() => openApuPrint('simple')}>
                          <div>
                            <div style={{ fontWeight: 600 }}>Impresión simple</div>
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Conceptos y su análisis por tipo de recurso.</div>
                          </div>
                        </MenuItem>
                        <MenuItem onClick={() => openApuPrint('detallado')}>
                          <div>
                            <div style={{ fontWeight: 600 }}>Impresión detallada</div>
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Incluye memoria de cálculo, razonamientos y validaciones.</div>
                          </div>
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                )}
              </div>
            </div>
          )}

          {/* Búsqueda (full-width) */}
          <div className={styles.searchRow}>
            <Input
              className={styles.searchInput}
              placeholder="Buscar por clave o descripción…"
              value={search}
              onChange={(_, d) => setSearch(d.value)}
              size="small"
            />
            {/* Señal discreta de que el listado se está actualizando. La tabla NO se saca de
                la pantalla por esto: quien está leyendo un renglón lo sigue viendo. */}
            <span
              className={styles.refreshHint}
              style={{ visibility: conceptsRefreshing ? 'visible' : 'hidden' }}
              aria-live="polite"
            >
              <Spinner size="extra-tiny" />
              Actualizando…
            </span>
          </div>

          {/* Línea contextual (los totales viven en la tira de stats de arriba) */}
          {filtered.length !== concepts.length && (
          <div className={styles.summaryRow}>
            <span className={styles.meta}>mostrando {filtered.length} de {concepts.length}</span>
          </div>
          )}

          {/* Tabla virtualizada */}
          {conceptsLoading ? (
            <div className={styles.loading}>
              <Spinner label="Cargando conceptos…" />
            </div>
          ) : (
            <div ref={tableRef} className={styles.table}>
              {selectionMode && (
                <div className={styles.selBar}>
                  <Checkbox
                    checked={selectedCount === selectableCount ? true : selectedCount === 0 ? false : 'mixed'}
                    onChange={(_, d) => toggleTodos(d.checked === true)}
                    label={`${selectedCount} de ${selectableCount} conceptos seleccionados`}
                  />
                  <span className={styles.selHint}>OPUS ai buscará el equivalente de los conceptos seleccionados en tus catálogos de referencia. Los que desmarques no se incluyen en esta búsqueda.</span>
                </div>
              )}
              <div
                ref={setTheadEl}
                className={styles.thead}
                style={selectionMode ? { gridTemplateColumns: `34px 16px 72px minmax(0, 1fr) 56px 64px 92px 116px minmax(0, 120px) 92px` } : undefined}
              >
                {selectionMode && <span />}
                <span />
                <span>Clave</span>
                <span>Descripción</span>
                <span style={{ textAlign: 'center' }}>Unidad</span>
                <span style={{ textAlign: 'right' }}>Cant.</span>
                <span style={{ textAlign: 'right' }}>P.U.</span>
                <span style={{ textAlign: 'right' }}>Importe</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  Confianza
                  <Popover withArrow positioning="below">
                    <PopoverTrigger disableButtonEnhancement>
                      <span className={styles.helpBtn} role="button" tabIndex={0} aria-label="Qué significa cada nivel de confianza">?</span>
                    </PopoverTrigger>
                    <PopoverSurface>
                      <div className={styles.helpCard}>
                        <LeyendaConfianza />
                      </div>
                    </PopoverSurface>
                  </Popover>
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, lineHeight: 1.1 }}>
                  <span title="Crear el análisis de precio unitario con inteligencia artificial">Analizar con IA</span>
                  <Popover positioning="below-end" withArrow>
                    <PopoverTrigger disableButtonEnhancement>
                      <button
                        type="button"
                        title="Elegir a cuáles crearles el precio con inteligencia artificial"
                        disabled={!!isActive || isGenerating || !canMutate}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 20, height: 20, flexShrink: 0, borderRadius: 6, padding: 0,
                          border: '1px solid var(--border-subtle)', background: 'var(--bg-overlay)',
                          color: 'var(--text-secondary)', cursor: isActive || !canMutate ? 'default' : 'pointer',
                          fontSize: 11, lineHeight: 1, opacity: isActive || !canMutate ? 0.4 : 1,
                        }}
                      >▾</button>
                    </PopoverTrigger>
                    <PopoverSurface style={{ padding: 12, minWidth: 260, maxWidth: 340 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>¿A cuáles les creamos el precio con inteligencia artificial?</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', margin: '2px 0 8px' }}>Prende un grupo. Si prendes otro, el anterior se apaga.</div>
                      {/* «Todos» son todos. Este aviso no cambia lo que hace el botón: solo
                          dice cuántos de esos ya venían con precio de catálogo, para que el
                          cotizador lo sepa antes de prenderlos. La vuelta atrás existe. */}
                      {conEquivalenteFirme.length > 0 && (
                        <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.45, margin: '0 0 8px', paddingBottom: 8, borderBottom: '1px solid var(--border-subtle)' }}>
                          Entre estos hay {conEquivalenteFirme.length === 1
                            ? '1 concepto que ya tiene'
                            : `${conEquivalenteFirme.length} conceptos que ya tienen`} precio de tu
                          catálogo con coincidencia alta. Si los prendes, el precio que arme la
                          inteligencia artificial pasa a contar en el total en su lugar; puedes
                          volver al de tu catálogo en un clic desde el análisis del concepto.
                        </div>
                      )}
                      {[
                        { k: 'all' as const, label: 'Todos', count: confianzaCounts.alta + confianzaCounts.media + confianzaCounts.sin_equivalente + confianzaCounts.sintetica + confianzaCounts.sin_procesar },
                        ...IA_SCOPES.filter((s) => confianzaCounts[s.k] > 0).map((s) => ({ k: s.k, label: s.label, count: confianzaCounts[s.k] })),
                      ].map((row) => {
                        const on = iaScope === row.k;
                        return (
                          <div key={row.k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 2px' }}>
                            <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{row.label} <span style={{ color: 'var(--text-tertiary)' }}>({row.count})</span></span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={on}
                              aria-label={`Encender la inteligencia artificial en: ${row.label}`}
                              onClick={() => void toggleIaScope(row.k)}
                              style={{
                                position: 'relative', width: 38, height: 22, borderRadius: 999, padding: 0, flexShrink: 0,
                                border: '1px solid',
                                borderColor: on ? 'var(--privado)' : tokens.colorNeutralStrokeAccessible,
                                background: on ? 'var(--privado)' : tokens.colorNeutralStrokeAccessible,
                                cursor: 'pointer',
                              }}
                            >
                              <span style={{ position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.3)', transition: 'left .15s' }} />
                            </button>
                          </div>
                        );
                      })}
                    </PopoverSurface>
                  </Popover>
                </span>
              </div>
              <div ref={tbodyRef} className={styles.tbody}>
                {filtered.length > 0 && (
                  <StickyGroupHeaders
                    rows={filtered}
                    depthByIdx={depthByIdx}
                    virtualItems={rowVirtualizer.getVirtualItems()}
                    scrollOffset={scrollAreaRef.current?.scrollTop ?? rowVirtualizer.scrollOffset ?? 0}
                    theadH={theadH}
                  />
                )}
                {filtered.length === 0 ? (
                  <div className={styles.empty}>
                    {/* "Sin conceptos detectados" es un VEREDICTO SOBRE EL DOCUMENTO y
                        queda reservado para cuando la lectura sí terminó y no encontró
                        ninguno. Si la corrida se cortó (falla o detención) o la hoja
                        todavía no se armó, la hoja está vacía por otra razón y hay que
                        decir cuál: lo contrario mandó a Sebastián a buscar el problema
                        en su documento cuando el problema era nuestro (2026-08-12). */}
                    {enCola
                      ? 'Esperando su turno para leer el documento…'
                      : quote?.status === 'queued' || quote?.status === 'running'
                        ? 'Leyendo el documento…'
                        : search || filter !== 'all'
                          ? 'Sin resultados para este filtro.'
                          : quote?.status === 'failed'
                            ? 'La lectura no llegó a terminar, así que la hoja quedó vacía. Vuelve a intentarlo con el paso 1.'
                            : quote?.status === 'canceled'
                              ? 'La lectura se detuvo antes de terminar, así que la hoja quedó vacía.'
                              : quote?.status === 'draft'
                                ? 'Todavía no armas la hoja: empieza por el paso 1, arriba.'
                                : 'Sin conceptos detectados: el documento no trae conceptos que se puedan presupuestar.'}
                  </div>
                ) : (
                  // Virtualización por FLUJO NORMAL + espaciadores (2026-07-06):
                  // el posicionamiento absoluto por transform dependía de que la
                  // altura estimada coincidiera con la real — en pantallas
                  // angostas (iPad) la descripción ocupa 3-4× más líneas y las
                  // filas se ENCIMABAN. En flujo normal encimarse es imposible;
                  // los estimados solo afectan el tamaño de los espaciadores.
                  (() => {
                    const vis = rowVirtualizer.getVirtualItems();
                    const padTop = Math.max(0, (vis[0]?.start ?? 0) - scrollMargin);
                    const padBottom = Math.max(
                      0,
                      rowVirtualizer.getTotalSize() - ((vis[vis.length - 1]?.end ?? 0) - scrollMargin),
                    );
                    return (
                  <div ref={listRef}>
                    {/* LOS HUECOS VAN COMO DOS DIVS HERMANOS, NO COMO RELLENO DEL CONTENEDOR.
                        Es lo que deja al navegador sostener la hoja quieta: Chrome corre el
                        scroll solo cuando algo cambia de alto por encima de lo que estás
                        mirando, PERO se rinde si lo que cambió fue el relleno (padding) de un
                        contenedor del renglón que tiene enfocado —está escrito así en la
                        norma—. Con el hueco de arriba como relleno de este mismo div, la
                        defensa del navegador quedaba apagada justo para el árbol. Medido el
                        2026-08-27 con la hoja de 2.838 renglones: con relleno, entrar un
                        renglón nuevo por arriba corría la hoja 13,6 px; con estos dos
                        hermanos, el renglón del centro se queda EXACTAMENTE donde estaba.
                        El porqué completo, en `shouldAdjustScrollPositionOnItemSizeChange`. */}
                    <div aria-hidden style={{ height: `${padTop}px` }} />
                    {vis.map((vi) => {
                      const c = filtered[vi.index];
                      if (!c) return null;

                      // Fila de agrupador/partida — sangría por profundidad real
                      // (parentIdx) + clave visible (catálogos tipo CONAGUA
                      // traen clave jerárquica en los agrupadores: A, 1, 1.1…).
                      if (c.kind === 'group') {
                        const depth = depthByIdx.get(c.idx) ?? 0;
                        const isL1 = depth === 0;
                        // Agrupadores estilo mockup: letra grande→chica y color de
                        // fondo distinto por nivel; SIN borde izquierdo. Pastel en
                        // claro / tinte sutil en oscuro vía vars del design system
                        // (mismas que el overlay sticky). Nivel 2+ = ranura neutral.
                        const gLvl = Math.min(depth, 5);
                        const gBg = `var(--group-bg-${gLvl})`;
                        const gColor = `var(--group-fg-${gLvl})`;
                        return (
                          <div
                            key={c.id}
                            data-index={vi.index}
                            ref={rowVirtualizer.measureElement}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-s)',
                              padding: isL1 ? '9px var(--space-m)' : '7px var(--space-m)',
                              paddingLeft: `${14 + depth * 16}px`,
                              borderBottom: '1px solid var(--border-subtle)',
                              background: gBg,
                            }}
                          >
                            {selectionMode && (() => {
                              const hijos = conceptIdsUnderGroup(c.idx);
                              const hijosSet = new Set(hijos);
                              const sel = concepts.filter((x) => x.kind !== 'group' && x.cotizar !== false && hijosSet.has(x.id)).length;
                              const checked = hijos.length > 0 && sel === hijos.length ? true : sel === 0 ? false : 'mixed';
                              return (
                                <span onClick={(e) => e.stopPropagation()} style={{ marginRight: '6px', flexShrink: 0 }}>
                                  <Checkbox
                                    checked={checked}
                                    disabled={hijos.length === 0}
                                    onChange={(_, d) => togglePartida(c.idx, d.checked === true)}
                                  />
                                </span>
                              );
                            })()}
                            {!isL1 && (
                              <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--fs-100)', marginRight: '2px', flexShrink: 0 }}>›</span>
                            )}
                            {c.clave && (
                              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: gColor, opacity: 0.8, marginRight: '8px', flexShrink: 0 }}>
                                {c.clave}
                              </span>
                            )}
                            <span
                              title={c.descripcion}
                              style={{
                                flex: 1,
                                minWidth: 0,
                                lineHeight: 1.4,
                                color: gColor,
                                fontSize: isL1 ? '13px' : depth === 1 ? '12px' : '11.5px',
                                fontWeight: isL1 ? 750 : depth === 1 ? 650 : 600,
                                letterSpacing: isL1 ? '0.04em' : '0.01em',
                                textTransform: isL1 ? 'uppercase' : 'none',
                              }}
                            >
                              {stripRenglonMark(c.descripcion)}
                            </span>
                          </div>
                        );
                      }

                      const hasMatch = !!(c.claveCatalogo || c.descripcionCatalogo);
                      const isSynthetic = c.confianza === 'sintetica' || !!c.apuIaJobRunId;
                      // Cualquier concepto (incluye "sin equivalente") abre el panel
                      // lateral de análisis, que explica el origen del precio o el
                      // porqué de no tener equivalente.
                      const canExpand = true;
                      const isFocused = panelId === c.id;
                      const isExpanded = SHOW_INLINE_ANALYSIS && canExpand && expandedId === c.id;
                      // El tipo (equivalente / sintético / sin precio) se distingue SOLO
                      // por el borde izquierdo (color de confianza). Sin fondo por estado
                      // (los sintéticos tienen status 'failed' y se pintaban de rojo).
                      const rowBg = 'transparent';
                      const { llmVeredicto } = parseFuente(c.fuente);
                      return (
                        <div
                          key={c.id}
                          data-index={vi.index}
                          data-demo-apu={isSynthetic ? '1' : undefined}
                          ref={rowVirtualizer.measureElement}
                          className={styles.trowOuter}
                          style={{
                            cursor: canExpand ? 'pointer' : 'default',
                            boxShadow: isFocused
                              ? `inset 3px 0 0 ${tokens.colorBrandForeground1}`
                              : `inset 3px 0 0 ${CONFIANZA_ACCENT[confKind(c)] ?? 'transparent'}`,
                          }}
                          onClick={() => canExpand && focusConcept(c.id)}
                        >
                          <div
                            className={styles.trow}
                            style={{
                              background: (isFocused || isExpanded) && rowBg === 'transparent' ? tokens.colorNeutralBackground3 : rowBg,
                              borderBottom: isExpanded ? 'none' : undefined,
                              opacity: selectionMode && c.cotizar === false ? 0.5 : undefined,
                              ...(selectionMode ? { gridTemplateColumns: '34px 16px 72px minmax(0, 1fr) 56px 64px 92px 116px minmax(0, 120px) 92px' } : {}),
                            }}
                          >
                          {selectionMode && (
                            <span onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Checkbox checked={c.cotizar !== false} onChange={() => toggleConcepto(c)} />
                            </span>
                          )}
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: '10px', lineHeight: 1, color: isFocused ? tokens.colorBrandForeground1 : 'var(--text-tertiary)', transform: isFocused ? 'rotate(90deg)' : undefined, transition: 'transform 0.15s' }}>›</span>
                          </span>
                          <span className={styles.claveCell} title={c.clave ?? undefined}>
                            {c.clave}
                          </span>
                          <span className={styles.desc} title={stripRenglonMark(c.descripcion)}>
                            {stripRenglonMark(c.descripcion)}
                          </span>
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <span className={styles.unitCell}>{c.unidad}</span>
                          </div>
                          <span className={styles.num}>
                            {c.cantidad != null ? Number(c.cantidad).toLocaleString('es-MX') : '—'}
                          </span>
                          <span className={styles.num}>{fmtMoney(Number(c.precioUnitario))}</span>
                          <span className={styles.num}>{fmtMoney(Number(c.importe))}</span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-start', minWidth: 0 }}>
                          {(() => {
                            // CORREGIDO A MANO (2026-08-13). Una persona escribió este
                            // renglón: en una licitación no es lo mismo que lo que salió
                            // del documento, así que se dice, con quién y cuándo. Va
                            // primero incluso que la marca de "revisa la página": ya se
                            // revisó, eso es justamente lo que pasó.
                            if (c.editadoManual) {
                              return (
                                <Tooltip
                                  relationship="description"
                                  content={
                                    <div style={{ fontSize: 'var(--fs-100)', lineHeight: 1.5, maxWidth: '320px' }}>
                                      {c.editadoPorNombre
                                        ? `Lo corrigió ${c.editadoPorNombre}${c.editadoAt ? ` el ${new Date(c.editadoAt).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}.`
                                        : 'Este renglón se corrigió a mano.'}
                                      {c.origen === 'conteo_pagina' && (
                                        <div style={{ marginTop: '4px', color: 'var(--text-tertiary)' }}>
                                          Había entrado marcado: no se pudo leer completo
                                          {c.origenPagina != null ? ` de la página ${c.origenPagina}` : ''} del documento.
                                        </div>
                                      )}
                                    </div>
                                  }
                                >
                                  <span style={{ display: 'inline-flex', cursor: 'help' }}>
                                    <Badge size="small" appearance="tint" color="brand"
                                      style={{ whiteSpace: 'nowrap' }}>
                                      Corregido a mano
                                    </Badge>
                                  </span>
                                </Tooltip>
                              );
                            }
                            // RENGLÓN QUE NO SE PUDO LEER DEL DOCUMENTO (2026-08-13).
                            // Está en la hoja para que no se pierda, pero con media
                            // descripción y una cantidad que puede no ser la del
                            // documento: va primero que cualquier otra marca porque es
                            // lo único que el cotizador TIENE que hacer con este renglón.
                            if (c.origen === 'conteo_pagina') {
                              return (
                                <Tooltip
                                  relationship="description"
                                  content={
                                    <div style={{ fontSize: 'var(--fs-100)', lineHeight: 1.5, maxWidth: '320px' }}>
                                      Este renglón no se pudo leer completo
                                      {c.origenPagina != null ? ` de la página ${c.origenPagina}` : ''} del
                                      documento. Lo que ves es solo el principio de la descripción, y la
                                      cantidad puede no ser la que dice el documento. Compáralo con el
                                      original y corrígelo en el panel del renglón: al guardar le buscamos
                                      su equivalente en tus catálogos.
                                    </div>
                                  }
                                >
                                  <span style={{ display: 'inline-flex', cursor: 'help' }}>
                                    <Badge size="small" appearance="filled" color="warning"
                                      style={{ whiteSpace: 'nowrap' }}>
                                      {c.origenPagina != null
                                        ? `Revisa la página ${c.origenPagina}`
                                        : 'Revisa en el documento'}
                                    </Badge>
                                  </span>
                                </Tooltip>
                              );
                            }
                            // No seleccionado para cotizar (status 'skipped' tras
                            // cotizar, o cotizar=false en borrador): se exporta sin análisis.
                            if (c.status === 'skipped' || (selectionMode && c.cotizar === false)) {
                              return (
                                <Badge size="small" appearance="outline" color="informative"
                                  style={{ whiteSpace: 'nowrap' }}>
                                  No se incluye
                                </Badge>
                              );
                            }
                            // Un sintético YA generado (tiene matriz) se muestra como
                            // tal aunque su status haya quedado en 'pending' (bug de
                            // datos observado en pruebas de factores): no debe decir
                            // "Sin precio".
                            const esSinteticoGenerado = isSynthetic || c.hasApuIaMatrix;
                            if (c.status === 'pending' && !esSinteticoGenerado) {
                              // "Evaluando…" solo cuando hay una cotización EN CURSO.
                              // Tras "Generar listado" (draft, sin cotizar) los
                              // conceptos están pendientes pero la evaluación no se
                              // ha lanzado → "Sin cotizar" (pedido Sebastián 2026-07-06).
                              const enCurso = !!isActive;
                              return (
                                <Badge size="small" appearance="tint" color={enCurso ? 'informative' : 'warning'}
                                  style={{ whiteSpace: 'nowrap' }}>
                                  {enCurso ? 'Evaluando…' : 'Sin precio'}
                                </Badge>
                              );
                            }
                            // El aviso de unidad habla del EQUIVALENTE del catálogo. Si el
                            // renglón pasó a sintético, el precio ya no sale de ahí: el aviso
                            // sobra y confunde. Quedaban los dos pills juntos —"Sintético" y
                            // "revisar unidad"— diciendo cosas distintas del mismo renglón.
                            // Hay que excluirlo explícitamente porque el equivalente se
                            // conserva guardado a propósito (por si se rechaza el sintético y
                            // hay que devolverle ese precio), así que `hasMatch` sigue siendo
                            // true. Reportado por Sebastián el 2026-08-19.
                            const showUnitWarn =
                              hasMatch && confKind(c) !== 'sintetica' && unitsDiffer(c.unidad, c.unidadCatalogo);
                            // El tooltip SIEMPRE explica qué significa el nivel y qué hacer
                            // (antes solo aparecía cuando había equivalente; "Sin equivalente"
                            // —el que más lo necesita— quedaba mudo). Si además hay equivalente,
                            // debajo va su detalle (clave, descripción, unidad, veredicto, puntaje).
                            const nivel = confKind(c);
                            const ayuda = CONFIANZA_AYUDA[nivel];
                            const confBadge = (
                              <Tooltip
                                relationship="description"
                                content={
                                  <div style={{ fontSize: 'var(--fs-100)', lineHeight: 1.5, maxWidth: '320px' }}>
                                    <div
                                      style={{
                                        marginBottom: hasMatch ? '7px' : 0,
                                        paddingBottom: hasMatch ? '7px' : 0,
                                        borderBottom: hasMatch ? '1px solid var(--border-subtle)' : undefined,
                                      }}
                                    >
                                      <div style={{ fontWeight: 700 }}>{confLabel(c)}</div>
                                      <div>{ayuda.que}</div>
                                      <div style={{ color: 'var(--text-tertiary)', marginTop: '3px' }}>{ayuda.hacer}</div>
                                    </div>
                                    {hasMatch && (
                                      <>
                                        {c.claveCatalogo && (
                                          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, marginBottom: '2px' }}>
                                            {c.claveCatalogo}
                                          </div>
                                        )}
                                        {c.descripcionCatalogo && (
                                          <div style={{ whiteSpace: 'normal' }}>{c.descripcionCatalogo}</div>
                                        )}
                                        {c.unidadCatalogo && (
                                          <div style={{ color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                            Unidad: {c.unidadCatalogo}
                                          </div>
                                        )}
                                        {(() => {
                                          const { llmVeredicto } = parseFuente(c.fuente);
                                          return (
                                            <>
                                              {llmVeredicto && (
                                                <div style={{ color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                                  {llmVeredicto === 'si' ? '✓ verificado' : '⚠ parcial'}
                                                </div>
                                              )}
                                              {c.score != null && (
                                                <div style={{ color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                                  Puntaje: {Math.round(c.score * 100)}%
                                                </div>
                                              )}
                                            </>
                                          );
                                        })()}
                                        {c.nota && (
                                          <div style={{ color: 'var(--text-tertiary)', marginTop: '2px', fontStyle: 'italic' }}>
                                            {c.nota.replace(/^(LLM|IA):\s*/i, '')}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                }
                              >
                                <span style={{ display: 'inline-flex', cursor: 'help' }}>
                                  <span className={styles.confPill} style={{ background: CONF_PILL[nivel]?.bg, color: CONF_PILL[nivel]?.fg }}>
                                    <span className={styles.confPillDot} />
                                    {confLabel(c)}
                                  </span>
                                </span>
                              </Tooltip>
                            );
                            if (!showUnitWarn) return confBadge;
                            return (
                              <span style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start', minWidth: 0 }}>
                                {confBadge}
                                <Tooltip
                                  relationship="description"
                                  content={
                                    <div style={{ fontSize: 'var(--fs-100)', lineHeight: 1.5, maxWidth: '300px' }}>
                                      <div style={{ fontWeight: 700, marginBottom: '2px' }}>
                                        Unidades distintas
                                      </div>
                                      <div>
                                        Concepto similar pero la unidad difiere:&nbsp;
                                        <strong>{c.unidad}</strong> (concurso) vs&nbsp;
                                        <strong>{c.unidadCatalogo}</strong> (catálogo).
                                      </div>
                                      <div style={{ color: 'var(--text-tertiary)', marginTop: '4px', fontStyle: 'italic' }}>
                                        Verifique conversión antes de aceptar el precio.
                                      </div>
                                    </div>
                                  }
                                >
                                  <span style={{ display: 'inline-flex', cursor: 'help' }}>
                                    <span className={styles.confPill} style={{ background: 'var(--warn-weak)', color: 'var(--warn)' }}>
                                      <span className={styles.confPillDot} />
                                      revisar unidad
                                    </span>
                                  </span>
                                </Tooltip>
                              </span>
                            );
                          })()}
                          {/* Antes: en el demo (solo lectura) se mostraba un 2º pill con la
                              fuente ("análisis con IA" / "Catálogo de muestra"). Se quitó para
                              dejar SOLO la confianza, igual que el grid normal; el origen del
                              precio se ve en el panel de análisis. */}
                          </div>
                          {/* Columna IA — toggle "Cotizar con IA" */}
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '1px' }}>
                            {renderIaToggle(c)}
                          </div>
                          </div>
                          {isExpanded && (
                            <div className={styles.expandPanel}>
                              <div className={styles.expandCols}>
                                <div className={styles.expandSide}>
                                  <span className={styles.expandColLabel}>Renglón del concurso</span>
                                  <span className={styles.expandColDesc}>{stripRenglonMark(c.descripcion)}</span>
                                  <span className={styles.expandColMeta}>Unidad: {c.unidad || '—'}</span>
                                </div>
                                <div className={styles.expandSide} style={{ borderLeft: '1px solid var(--border-subtle)', paddingLeft: '12px' }}>
                                  <span className={styles.expandColLabel}>{isSynthetic ? 'Origen del precio' : 'Match en catálogo'}</span>
                                  {/* EL PRECIO QUE SE REEMPLAZÓ, DICHO. Un concepto cotizado con
                                      inteligencia artificial conserva la clave del catálogo con la
                                      que había equivalido, así que este bloque mostraba ese código
                                      bajo el título "Origen del precio" — y el precio que cuenta es
                                      otro. Acá se aclara y se dice cuál era. */}
                                  {isSynthetic && (c.precioCatalogoGuardado ?? 0) > 0 && (
                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                                      Cuenta el precio armado con inteligencia artificial. Antes tenía
                                      el de tu catálogo:{' '}
                                      <b style={{ fontFamily: 'var(--font-mono)' }}>
                                        {fmtMoney(c.precioCatalogoGuardado ?? 0)}
                                      </b>
                                      . Puedes volver a él desde el análisis del concepto.
                                    </span>
                                  )}
                                  {c.claveCatalogo || c.descripcionCatalogo ? (
                                    <>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
                                        {c.claveCatalogo && (
                                          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '11px', color: tokens.colorBrandForeground1 }}>
                                            {c.claveCatalogo}
                                          </span>
                                        )}
                                        {c.catalogIdOrigen && catalogColorMap[c.catalogIdOrigen] && (
                                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9px', color: 'var(--text-tertiary)', background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)', borderRadius: '999px', padding: '0 5px', lineHeight: '16px' }}>
                                            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: catalogColorMap[c.catalogIdOrigen], flexShrink: 0 }} />
                                            {allCatalogs.find((cat) => cat.id === c.catalogIdOrigen)?.name ?? ''}
                                          </span>
                                        )}
                                      </div>
                                      {c.descripcionCatalogo && <span className={styles.expandColDesc}>{c.descripcionCatalogo}</span>}
                                      {c.unidadCatalogo && <span className={styles.expandColMeta}>Unidad: {c.unidadCatalogo}</span>}
                                    </>
                                  ) : (
                                    <span style={{ fontSize: '11px', color: isSynthetic ? 'var(--privado)' : 'var(--text-tertiary)', fontStyle: 'italic' }}>
                                      {isSynthetic ? 'Análisis de precio unitario con IA (OPUS ai)' : 'Sin coincidencia en catálogo'}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className={styles.expandBottom}>
                                {c.score != null && (
                                  <div className={styles.scoreWrap}>
                                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>Score</span>
                                    <div
                                      className={styles.scoreTrack}
                                      style={{ background: 'linear-gradient(to right, var(--bad) 0%, var(--warn) 40%, var(--ok) 70%, var(--ok) 100%)' }}
                                    >
                                      <div style={{
                                        position: 'absolute', left: `${Math.round(c.score * 100)}%`, top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '9px', height: '9px', borderRadius: '50%',
                                        background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                                        border: '1.5px solid rgba(0,0,0,0.15)',
                                      }} />
                                    </div>
                                    <span className={styles.scorePct}>{Math.round(c.score * 100)}%</span>
                                  </div>
                                )}
                                {llmVeredicto && (
                                  <span className={styles.llmVerdict} style={{ color: llmVeredicto === 'si' ? 'var(--ok)' : 'var(--warn)' }}>
                                    {llmVeredicto === 'si'
                                      ? <><CheckmarkCircle20Filled style={{ fontSize: '14px' }} />&nbsp;Verificado</>
                                      : <><Warning20Regular style={{ fontSize: '14px' }} />&nbsp;Parcial</>
                                    }
                                  </span>
                                )}
                                {c.nota && (
                                  <span className={styles.notaText} title={c.nota.replace(/^(LLM|IA):\s*/i, '')}>
                                    "{c.nota.replace(/^(LLM|IA):\s*/i, '')}"
                                  </span>
                                )}
                              </div>
                              {(isSynthetic || (c.insumos?.length ?? 0) > 0) && (
                                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setAnalysisOpenId(analysisOpenId === c.id ? null : c.id); }}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--border-subtle)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 650, fontFamily: 'inherit', color: 'var(--accent)' }}
                                  >
                                    {analysisOpenId === c.id ? '▾' : '▸'} Ver análisis de precio unitario
                                  </button>
                                  {analysisOpenId === c.id && (
                                    <div style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
                                      <ConceptAnalysisView quoteId={c.quoteId} concepto={c} moneda={quote?.moneda} />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div aria-hidden style={{ height: `${padBottom}px` }} />
                  </div>
                    );
                  })()
                )}
              </div>
            </div>
          )}
        </section>
      </div>
      </div>

      {/* ====== Panel lateral: análisis de precio del concepto enfocado ====== */}
      {(() => {
        if (!panelId) return null;
        const c = concepts.find((x) => x.id === panelId);
        if (!c) return null;
        const catName = c.catalogIdOrigen
          ? (allCatalogs.find((cat) => cat.id === c.catalogIdOrigen)?.name ?? null)
          : null;
        // ← → RECORREN LA LISTA QUE SE ESTÁ VIENDO (la del filtro aplicado): filtrás
        // "Por revisar" y los repasás uno por uno; buscás "muro" y recorrés esos. No hace
        // falta ningún mecanismo aparte — `filtered` ya es esa lista.
        const visibles = filtered.filter((x) => x.kind !== 'group');
        // Si el renglón que se está mirando salió del filtro por lo que se le ACABA de
        // corregir (corregir uno lo saca de "Por revisar"), sigue contando para recorrer:
        // se lo devuelve a su lugar en la lista. Sin esto, guardar hacía desaparecer las
        // flechas justo cuando el cotizador iba por el siguiente. Vale solo para ese
        // renglón y hasta que se cambie el filtro: meter en la cuenta cualquier renglón
        // que no está en la lista haría que "2 de 4" hablara de una lista que no es la que
        // se ve.
        if (recienCorregidoRef.current === c.id && !visibles.some((x) => x.id === c.id)) {
          const at = visibles.findIndex((x) => x.idx > c.idx);
          visibles.splice(at < 0 ? visibles.length : at, 0, c);
        }
        const i = visibles.findIndex((x) => x.id === c.id);
        const nav = i >= 0 && visibles.length > 1
          ? {
              pos: i + 1,
              total: visibles.length,
              onPrev: () => { const p = visibles[i - 1]; if (p) focusConcept(p.id); },
              onNext: () => { const n = visibles[i + 1]; if (n) focusConcept(n.id); },
            }
          : undefined;
        return (
          <ConceptPricePanel
            nav={nav}
            moneda={quote?.moneda}
            // El presupuesto de EJEMPLO es de solo lectura, igual que el resto de sus
            // botones: los campos entran apagados en vez de invitar a escribir para
            // rechazarlo después. El servidor lo rechaza igual (403), pero pedirle al
            // usuario que escriba algo que no se va a guardar es hacerle perder el
            // trabajo (visto en la prueba, 2026-08-13).
            canEdit={!!canMutate && !isActive && !isDemo}
            onEdited={(r) => {
              // El renglón que contesta el servidor reemplaza al de la lista (así el
              // importe, el precio y el total se mueven sin esperar la recarga), y el
              // listado se recarga igual para que nada quede desfasado.
              setConcepts((prev) => prev.map((x) => (x.id === r.concepto.id ? r.concepto : x)));
              recienCorregidoRef.current = r.concepto.id;
              const msg = !r.rebuscado
                ? 'Renglón corregido.'
                : r.resultado === 'con_precio'
                  ? 'Renglón corregido. Le encontramos precio en tus catálogos.'
                  : r.resultado === 'sin_catalogos'
                    ? 'Renglón corregido. No hay catálogos de referencia para buscarle precio.'
                    : 'Renglón corregido. Todavía no encontramos un concepto equivalente en tus catálogos.';
              dispatchToast(
                <Toast>
                  <ToastTitle>{msg}</ToastTitle>
                </Toast>,
                { intent: r.rebuscado && r.resultado !== 'con_precio' ? 'warning' : 'success', timeout: 5000 },
              );
              void loadConcepts(c.quoteId);
            }}
            quoteId={c.quoteId}
            concepto={c}
            catalogName={catName}
            llmVeredicto={parseFuente(c.fuente).llmVeredicto}
            isActive={!!isActive}
            onClose={() => {
              setPanelId(null);
              expandedIdRef.current = null;
              setExpandedId(null);
            }}
            onChanged={() => { void loadConcepts(c.quoteId); }}
            onGoToEquivalentes={goToEquivalentes}
            mode={paneMode === 'abajo' ? 'abajo' : 'derecha'}
            onModeChange={changePaneMode}
            size={paneMode === 'abajo' ? paneH : paneW}
            onResize={changePaneSize}
          />
        );
      })()}
      </div>{/* /contentCol */}
      </div>{/* /workArea */}

      {/* ====== Confirmación para DETENER un proceso largo ====== */}
      <Dialog open={confirmarAlto !== null} onOpenChange={(_, d) => { if (!d.open) setConfirmarAlto(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {confirmarAlto === 'sinteticos'
                ? '¿Detener la generación de análisis de precios?'
                : `¿Detener ${procesoPresupuesto}?`}
            </DialogTitle>
            <DialogContent>
              {confirmarAlto === 'sinteticos' ? (
                <p style={{ margin: 0 }}>
                  Los análisis que ya se crearon <strong>se conservan</strong> y se cobran, porque
                  quedan listos para usar en tu presupuesto.
                  {' '}<strong>Los que están a medio hacer se terminan</strong> antes de parar —
                  se trabaja en varios a la vez, así que vas a ver aparecer unos cuantos más
                  después de confirmar; es a propósito, para no tirar lo ya pagado. Te vamos a
                  decir cuántos son.
                  {' '}Lo que falta queda pendiente: si vuelves a lanzarla, sigue por los que
                  faltan y <strong>no se cobra de nuevo</strong> lo ya hecho.
                </p>
              ) : enArmado ? (
                <p style={{ margin: 0 }}>
                  Se termina de leer el documento que ya empezó y{' '}
                  <strong>la hoja de presupuesto queda guardada</strong> con los conceptos que
                  se leyeron; lo que no se hace es buscar los precios. No se pierde nada de lo
                  hecho: si vuelves a lanzarlo, la lectura se reusa y{' '}
                  <strong>no se cobra de nuevo</strong>.
                </p>
              ) : (
                <p style={{ margin: 0 }}>
                  Los conceptos que ya se resolvieron <strong>quedan con su precio</strong> y se
                  cobran; no se pierde nada de lo hecho. Los que faltan quedan sin resolver, y
                  esto <strong>no se puede reanudar</strong>: para seguir hay que volver a
                  lanzarla, y va a continuar por los que faltan.
                </p>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmarAlto(null)}>
                Seguir trabajando
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  const proceso = confirmarAlto;
                  setConfirmarAlto(null);
                  if (proceso === 'sinteticos') void onStopApuIa();
                  else void onControl('stop');
                }}
              >
                Sí, detener
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* ====== Diálogo de confirmación antes de cotizar (gate por conceptos) ====== */}
      <Dialog
        open={costConfirm !== null}
        onOpenChange={(_, d) => {
          if (!d.open) setCostConfirm(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {costConfirm ? (quotePricing.modos[costConfirm.mode]?.label ?? 'Buscar equivalentes') : 'Buscar equivalentes'}
            </DialogTitle>
            <DialogContent>
              {costConfirm && (() => {
                const reuse = costConfirm.opts.reuseConcepts;
                const alcanza = conceptsEffectiveRemaining == null || conceptsEffectiveRemaining >= costToSearch;
                return (
                  <>
                    <p style={{ margin: '0 0 var(--space-m)' }}>
                      {reuse ? (
                        hasSearchedBefore ? (
                          <>Se volverán a buscar <strong>{costToSearch.toLocaleString('es-MX')}</strong> conceptos{confianzaCounts.sintetica > 0 ? ` (${confianzaCounts.sintetica.toLocaleString('es-MX')} con análisis hecho con IA se conservan)` : ''}.</>
                        ) : (
                          <>Vamos a buscar precios equivalentes para <strong>{costToSearch.toLocaleString('es-MX')}</strong> conceptos en tus catálogos de referencia.</>
                        )
                      ) : (
                        <>Este documento tiene <strong>{costConfirm.concepts.toLocaleString('es-MX')}</strong> conceptos.</>
                      )}
                    </p>
                    {cr && (() => {
                      // Aviso de costo previo (promedio medido × factor). Se cobra el consumo real.
                      const prom = promediosDe(usageData).find((p) => p.id === (costConfirm.mode === 'catalogos' ? 'catalogos' : 'ia_completa'));
                      if (!prom) return null;
                      const n = reuse ? costToSearch : costConfirm.concepts;
                      const est = Math.max(1, Math.ceil(n * prom.creditos));
                      return (
                        <p style={{ margin: '0 0 var(--space-m)' }}>
                          Esta cotización usará <strong>≈ {est.toLocaleString('es-MX')} créditos</strong> ({n.toLocaleString('es-MX')} conceptos × {prom.texto.replace(' (promedio)', '').replace(/ por .*/, '')} en promedio).
                          Se cobra el consumo real.
                          {usageData?.tokens ? ` Tienes ${formatCreditos(usageData.tokens.remaining, 'saldo')}.` : ''}
                        </p>
                      );
                    })()}
                    {conceptsEffectiveRemaining != null && (
                      <p style={{ margin: '0 0 var(--space-m)', color: alcanza ? 'var(--text-secondary)' : 'var(--bad)' }}>
                        Conceptos restantes este mes: <strong>{conceptsEffectiveRemaining.toLocaleString('es-MX')}</strong>
                        {!alcanza && ' — no alcanza para esta corrida. Recarga capacidad para continuar.'}
                      </p>
                    )}
                    {costConfirm.mode === 'catalogos' && (
                      <p style={{ margin: 0, fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)' }}>
                        Modo: Agente de Precios Unitarios
                      </p>
                    )}
                    {/* Re-búsqueda: qué conservar (solo si ya hay equivalentes de
                        catálogo que perder). Los sintéticos siempre se conservan. */}
                    {costConfirm.opts.reuseConcepts && (confianzaCounts.alta + confianzaCounts.media) > 0 && (
                      <div style={{ marginTop: 'var(--space-m)', borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-m)' }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>¿Qué conservar de la búsqueda anterior?</div>
                        <RadioGroup value={preserveScope} onChange={(_, d) => setPreserveScope(d.value as 'alta' | 'alta_media' | 'none')}>
                          <Radio value="alta" label={`Conservar los de confianza alta (${confianzaCounts.alta}) — vuelve a buscar media (${confianzaCounts.media}) y sin equivalente (${confianzaCounts.sin_equivalente})`} />
                          <Radio value="alta_media" label={`Conservar alta y media (${confianzaCounts.alta + confianzaCounts.media}) — vuelve a buscar solo sin equivalente (${confianzaCounts.sin_equivalente})`} />
                          <Radio value="none" label="No conservar — vuelve a buscar todos" />
                        </RadioGroup>
                        <div style={{ fontSize: 'var(--fs-100)', color: 'var(--text-tertiary)', marginTop: 6 }}>
                          Los conceptos con análisis con IA siempre se conservan.
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setCostConfirm(null)}>
                Cancelar
              </Button>
              {costConfirm && conceptsEffectiveRemaining != null && conceptsEffectiveRemaining < costToSearch ? (
                <Button appearance="primary" onClick={() => { setCostConfirm(null); setRechargeOpen(true); }}>
                  {cr ? 'Comprar créditos' : 'Comprar tokens'}
                </Button>
              ) : (
                <Button
                  appearance="primary"
                  disabled={catalogsSaving}
                  onClick={() => {
                    if (!costConfirm) return;
                    const options: Record<string, unknown> = {};
                    if (costConfirm.opts.skipLlm) options.skipLlm = true;
                    // Flujo dos pasos: cotizar sobre el listado ya leído (no re-extrae).
                    if (costConfirm.opts.reuseConcepts) {
                      options.reuseConcepts = true;
                      // Qué conservar de la búsqueda anterior (sintéticos siempre).
                      options.preserveMatches = preserveScope;
                    }
                    setCostConfirm(null);
                    void runRegenerate(options);
                  }}
                >
                  {catalogsSaving ? 'Procesando…' : 'Aceptar y buscar equivalentes'}
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* ====== Tienda de tokens (popout) ====== */}
      <TiendaDialog open={rechargeOpen} onClose={() => setRechargeOpen(false)} />

      {/* ====== Diálogo de overflow de cuota al recotizar ====== */}
      <Dialog
        open={overflow !== null}
        onOpenChange={(_, d) => {
          if (!d.open) setOverflow(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>El presupuesto supera tu capacidad actual</DialogTitle>
            <DialogContent>
              {overflow && (
                <>
                  <p style={{ margin: '0 0 var(--space-m)' }}>
                    Este archivo tiene <strong>{overflow.preview.totalConcepts}</strong> conceptos.
                    Con tu saldo actual puedes procesar hasta{' '}
                    <strong>{overflow.preview.remaining ?? 0}</strong> ahora.
                  </p>
                  <p style={{ margin: '0 0 var(--space-m)' }}>
                    Puedes cotizar ese tramo, comprar una recarga de {palabraBolsa}, o cambiar a un plan superior.
                  </p>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setOverflow(null)}>
                Cancelar
              </Button>
              <Button
                appearance="secondary"
                onClick={() => {
                  window.open('https://ecosoft.com.mx/opus-ia', '_blank', 'noopener');
                }}
              >
                Ver planes
              </Button>
              <Button
                appearance="secondary"
                onClick={() => { setOverflow(null); setRechargeOpen(true); }}
              >
                {cr ? 'Comprar créditos' : 'Comprar tokens'}
              </Button>
              <Button
                appearance="primary"
                disabled={catalogsSaving || !overflow?.preview.remaining}
                onClick={() => {
                  if (!overflow?.preview.remaining) return;
                  const options: Record<string, unknown> = {
                    maxConcepts: overflow.preview.remaining,
                  };
                  if (overflow.pendingOpts.skipLlm) options.skipLlm = true;
                  setOverflow(null);
                  void runRegenerate(options);
                }}
              >
                {catalogsSaving
                  ? 'Procesando…'
                  : `Buscar equivalentes de los primeros ${overflow?.preview.remaining ?? 0}`}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* ====== Documento escaneado: se lee mirando las páginas ====== */}
      <Dialog
        open={visionPreview !== null}
        onOpenChange={(_, d) => { if (!d.open) setVisionPreview(null); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Este documento necesita una lectura más avanzada</DialogTitle>
            <DialogContent>
              <p style={{ margin: '0 0 12px' }}>
                Este documento es una <strong>imagen escaneada</strong>: no tiene texto que se pueda
                leer. Para armar el listado hay que <strong>mirar cada página</strong>, y eso lo hace
                una <strong>inteligencia artificial más avanzada</strong> que la que usamos
                normalmente.
              </p>
              <p style={{ margin: '0 0 12px' }}>
                Por eso <strong>consume más {palabraBolsa} de tu bolsa</strong> que una lectura normal: se
                procesa el documento página por página
                {visionPreview?.visionPages ? `(son ${visionPreview.visionPages} páginas)` : ''}. Vas a
                ver el avance mientras trabaja.
              </p>
              <p style={{ margin: '0 0 12px' }}>
                Cuando termine, conviene revisar el listado contra el documento original. Si algún
                renglón quedó tan desvanecido que no se pudo interpretar, te lo vamos a señalar.
              </p>
              {isEcosoftStaff && !OCULTAR_COSTO_IA && visionPreview?.visionCost && (
                <p style={{ margin: '0 0 12px', fontSize: '12px', color: tokens.colorNeutralForeground3, fontFamily: 'monospace' }}>
                  [staff Ecosoft] Costo estimado de la lectura por imagen: $
                  {visionPreview.visionCost.usd.toFixed(4)} USD.
                </p>
              )}
              {visionPreview && !visionPreview.fits && !!visionPreview.remaining && (
                <p style={{ margin: '0 0 12px' }}>
                  Por tu cuota restante, se van a presupuestar solo los primeros{' '}
                  <strong>{visionPreview.remaining}</strong> conceptos.
                </p>
              )}
              <p style={{ margin: 0 }}>¿Quieres continuar igual?</p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setVisionPreview(null)}>Cancelar</Button>
              <Button
                appearance="primary"
                onClick={() => {
                  setVisionPreview(null);
                  // El flag va SÍ O SÍ acá: el usuario ya aceptó leer por imagen y
                  // el worker no debe volver a deducirlo. Faltaba en este camino
                  // —solo estaba en el asistente— y por eso el pedido seguía por
                  // texto y devolvía el listado con las cantidades inventadas.
                  void runRegenerate({ ...(visionOptions ?? {}), useVision: true });
                }}
              >
                Sí, leer el documento
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
